/**
 * PSP/CRE Tool v3.0 - Tab 1: Force Composition Builder
 * COMPLETE VERSION
 *
 * VERSION 4.18.9 (March 26, 2026) - CLEANUP-002:
 *   Removed 3 dead/duplicate code items:
 *   (1) calcTotalStrength() inline const inside useEffect (line ~5198) — identical to
 *       calculateTotalStrength() at module level. Inlined as forceBuilderResult?.total_strength || 0.
 *   (2) addHierarchyToAllUnits() was a local const inside enhanceForceBuilderResult — used only
 *       by a call in the same function and duplicated verbatim as rebuildAllHierarchies() inside
 *       handleUnitUpdate. Hoisted to module level (before enhanceForceBuilderResult); both callers
 *       now reference the single definition.
 *   (3) rebuildAllHierarchies() local const inside handleUnitUpdate — removed; replaced by call
 *       to module-level addHierarchyToAllUnits().
 *   No logic change — only structure.
 *
 * VERSION 4.18.8 (March 25, 2026) - BUG-198:
 *   Battalion display name not regenerated on type change.
 *   Root cause: handleBattalionTypeChange updated unit_type, strength, posture, etc.
 *   but never updated the name field. After changing MECHANIZED → ARTILLERY, the card
 *   still showed "1/Brigade Mechanized" instead of "1/Brigade Artillery".
 *   Fix: regenerate name in handleBattalionTypeChange using battalion_number and
 *   parent_brigade_name (both present on ForceService-built battalions) + type label
 *   map mirroring ForceService._generateBattalionName. HQ type uses "{BrigadeName} HQ".
 *   Fallback: keep existing name when battalion_number or parent_brigade_name absent.
 *
 * VERSION 4.18.7 (March 24, 2026) - BUG-192: Personnel double-counts on build then drops on edit.
 *   Root cause: enhanceForceBuilderResult total_strength formula included battalions.reduce()
 *   but handleUnitUpdate did not — two different formulas for the same value. On build:
 *   4,800 + 3x1,000 = 7,800 (double count). After any SAVE CHANGES: 4,800 (brigade only).
 *   Architecture: brigade.strength at BRIGADE echelon = national-standard TOTAL (battalions
 *   are organic sub-units already counted within it, not additive). Fix: removed
 *   battalions.reduce() from enhanceForceBuilderResult — formulas now identical.
 *
 * VERSION 4.18.6 (March 24, 2026) - BUG-191: Brigade Personnel not adding to total force.
 *   Root cause: handleUnitUpdate overwrote result.brigade.strength with sum(battalions[])
 *   [e.g. 1900] on every SAVE CHANGES, discarding the user's national-standard brigade
 *   strength (e.g. 4800 for German ARMOR). Header showed 1,900 Personnel regardless of type.
 *   Fix: Removed the battalion-sum -> brigade.strength overwrite entirely. At BRIGADE echelon
 *   result.brigade (singular) is the top-level unit; its strength is authoritative. Battalions
 *   are sub-components already counted within brigade.strength, not additive on top.
 *
 * VERSION 4.18.5 (March 24, 2026) - BUG-190: handleUnitTypeChange brigade strength gate.
 *   Root cause: national standard strength won unconditionally for ALL brigade types in the
 *   BRIGADE branch, including CS/CSS types (e.g. LOGISTICS showing 4000 instead of ~2000).
 *   Same antipattern as BUG-178 at battalion level. Fix: added module-level BRIGADE_MANEUVER_TYPES
 *   constant; national standard wins only for maneuver types; CS/CSS types use
 *   UNIT_TYPES_FROM_CONSTANTS[newType].typical_strength (already set in unitTypes.js).
 *
 * VERSION 4.18.4 (March 24, 2026) - BUG-189: Personnel = 0 after SAVE CHANGES at BATTALION echelon.
 *   Root cause: handleUnitUpdate total_strength formula missing updatedResult.battalion?.strength.
 *   For BATTALION echelon the unit lives in .battalion (singular) — not in any array.
 *   Formula had corps/division/brigade/divisions[]/brigades[] but NOT battalion (singular).
 *   Every handleSaveUnit call fired handleUnitUpdate which recalculated total_strength = 0,
 *   overwriting the correct value set by enhanceForceBuilderResult (BUG-188 fix).
 *   Note: BUG-188 guard in enhanceForceBuilderResult already included battalion?.strength
 *   correctly — only handleUnitUpdate was missing it.
 *   Fix: added (updatedResult.battalion?.strength || 0) to the formula.
 *   This is the same pattern as BUG-128 (division singular) and BUG-187 (brigade singular).
 *
 * VERSION 4.18.3 (March 24, 2026) - BUG-188: Personnel = 0 at BATTALION/BRIGADE echelon on build.
 *   Root cause: ForceService.formatBuildResult() for BATTALION and BRIGADE echelons does not
 *   set total_strength on the result object. ARCH-012 (v4.18.2) made all three strength
 *   consumers read total_strength exclusively — no per-echelon fallbacks. Personnel showed
 *   0 immediately after build for BATTALION (Supply/Medical/Infantry) and BRIGADE echelons.
 *   Fix: enhanceForceBuilderResult() now computes total_strength at the end of PASS 4 if
 *   not already set. Same additive formula as handleUnitUpdate. For CORPS/DIVISION the
 *   value is already set by ForceService so the guard (if !result.total_strength) is a no-op.
 *
 * VERSION 4.18.2 (March 24, 2026) - ARCH-012 + BUG-187 + CLEANUP-003/004 + ENH-GAP-001:
 *
 *   ARCH-012: ForceStructurePreview.getSummary() had 4 echelon branches — each needed
 *     individual maintenance and each had its own bug (BUG-128 DIVISION, BUG-187 BRIGADE).
 *     Root problem: branching on echelon to count units and sum strength is wrong architecture.
 *     The function does not need to know what echelon was selected — it just reads what is
 *     present in forceBuilderResult and counts/sums it.
 *     Fix: replaced 4 branches with one uniform function that works for all echelons:
 *       corps count    = corps ? 1 : 0
 *       divisions      = divisions[].length + (division ? 1 : 0)
 *       brigades       = brigades[].length  + (brigade  ? 1 : 0)
 *       battalions     = battalions[].length + (battalion ? 1 : 0)
 *       strength       = total_strength (always — see BUG-187 fix below)
 *       weightedModifier = calculated from all battalions (any echelon that has them)
 *     Only non-zero counts are returned — display panel shows only what exists.
 *
 *   BUG-187: BRIGADE echelon Personnel counter was frozen at build time.
 *     Root cause A (getSummary): read brigade.strength directly — set once by ForceService,
 *       never updated when battalion types/strengths changed. Resolved by ARCH-012.
 *     Root cause B (handleUnitUpdate): total_strength formula did not include
 *       brigade?.strength (singular) — only brigades[] array (empty for BRIGADE echelon).
 *       After any battalion edit, total_strength was always 0 for BRIGADE echelon.
 *     Fix B: handleUnitUpdate now sums battalions[] → writes to brigade.strength, then
 *       total_strength formula includes brigade?.strength. Mirrors DIVISION fix (BUG-128).
 *
 *   CLEANUP-003: calcTotalStrength (useEffect) and calculateTotalStrength (handleComplete)
 *     were identical bodies with different names. Both replaced by direct total_strength
 *     read — single source of truth, no echelon branching, no frozen-strength antipattern.
 *
 *   ENH-GAP-001: Weighted modifier was only shown for BRIGADE echelon. Now shown for any
 *     echelon that has battalions — CORPS and DIVISION now display weighted modifier too.
 *
 * VERSION 4.18.1 (March 24, 2026) - BUG-182: BattalionTypeSelector showed duplicate
 *   "Mechanized Infantry Battalion" in the Combat group — stale 'CAVALRY' key in
 *   COMBAT_TYPES hardcoded list fell back to MECHANIZED via getBattalionTypeConfig().
 *   Root cause: DOCTRINAL-005 (unitTypes.js v2.11) renamed the key to 'RECONNAISSANCE'
 *   but BattalionTypeSelector was never updated.
 *   Fix: Updated all three hardcoded lists in BattalionTypeSelector to match current
 *   BATTALION_TYPES keys exactly (v2.12 — 18 types):
 *     COMBAT (7):     INFANTRY, MECHANIZED, ARMOR, RECONNAISSANCE, AIRBORNE, AIR_ASSAULT, MOUNTAIN
 *     CS (4):         ARTILLERY, AIR_DEFENSE, ENGINEERS, AVIATION
 *     CSS (7):        LOGISTICS, SIGNAL, MEDICAL, MAINTENANCE, TRANSPORT, SUPPLY, MEDICAL_SUPPORT
 *   Also updated BattalionTypeReferenceTable ALL_TYPES to match.
 *
 * VERSION 4.18.0 (March 24, 2026) - BUG-NEW (BATTALION save revert) + BUG-178/179/180 + BUG-181:
 *
 *   BUG-NEW FIX: BATTALION echelon type changes always reverted to MECHANIZED after Save.
 *     Root cause: ForceBuilder.buildBattalion() returns { battalion: Battalion_class_instance, ... }.
 *     This class instance was stored directly in React state (forceBuilderResult.battalion).
 *     When safeDeepClone ran on the class instance in UnitEditDialog, the resulting plain
 *     object's id could not be reliably compared to newResult.battalion.id (class instance)
 *     in handleSaveUnit, causing the ID check to fail silently. The else branch fired:
 *     updateUnitInArray(undefined, updatedUnit) → undefined → newResult.battalion remained
 *     the original class instance → revert to MECHANIZED.
 *     Fix: call ForceBuilder.formatBuildResult('BATTALION', rawResult) after buildBattalion.
 *     This serializes the Battalion class instance via toJSON() to a plain JSON object before
 *     it enters React state. Plain objects have fully predictable spread/comparison semantics.
 *     Mirrors the pattern used by buildForce() for all echelons.
 *
 *   BUG-178 FIX: handleBattalionTypeChange strength used national standard for ALL battalion
 *     types including ADA (400), LOGISTICS (300), SIGNAL (400). Old check:
 *     _natBnStrength > 0 ? _natBnStrength : typical_strength
 *     — national standard wins unconditionally when > 0, overriding doctrinal typical_strength
 *     for CS/CSS types. Fix: gate national standard on MANEUVER_TYPES list, matching the
 *     identical logic in ForceService.buildBattalion v3.3.3 (BUG-176 fix).
 *
 *   BUG-179 FIX: Quick Build Battalion Type onChange used category === 'CSS' || 'SUPPORT' for
 *     strength resolution. Same inconsistency as BUG-178 — FIRES/ADA types incorrectly treated
 *     as maneuver. Fix: replace with MANEUVER_TYPES list check.
 *
 *   BUG-180 FIX: getNatBatStrength in useEffect (initial display strength) used same stale
 *     category check. Fix: MANEUVER_TYPES list, consistent with ForceService.
 *
 *   All three (178/179/180) use the module-level BATTALION_MANEUVER_TYPES constant defined
 *   below, which matches ForceService.buildBattalion v3.3.3 MANEUVER_TYPES exactly.
 *
 * VERSION 4.17.9 (March 23, 2026) - BUG-174c continued: ref sync was async via useEffect —
 *   if user selected type and immediately clicked BUILD before the effect ran, ref still held
 *   the old value. Fix: both Battalion Type and Brigade Type onChange handlers now update
 *   quickConfigRef.current synchronously before calling setQuickConfig. The ref is always
 *   current at BUILD click regardless of React's async rendering cycle.
 *
 * VERSION 4.17.8 (March 23, 2026) - BUG-174c: handleQuickBuild stale closure — root cause
 *   of all unit_type selection failures. quickConfig captured in the handleQuickBuild closure
 *   was always the INITIAL state (unit_type='MECHANIZED') regardless of what the user selected
 *   in the dropdown. The functional setQuickConfig(prev=>) correctly updated React state, but
 *   handleQuickBuild read the OLD closed-over quickConfig — never seeing the update.
 *   Fix: added quickConfigRef (useRef) kept in sync with quickConfig via useEffect. All reads
 *   inside handleQuickBuild now use quickConfigRef.current (always latest state). This is the
 *   actual root cause behind BUG-174/174b/175/176 symptoms — the type selector appeared to
 *   work (state updated correctly) but the build always used the stale initial value.
 *
 * VERSION 4.17.7 (March 23, 2026) - BUG-177: Role 1 chip used has_frontage as proxy.
 *   Root cause: three locations checked has_frontage to decide Role 1 display —
 *   ARTILLERY/ENGINEERS/LOGISTICS/TRANSPORT/SUPPLY all have has_frontage=false but
 *   needs_role1=true, so they showed 'No Role 1' despite having organic medical support.
 *   Fix: all three chip/label locations now read needs_role1 from getBattalionTypeConfig().
 *   Also includes BUG-176b fix (handleBattalionTypeChange category-aware strength).
 *
 * VERSION 4.17.5 (March 22, 2026) - BUG-176: Battalion type-correct strength in quick build.
 *   Root cause: buildBattalion() was always called with strength: quickConfig.strength
 *   (national standard battalion value — 950 for French). This made the type-aware
 *   resolution in ForceService unreachable. All types built at national standard size.
 *   French Logistics battalion built at 950 instead of doctrinal ~500.
 *   Fix A (Tab1): removed explicit strength from buildBattalion() call for BATTALION echelon.
 *     ForceService now derives type-correct strength.
 *   Fix B (Tab1): useEffect and Battalion Type onChange now compute display strength using
 *     the same category logic: CSS/SUPPORT → typical_strength, COMBAT → national standard.
 *   Fix C (ForceService v3.3.2): strength resolution checks unitTypeConfig.category.
 *
 * VERSION 4.17.4 (March 22, 2026) - BUG-175 + BUG-175b:
 *   BUG-175 FIX: Battalion edit dialog changes reverted on save in BATTALION echelon.
 *     Root cause: handleSaveUnit BATTALION branch called updateUnitInArray(newResult.battalions)
 *     — for BATTALION echelon the unit lives in forceBuilderResult.battalion (singular),
 *     battalions[] is always empty. Update wrote to empty array → lost silently.
 *     Fix: check if newResult.battalion.id === updatedUnit.id and merge there first;
 *     fall back to battalions[] for brigade/division/corps echelons where sub-battalions
 *     live in the array.
 *   BUG-175b FIX: Battalion Type selector in quick build panel didn't update Total Strength.
 *     Root cause: onChange only set unit_type, not strength. Engineer (600), Medical (300),
 *     Signal (400) differ from national standard base. Fix: onChange now reads
 *     BATTALION_TYPES[newType].typical_strength and updates quickConfig.strength alongside
 *     unit_type in a single setQuickConfig call.
 *
 * VERSION 4.17.3 (March 22, 2026) - BUG-174b: Battalion/Brigade Type selector onChange
 *   used stale closure form setQuickConfig({ ...quickConfig, unit_type }) — if quickConfig
 *   was stale at the time of the event, the spread overwrote with old values, losing the
 *   user's type selection before BUILD was clicked. Fix: both selectors (BATTALION and
 *   BRIGADE) now use functional form setQuickConfig(prev => ({ ...prev, unit_type }))
 *   which always operates on the latest state regardless of closure age.
 *
 * VERSION 4.17.2 (March 22, 2026) - BUG-173b + BUG-174:
 *   BUG-173b FIX: initializeTacticalInParent() always hardcoded posture='DELIBERATE_DEFENSE'
 *     regardless of battalion unit type. CSS/support battalions (LOGISTICS, SIGNAL, MEDICAL
 *     etc.) generated inside brigade compositions inherited this combat posture, producing
 *     "Invalid posture for Logistics" alert whenever the battalion edit dialog was opened.
 *     The post-hoc translatePostureForUnitType() call in enhanceUnit() was unreliable
 *     (class/plain-object boundary mutation could silently fail).
 *     Fix: initializeTacticalInParent() now calls translatePostureForUnitType(unit.unit_type,
 *     'DELIBERATE_DEFENSE') before setting posture — CSS types receive SUPPORT_OPERATIONS
 *     from the start; combat types receive DELIBERATE_DEFENSE (unchanged). posture_multiplier
 *     set accordingly (0.70 / 1.0 / 0.30). The translatePostureForUnitType call in
 *     enhanceUnit() remains as a second-pass safety net.
 *   BUG-174 FIX: BATTALION echelon quick build always produced Mechanized Infantry
 *     regardless of type. Root cause: buildBattalion() was called without unit_type;
 *     no type selector existed in the BATTALION echelon UI (same class as BUG-172).
 *     Fix A: Battalion Type Select dropdown added to BATTALION echelon UI block,
 *     populated from Object.values(BATTALION_TYPES).
 *     Fix B: unit_type: quickConfig.unit_type passed to buildBattalion() call.
 *
 * VERSION 4.17.1 (March 21, 2026) - BUG-173: handleBattalionTypeChange now updates
 *   strength and posture when battalion type changes in the edit dialog.
 *   Root cause A (strength): strength field was never touched in handleBattalionTypeChange —
 *   changing Air Defense → Infantry left strength at stale value. Fix mirrors BUG-126
 *   (brigade): national standard battalion strength first (getDefaultStrength BATTALION),
 *   config.typical_strength as fallback.
 *   Root cause B (posture): when tactical_in_parent already existed (existing battalion),
 *   the spread `...editedUnit.tactical_in_parent` preserved the old posture — CSS type
 *   inherited combat posture from previous type, showing "Invalid posture" warning and
 *   feeding wrong casualty multiplier to calculator. Fix: always reset posture/role to
 *   type-appropriate defaults on type change, regardless of tactical_in_parent existence.
 *   Downstream: handleSaveUnit BATTALION branch (line ~3372) already saves full updatedUnit
 *   including strength — Tab 2-4 receive correct per-type strength after this fix.
 *
 * VERSION 4.17.0 (March 21, 2026) - BUG-171 + BUG-172:
 *   BUG-172 FIX: BRIGADE echelon quick build was completely non-functional — silent throw.
 *     Root cause: handleQuickBuild passed name/strength/battalions/nationalStandard to
 *     buildBrigade() but never passed unit_type. buildBrigade() throws immediately when
 *     unit_type absent (v3.0 strict validation). catch block swallowed silently — user
 *     clicked BUILD and nothing happened, no feedback.
 *     Fix A: unit_type added to quickConfig initial state (default 'MECHANIZED').
 *     Fix B: quickConfig useEffect changed to spread prev — preserves unit_type across
 *       echelon/nation changes.
 *     Fix C: Brigade Type Select dropdown added to BRIGADE echelon UI (alongside Number
 *       of Battalions). Populated from getAvailableBrigadeTypes(nationalStandard).
 *     Fix D: unit_type: quickConfig.unit_type passed to buildBrigade() call.
 *     Fix E: catch block now sets quickBuildError state — Alert rendered above BUILD
 *       button so future build failures surface to user instead of disappearing silently.
 *   BUG-171 FIX: Battalion strength in BATTALION echelon ignored national standard.
 *     Root cause: buildBattalion() read unitTypeConfig.typical_strength first — always
 *     truthy (INFANTRY/MECHANIZED/ARMOR all 800) — making getDefaultStrength() unreachable.
 *     Fix in ForceService.js v3.3.1: priority swapped to national standard first.
 *     (Tab1 carries no code change for BUG-171 — fix is entirely in ForceService.js.)
 *
 * VERSION 4.16.1 (March 19, 2026) - BUG-164 fix: add division singular to forceData.
 *   forceBuilderResult.division (singular object) was never written to the flat
 *   scenario.force path. Tab2 divisionConfigs initialiser reads scenario.force.divisions
 *   (plural) which is [] in DIVISION echelon. Without the singular path, divisionConfigs
 *   was always [] → phase cascade impossible. Fix: both forceData objects (auto-save
 *   useEffect + handleComplete) now include division: forceBuilderResult?.division||null.
 *   Tab2 v7.12.3 reads scenario?.force?.division as final fallback in divisions useMemo.
 * VERSION 4.16.0 - BUG-131 + BUG-128 + BUG-130 + BUG-129 (March 17, 2026):
 *   BUG-131 FIX: handleSaveUnit BRIGADE branch now rebuilds sub_units when unit_type
 *     changed. Previously, setEditedUnit updated strength (BUG-126 fix) but left
 *     sub_units as the old type composition (e.g. ARTILLERY sub_units sum = 2,600
 *     instead of HEAVY sub_units scaled to national standard = 4,800). The v3.0
 *     calculator reads sub_units not brigade.strength — stale sub_units propagated
 *     wrong strength to Tab 5 and Tab 6. Fix: call
 *     ForceBuilder.generateScaledSubUnitsForTypeChange() when the brigade unit_type
 *     differs from the previously stored type. Returns null for ARTILLERY/CSS/MEDICAL
 *     (those types don't use doctrinal sub_units — no change needed).
 *     Auto-resolves BUG-135 (Role 1 facility count) and BUG-139 (Bde 3 casualties).
 *   BUG-128 FIX: handleUnitUpdate total_strength formula now includes
 *     updatedResult.division?.strength for DIVISION echelon. Previously the formula
 *     only checked updatedResult.divisions[] (array, absent at DIVISION echelon) so
 *     the Division HQ personnel (~1,000) was never added to the total, giving
 *     13,300 instead of ~14,300 (and ~20,400 at CORPS remains correct).
 *   BUG-130 FIX: Combined Effect Preview KIA Rate now applies body armor KIA
 *     reduction (getArmorKIAReduction). calculateForceQualityModifier does not
 *     include body_armor_level — the calculator applies it separately — but the
 *     preview was missing this step. With LEVEL_III: KIA 1.00x → 0.85x.
 *     Added import: getArmorKIAReduction from bodyArmorProtection.js.
 *   BUG-129 FIX (partial): Combined Effect Preview DOW Rate now shows a caption
 *     "incl. {tech.label} ×{techDowFactor}" making the technology-environment
 *     contribution to DOW visible. Previously the factor was silent — DRONE_CONTESTED
 *     adds ×1.08 to DOW via (tech.casualty_multiplier×0.3 + 0.7) inside
 *     calculateForceQualityModifier, producing 0.64x instead of the expected 0.60x.
 *     Full fix requires source audit of calculator (separate session).
 *   CLEANUP: Removed dead empty `if (removed > 0) {}` block in handleSaveUnit.
 *
 * VERSION 4.15.0 - BUG-127: Division HQ chip now shows MULTINATIONAL flag/label instead of NATO for MULTINATIONAL force (display-only fix; NATO baseline used for HQ calculations remains unchanged) (March 17, 2026)
 *
 * VERSION 4.14.0 - BUG-126: handleUnitTypeChange now applies national standard brigade strength on type change — previously used unitTypes.js typical_strength (NATO baseline), not national standard (March 17, 2026)
 *
 * VERSION 4.13.0 - BUG-091 + BUG-093 (March 14, 2026):
 * - BUG-091 FIX: Unit list nationality Chip now shows flag + label text
 *   (e.g. "🌐 Multinational" / "🔵 NATO Standard") instead of flag emoji alone.
 *   Root cause: small Chip rendered only NATIONAL_STANDARDS[ns].flag — NATO roundel
 *   and MULTINATIONAL globe both appear as solid blue circles at size="small".
 *   Fix: label template `${flag} ${label}` — text makes each standard unambiguous
 *   regardless of emoji rendering fidelity.
 * - BUG-093 FIX: TacticalEditor inline summary Chip labels: '[C] Frontage' / '[S] Support'
 *   → 'Frontage' / 'Support'. Root cause: prior [C]/[S] strip fix (session 26) was
 *   applied to the list-view battalion chips (line ~3157) but missed the TacticalEditor
 *   brigade/division summary chips inside the edit dialog Alert at line ~2649.
 *
 * VERSION 4.12.0 - BUG-092 (March 13, 2026):
 * - National standard selector now shown for brigades under a MULTINATIONAL division (MND).
 *   Root cause: render condition checked only corps-level nationalStandard === 'MULTINATIONAL'.
 *   A brigade with parent_division_id pointing to an MND was not matched — selector hidden.
 *   Fix: derived var parentDivIsMultinational looks up the parent division in
 *   forceBuilderResult.divisions and checks its nationalStandard. Render condition updated to
 *   (corpsIsMultinational || parentDivIsMultinational) — covers both cases.
 *
 * VERSION 4.11.0 - ENH-018 Phase F (March 13, 2026):
 * - Lead/Framework Nation concept for MULTINATIONAL corps.
 *   New `corpsLeadNation` state. When set, ForceService.buildCorps() calls
 *   getSupportUnitsForEchelon(corpsLeadNation, 'corps') instead of the averaged
 *   MULTINATIONAL entry — reflects real NATO Framework Nation Concept (FNC).
 * - UI: Select appears inside "Configure Division Standards" card when
 *   echelon=CORPS and nationalStandard=MULTINATIONAL. All nations except
 *   MULTINATIONAL available. Optional — null = use averaged MULTINATIONAL data.
 * - Save path: corpsLeadNation added to forceData auto-save useEffect + reset.
 * - handleNationalStandardChange: clears corpsLeadNation when standard changes.
 * - ForceService.buildCorps() receives config.corpsLeadNation; one conditional
 *   at getSupportUnitsForEchelon call site. All other paths unchanged.
 * - No changes to: calculator, Tab5, nationalStandards.js.
 *
 * VERSION 4.10.0 - BUG-075 + BUG-074 (March 12, 2026):
 * - BUG-075 FIX: nationalStandard Select added to TacticalEditor for brigades in
 *   MULTINATIONAL force. Previously read-only after Quick Build. Excludes MULTINATIONAL
 *   from options. On change resets unit_type if no longer valid for the new standard.
 * - BUG-074 companion: STRYKER now appears in USA/MULTINATIONAL unit type selector
 *   (see nationalStandards.js v2.9 — STRYKER key existed in unitTypes.js since v2.5.0
 *   but was absent from available_brigade_types in nationalStandards.js).
 *
 * VERSION 4.9.0 - BUG-071 + BUG-072 (March 12, 2026):
 * BUG-071: Quick Build permanently disabled for non-MULTINATIONAL CORPS. allFlagsSelected
 *   CORPS branch iterated divisionStandards which is always {} for Romanian/US/NATO —
 *   button never enabled. Fix: short-circuit when nationalStandard !== MULTINATIONAL.
 * BUG-072: Total Strength field showed stale quickConfig.strength for CORPS/DIVISION.
 *   Value never recalculates when numDivisions changes and is not passed to buildCorps().
 *   Fix: show disabled "Calculated from subordinate units" field for CORPS and DIVISION.
 *
 * VERSION 4.8.0 - ENH-018 Phase B: Support Formations display (March 11, 2026):
 *   - Added getSupportUnitTab3Tag() helper — maps provides[] to Tab3 capability label.
 *   - Added SupportFormationsPanel component — renders organic support formations
 *     (Corps Support Group + per-Division Support Groups) as collapsed Accordions
 *     at the bottom of ForceStructurePreview. Read-only. Collapsed by default.
 *   - ForceStructurePreview: mounts SupportFormationsPanel for CORPS and DIVISION
 *     echelons only. Sources:
 *       CORPS:    forceBuilderResult.support_troops (corps group)
 *                 forceBuilderResult.division_support_groups[] (per-division groups)
 *       DIVISION: forceBuilderResult.support_troops (single division group)
 *   - Zero logic changes. No calculator or Tab2-5 impact.
 *
 * VERSION 4.7.6 - COSMETIC-02 + ENH-BAP-MOVE + FIX-C (March 10, 2026):
 *   A. ForceQualityPanel title: "{level} Force Quality Settings" → "Friendly Force Quality".
 *      Cascade Alert: "at {level} level" → "for the entire force". Level prop retained
 *      for potential future use — only the display was changed.
 *   B. Body armor moved from Tab2 EnemyAssessmentPanel → Tab1 ForceQualityPanel (ENH-BAP-MOVE).
 *      - NEW IMPORT: BODY_ARMOR_LEVELS, DEFAULT_BODY_ARMOR_LEVEL from bodyArmorProtection.js
 *      - ForceQualityPanel: "Body Armor Standard" Select added after CLS Training.
 *        Writes to forceQuality.body_armor_level. Reads from force.force_quality in calculator.
 *      - getForceQualityLabel: body_armor_level added to label map.
 *   C. FIX-C: Auto-reset HQ/Support personnel on unit type change (handleUnitTypeChange).
 *      Previously: 200/520 carried over regardless of new type. Default bar showed correct
 *      values but user had to press "Reset to Default" manually.
 *      Fix: same logic as handleResetBreakdown now runs at end of handleUnitTypeChange,
 *      keyed on newStrength (already computed) so brigade support % is correct.
 *
 * VERSION 4.7.5 - BUG-067: Support Personnel always 0 on brigade open (March 9, 2026):
 *   ROOT CAUSE 1: handleAddBrigade sets type='Brigade' (mixed case).
 *   Seed guard checked type==='BRIGADE' (strict) — always false for brigades.
 *   Fell into CORPS/DIVISION else branch → breakdown_ratios.support=0.00 → supportPersonnel=0.
 *   ROOT CAUSE 2 (self-perpetuating): save path persists breakdown:{command:200,support:0}
 *   because hqPersonnel>0 makes includesBreakdown=true. Next open reads stored 0.
 *   FIX A: All 5 type comparisons changed to type?.toUpperCase()==='BRIGADE'.
 *   FIX B: breakdown seed path repairs brigade with breakdown.support===0 by re-deriving
 *   support from national standard (covers all previously saved broken brigades).
 * VERSION 4.7.4 - BUG-066: flushState markDirty (March 8, 2026):
 *   - BUG-066 FIXED: flushState() now calls markDirty() — toolbar tab navigation was
 *     silently saving state without flagging Tab 5 results as stale.
 *
 * VERSION 4.7.3 - ENH-014 + calculationService integration (March 8, 2026):
 *   - ENH-014 FIXED: Division selector inside MULTINATIONAL corps now includes MULTINATIONAL.
 *     Enables MND-SE / MND-N / MND-C (NATO MC 0551). Brigade selector still excludes MULTINATIONAL
 *     (correct doctrine — brigades must have specific national standard).
 *   - calculationService: markDirty() called on Save & Continue — signals Tab 5 that force
 *     composition has changed and any existing calculation result is stale.
 *
 * VERSION 4.7.2 - COSMETIC-01 + FLAG-SORT (March 5, 2026):
 * - FIX COSMETIC-01: Corps count displayed as 0 in ForceStructurePreview.
 *   Root cause: `forceBuilderResult.corps ? 1 : 0` — corps object can be falsy
 *   (null/undefined) at render time even though echelon === 'CORPS' is confirmed.
 *   Fix: `echelon === 'CORPS' ? 1 : 0` — we are inside the CORPS branch, count is
 *   always exactly 1. Same pattern already used for division: `division ? 1 : 0`.
 * - ENH: National standard dropdowns now sorted in preferred order.
 *   sortNationalStandards() utility: MULTINATIONAL first, NATO second, ROMANIAN third,
 *   remaining nations alphabetical by label. Applied at all 3 Select sites:
 *   (1) top-level echelon selector, (2) division standards grid, (3) brigade standards grid.
 *   Nested selectors (div/bde) already filter MULTINATIONAL — they render:
 *   NATO → ROMANIAN → BRITISH → GERMAN → POLISH → US.
 *
 * VERSION 4.7.1 - BUG FIX: Personnel total not updating on unit type change (March 3, 2026):
 * - PROBLEM: Green header "Personnel" count frozen after editing brigade type in TacticalEditor
 * - SYMPTOM: Change Artillery (2,600) → Mechanized Infantry (4,700): header stays at 28,200
 * - ROOT CAUSE: ForceService sets total_strength ONCE at build time (handleQuickBuild).
 *   handleUnitUpdate rebuilt tactical hierarchies but never recalculated total_strength.
 *   ForceStructurePreview and calcTotalStrength() both read forceBuilderResult.total_strength
 *   directly — so the stale snapshot persisted until the next full Quick Build.
 * - IMPACT: Structural (not cosmetic). forceData.totalStrength passed to calculator was also
 *   stale — calculator received wrong force size after any unit type edit.
 * - FIX: Added total_strength recalculation in handleUnitUpdate (after rebuildAllHierarchies,
 *   before setForceBuilderResult). Formula mirrors ForceService.js lines 2480-2487 exactly:
 *     corps.strength + sum(divisions[].strength) + sum(brigades[].strength)
 *     + sum(medical_brigades[].brigade.strength)
 *   Battalions excluded — their strength is already embedded in brigade.strength.
 * - VERIFICATION: Edit Artillery brigade (2,600) → Mechanized Infantry (4,700):
 *   header should update from 28,200 → 30,300 immediately on save.
 *
 * VERSION 4.7.0 - CRITICAL BUG FIX: Support Personnel = 0 (February 16, 2026):
 * - PROBLEM: All brigades showing Support Personnel = 0 instead of ~15-20% of strength
 * - SYMPTOM: NATO 4700-strength brigade showed: 200 HQ / 0 Support (should be 200 HQ / 940 Support)
 * - ROOT CAUSE: breakdown_ratios applied to hq_personnel (200) instead of total strength (4700)
 * - CALCULATION ERROR: 200 * 0.90 = 180 support (WRONG), should be 4700 * 0.20 = 940
 * - IMPACT: Breaks BUG-065 fix - calculator reads breakdown.support = 0, support casualties = 0
 * - FIX: Different logic for BRIGADE vs CORPS/DIVISION
 *   - BRIGADE: HQ = fixed count (200), Support = percentage of total (20%)
 *   - CORPS/DIVISION: Apply breakdown_ratios to HQ strength (e.g., 1500 * 0.10/0.90)
 * - LOCATIONS FIXED:
 *   - Lines 1600-1634: Dialog initialization (loads existing or defaults)
 *   - Lines 2019-2052: Reset button handler
 *   - Lines 2323-2341: Display info alert (shows correct defaults)
 * - VERIFICATION: Edit NATO Mechanized brigade → Should show "Default (NATO): 200 HQ / 940 Support"
 * - RELATED: casualtyCalculator v5.2.0 BUG-065 now receives correct support breakdown
 *
 * VERSION 4.6.4 - CRITICAL BUG FIX: force_quality Naming (February 14, 2026):
 * - PROBLEM: Calculator received force_quality = undefined, all force quality settings ignored
 * - ROOT CAUSE: Tab1 saved forceQuality (camelCase), calculator expects force_quality (snake_case)
 * - IMPACT: CLS training, equipment level, technology environment, morale - ALL ignored by calculator
 * - USER IMPACT: Casualty calculations used fallback values instead of user-configured settings
 * - FIX: Changed TWO save locations from `forceQuality,` to `force_quality: forceQuality,`
 *   - Line 4139: useEffect auto-save (saves on every state change)
 *   - Line 4344: handleComplete button save (saves on "Build Force & Continue")
 * - CASCADING EFFECTS: Affects TCCC survival, body armor protection, drone modifiers, morale effects
 * - VERIFICATION: localStorage now shows scenario.force.force_quality with all 7 settings
 * - RELATED: BUG-A documented, companion to Tab2 v7.4.2 (enemy_force_quality fix)
 *
 * VERSION 4.6.3 - DIAGNOSTIC: Added HQ Position Recalc Logging (February 13, 2026):
 * - PROBLEM: 8 repeated getBrigadeHQPosition calls AFTER force built and saved (performance issue)
 * - SYMPTOM: Console shows "🎯 Brigade HQ Position: MECHANIZED (NATO) = 18km" × 8 after save
 * - PATTERN: Always same brigade type, happens after localStorage save, 8 calls not 6 brigades
 * - IMPACT: Minor console noise, no data accuracy issue (positions are correct)
 * - DIAGNOSTIC: Added 🔴 and 🟡 console logs to trace getBrigadeHQPosition calls in Tab1
 * - CHANGED LINES: 1601 (🔴 invalid position recalc), 1887 (🟡 type change recalc)
 * - PURPOSE: Determine if source is Tab1 useEffect, type change handler, or external (ForceService)
 * - TEST: Build force, check console for 🔴/🟡 logs vs 🎯 logs to identify source
 * - NEXT: Based on log output, will fix in Tab1, ForceService, or brigadeTypeOffsets
 * - NOTE: Positions calculate correctly (18km/23km/20km/25km), just calculated 8 times unnecessarily
 *
 * VERSION 4.6.2 - CRITICAL BUG FIX: ForceStructurePreview Fallback Removal (February 13, 2026):
 * - PROBLEM: Display showed 24,000 personnel instead of 28,200 despite v4.6.1 "fix"
 * - ROOT CAUSE: v4.6.1 removed fallback from useEffect (line 4058) but MISSED ForceStructurePreview (lines 3259, 3273)
 * - SYMPTOM: User sees 24,000 in green "Force Structure Created" card (missing 4,200 HQ personnel)
 * - ARCHITECTURE VIOLATION: ForceStructurePreview still had `|| brigades.reduce()` fallback
 * - FIX: Removed fallback from BOTH locations in ForceStructurePreview component
 * - CHANGED LINES: 3255-3267 (CORPS echelon), 3270-3282 (DIVISION echelon)
 * - VERIFICATION: Now displays correct 28,200 = Corps HQ (2,000) + Div HQs (2,200) + Brigades (24,000)
 * - ADDED: Debug console.error if total_strength missing (helps catch ForceService bugs)
 * - RESULT: Display now matches calculator input - no more 15% undercount
 *
 * VERSION 4.6.1 - CRITICAL ARCHITECTURE FIX: Remove Fallback Logic (February 13, 2026):
 * - PROBLEM: Tab1 had fallback that summed only brigades (24,000), missing Corps+Division HQs (4,200)
 * - ARCHITECTURE VIOLATION: "No fallback" rule - ForceService is single source of truth
 * - ROOT CAUSE: Lines 4048 and 4090 had `brigades.reduce()` fallback when total_strength missing
 * - USER IMPACT: Calculator received totalStrength = 24,000 instead of 28,200 (15% undercount!)
 * - FIX: Removed ALL fallback logic - now uses ONLY forceBuilderResult.total_strength
 * - VERIFICATION: ForceService v3.0.7 line 2464 provides total_strength correctly
 * - ADDED: Debug logging if total_strength missing (indicates ForceService bug)
 * - CHANGED LINES: 4033-4071 (useEffect calcTotalStrength), 4080-4102 (calculateTotalStrength)
 * - RESULT: Calculator now receives correct 28,200 total strength
 * - RELATED: v4.6.0 fixed ESLint, v3.0.7 ForceService calculates total correctly
 *
 * VERSION 4.6.0 - CRITICAL BUG FIX: HQ Casualties Bug (BUG-065) (February 13, 2026):
 * - PROBLEM: Calculator received totalStrength = 2000 (Corps HQ) instead of 46000 (full force)
 * - IMPACT: All casualty calculations WRONG - 23x undercount on force size
 * - ROOT CAUSE: useEffect line 4042 used calcStrength() which returns corps.strength (HQ only)
 * - FIX: Moved calculation inline to useEffect (avoids ESLint exhaustive-deps warning)
 * - LOGIC: Same as calculateTotalStrength() - returns total_strength or sums brigades
 * - VERIFICATION: ForceService v3.0.7 correctly sets corps.strength=2000, total_strength=46000
 * - RESULT: Calculator now receives correct force size, HQ casualties display correctly
 * - CHANGED LINES: 4017-4073 (useEffect with inline calcTotalStrength function)
 * - KEPT: calculateTotalStrength() function (line ~4078) for use in handleComplete
 * - ESLINT: All dependencies now in dependency array, no exhaustive-deps warnings
 * - RELATED FIXES: 
 *   • v4.2.4 fixed display bug (Force Structure Preview showing wrong total)
 *   • v3.0.7 ForceService.js fixed total_strength calculation (sum all brigades)
 *   • This fix completes the chain: ForceService → Tab1 → Calculator → Tab5
 *
 * VERSION 4.3.7 - FEATURE: Manual HQ/Support Personnel Breakdown (February 12, 2026):
 * - NEW: Users can now manually adjust HQ vs Support personnel breakdown per echelon
 * - DISPLAY: Edit dialog shows Total Strength (read-only) + HQ Personnel + Support Personnel
 * - AUTO-CALC: Total = HQ + Support (automatically updates when either field changes)
 * - DEFAULTS: Loads from nationalStandards.js breakdown_ratios (e.g., Romanian Corps: 150 HQ / 1,350 Support)
 * - RESET: "Reset to Default" button restores national standard ratios
 * - SCOPE: Only for Corps, Division, Brigade (Battalion keeps simple strength field)
 * - STORAGE: Breakdown saved to unit.breakdown = { command: X, support: Y }
 * - FLOW: Tab1 → Tab2 (preserved) → Calculator (applies different casualty rates)
 * - Lines changed: 1476-1483 (state), 1512-1537 (initialization), 1895-1945 (handlers), 
 *   1948-1978 (save), 2175-2250 (UI fields)
 *
 * VERSION 4.3.6 - UX FIX: Display top-level echelon in Force Structure Preview (February 12, 2026):
 * - BUG: When user creates Corps, display showed "2 Divisions | 6 Brigades | 18 Battalions"
 *   Missing: "1 Corps" at the beginning — user couldn't verify which echelon they created
 * - FIX: Updated ForceStructurePreview component to show complete hierarchy
 * - Corps echelon now shows: "1 Corps | 2 Divisions | 6 Brigades | 18 Battalions | 24,000"
 * - Division echelon now shows: "1 Division | 3 Brigades | 9 Battalions | 12,000"
 * - Brigade echelon now shows: "1 Brigade | 3 Battalions | 4,000"
 * - Battalion echelon now shows: "1 Battalion | 600"
 * - Changes: Lines 3049-3089 (getSummary function), Lines 3103-3108 (Grid display)
 *
 * VERSION 4.3.5 - CLEANUP: Console debug log removal (February 11, 2026):
 * - Removed 25 console.log debug/trace statements (useEffect traces, cascade logs,
 *   unit type change logs, enhanceForceBuilderResult traces, DEBUG sub_units checks)
 * - Kept: console.warn in getBrigadeHQPosition catch (legitimate recovery warning)
 * - Kept: console.error in handleQuickBuild catch (legitimate error surfacing)
 *
 * VERSION 4.3.4 - BUG FIX: Invalid position_feba_km on dialog open (February 11, 2026):
 * - Old localStorage data stored negative position_feba_km for aviation/CSS/medical brigades
 *   (sign error in unitTypes.js typical_position_km, fixed in unitTypes v2.8)
 * - value={editedUnit.position_feba_km || 5} did not catch negatives (truthy), min:0 just clamped display
 * - Fix: useEffect recalculates via getBrigadeHQPosition if position_feba_km ≤ 0 on BRIGADE open
 * - Self-healing: fixes stale localStorage data on first dialog open, no manual reset needed
 *
 * VERSION 4.3.3 - UX FIX: MEDICAL brigade type change auto-deletes battalions (February 11, 2026):
 * - REMOVED: Blocking error when changing to MEDICAL with existing battalions
 * - ADDED: Auto-delete child battalions when MEDICAL brigade is saved
 * - ADDED: Blue info Alert informing user how many battalions were removed and why
 * - ADDED: unitTypeInfo state (separate from unitTypeError) for non-blocking informational messages
 * - Both states reset on dialog open (useEffect) and on type change
 *
 * VERSION 4.3.2 - BUG FIX: Duplicate unitTypeError display (February 11, 2026):
 * - FormHelperText below Unit Type dropdown was showing unitTypeError text
 *   while the Alert banner at top also showed same text — duplicate display
 * - Fix: FormHelperText shows hint when no error, non-breaking space when error exists
 *   Banner (Alert) remains sole owner of error message display
 *
 * VERSION 4.3.1 - BUG FIX: Brigade HQ position on unit type change (February 11, 2026):
 * - BUG-REG-001: handleUnitTypeChange used defaults.position_km (battalion value, 5km for Infantry)
 *   instead of getBrigadeHQPosition() for BRIGADE echelon
 * - Fix: unitEchelon === 'BRIGADE' → getBrigadeHQPosition(nationalStandard, newType)
 *   unitEchelon === 'BATTALION' → defaults.position_km (unchanged)
 * - Added import: getBrigadeHQPosition from ../constants/brigadeTypeOffsets
 * - Result: NATO Artillery brigade 23km, NATO Infantry brigade 18km, US MEDICAL 35km (correct)
 *
 * VERSION 4.3.0 - TOOLBAR NAVIGATION ARCHITECTURE (February 10, 2026):
 * - ARCH: Added forwardRef + useImperativeHandle for toolbar navigation compatibility
 *   - App.jsx can now call tab1Ref.current.flushState() before toolbar tab switch
 *   - flushState is a no-op in Tab1 (useEffect already auto-flushes on every state change)
 *   - Stub present for architectural consistency with Tabs 2-6
 * - BUG FIX: CRIT-007 pattern fixed in Tab1 (was fixed in Tab3/Tab4 but missed here)
 *   - useEffect onUpdate call: { ...scenario, force: forceData } -> { force: forceData }
 *   - handleComplete onUpdate call: { ...scenario, force: forceData } -> { force: forceData }
 *   - Tab1 now passes only owned data; App.jsx merges with current state (correct pattern)
 * - ESLINT FIX: Removed eslint-disable-next-line react-hooks/exhaustive-deps
 *   - onUpdate wrapped in useRef (onUpdateRef) - stable reference avoids infinite loop
 *   - useEffect dependency array is now correct without suppression
 *
 * VERSION 4.2.4 - CRITICAL TOTAL FORCE DISPLAY FIX (February 6, 2026):
 * - BUG FIX: Force Structure Preview showed 2,000 (Corps HQ only) instead of 56,000 (full force)
 * - ROOT CAUSE 1: ForceStructurePreview.getSummary() used corps.strength (HQ only) - line 3028
 * - ROOT CAUSE 2: calculateTotalStrength() returned HQ strength for Corps/Division - line 3824
 * - USER IMPACT: Tab1 displayed "2,000 Personnel" even with 9 brigades configured
 * - FIX 1: ForceStructurePreview now uses forceBuilderResult.total_strength (sum of brigades)
 * - FIX 2: calculateTotalStrength now returns total_strength or sums brigades for Corps/Division
 * - RESULT: Tab1 now correctly displays "56,000 Personnel" for 9-brigade force 
 * - DOWNSTREAM: Tab4 v6.0.2 reads this correctly, no longer shows "Force: 2,000" 
 * - LINES CHANGED: 3022-3037 (ForceStructurePreview), 3819-3835 (calculateTotalStrength)
 * 
 * VERSION 4.2.3 - MEDICAL BRIGADE & ESLINT FIXES (February 5, 2026):
 * - BUG FIX: Prevent changing brigade to MEDICAL type if it has battalions
 * - ISSUE: User could change SUSTAINMENT brigade to MEDICAL, but battalions remained
 * - RESULT: Medical Brigade showed with artillery/combat battalions (incorrect!)
 * - FIX: handleUnitTypeChange now checks for child battalions before allowing MEDICAL change
 * - ERROR MSG: "Cannot change to MEDICAL brigade type: This brigade has N battalion(s)"
 * - SOLUTION: User must delete battalions first, or rebuild with Quick Build
 * - ESLINT FIX: Removed unused getHQPosition import (line 295)
 * - NOTE: getHQPosition moved to ForceService in Phase 2.1, no longer needed in Tab1
 * 
 * VERSION 4.2.2 - CRITICAL STRENGTH OVERWRITE FIX (February 5, 2026):
 * - BUG FIX: enhanceForceBuilderResult was overwriting correct HQ strengths!
 * - ISSUE: getDefaultStrength returns TOTAL force (12,000), not HQ (1,000)
 * - RESULT: Division HQ showed 12,000/20,000/14,000 instead of 1,000
 * - ROOT CAUSE: Lines 3615-3618, 3629-3632, 3641-3645, 3668-3671, 3682-3685
 * - FIX: Removed ALL strength overwrites - ForceService already sets correct values
 * - FIX: Changed line 3653 to sum brigades instead of divisions (was using HQ values!)
 * - VALIDATION: ForceService.buildDivision correctly uses hq_personnel from nationalStandards
 * - VALIDATION: ForceService.buildBrigade already built with correct nationalStandard
 * - Now: Division HQ = 1,000 (correct), Brigade = 4,000/5,000/4,500 (correct)
 * 
 * VERSION 4.2.1 - CRITICAL VALIDATION FIX (February 5, 2026):
 * - BUG FIX: Validation was checking wrong variable!
 * - ISSUE: quickConfig.divisions/brigades (value: 2) vs numDivisions/numBrigades (value: 3)
 * - RESULT: Division 3 dropdown shown but never validated
 * - FIX: Changed validation to use numDivisions/numBrigades (actual UI state)
 * - Now validates ALL divisions/brigades that user configured
 * - Dependencies updated: quickConfig.divisions  numDivisions, quickConfig.brigades  numBrigades
 * 
 * VERSION 4.2 - National Standard Validation (February 5, 2026):
 * - CRITICAL FIX: Removed all nationalStandard fallbacks (|| 'NATO')
 * - Option B: Disable "Quick Build" button until all required flags selected
 * - Added useMemo() validation: allFlagsSelected checks flags before build
 * - CORPS: Validates divisionStandards[1] through divisionStandards[N]
 * - DIVISION: Validates brigadeStandards[1] through brigadeStandards[N]
 * - BRIGADE/BATTALION: Validates nationalStandard exists and not 'MULTINATIONAL'
 * - Button disabled proactively - prevents errors before they happen
 * - Removed fallbacks at lines 2004, 2764 (editedUnit, division.nationalStandard)
 * - Future-proof: Works for any new national standards (German, Polish, French, etc.)
 * 
 * VERSION 4.1 - Complete Tab Validation (February 3, 2026):
 * - CRITICAL FIX: Added unit_type validation to "Save & Continue" button
 * - handleComplete() now checks ALL brigades/battalions have unit_type
 * - Prevents proceeding to Tab 2 with missing unit_type
 * - Shows clear error: "Brigade 'X' is missing Unit Type (required for v3.0)"
 * - Complete validation layers: UnitEditDialog + handleComplete + ForceService
 * 
 * VERSION 3.15 CRITICAL BUG FIX (January 31, 2026):
 * - BUG FIX: QuickBuild was passing wrong strength to ForceBuilder!
 *   - CRITICAL BUG: Lines 2914 and 2923 passed national standard strength to Corps/Division
 *   - Example: ROMANIAN standard passes corps.strength = 36000 (total force, not HQ!)
 *   - Impact: Corps created with 36000 instead of 2000 (HQ personnel)
 *   - Impact: Division created with 12000 instead of 1000 (HQ personnel)
 *   - FIXED: Removed strength parameter from buildCorps() and buildDivision() calls
 *   - Result: ForceBuilder now uses default HQ strength (Corps: 2000, Division: 1000)
 *   - Combined with ForceBuilder v2.13.0 fix, Corps/Division HQ now correct!
 *   - Root cause: National standards stored total force, not HQ strength
 * 
 * VERSION 3.14 CRITICAL BUG FIX (January 31, 2026):
 * - BUG FIX: Corps/Division HQ strength overwritten with subordinate totals!
 *   - CRITICAL BUG: Lines 3508 and 3545 were overwriting HQ personnel with force totals
 *   - result.corps.strength (Corps HQ ~2000) was being set to division total (~46000)
 *   - result.division.strength (Division HQ ~1000) was being set to brigade total
 *   - Impact: Corps DNBI calculations used 46000 instead of 2000 (23x wrong!)
 *   - Impact: Support unit calculations failed (total_strength undefined)
 *   - FIXED: Now stores total force in result.total_strength (separate field)
 *   - FIXED: Keeps result.corps.strength as Corps HQ personnel only
 *   - FIXED: Keeps result.division.strength as Division HQ personnel only
 *   - Example: Corps HQ 2000  30 DNBI (correct), was using 46000  690 DNBI (wrong!)
 *   - Root cause: "Recalculate total strength" logic was overwriting wrong field
 *   - Console now shows: "Total force strength: 46000 (Corps HQ: 2000)"
 * 
 * VERSION 3.13 SECTION 12 FIX (January 31, 2026):
 * - SECTION 12: Initialize operational_state for Corps and Divisions
 *   - ISSUE: Corps/Divisions were created without operational_state field
 *   - Calculator v4.43 removed fallbacks, exposing missing data
 *   - FIXED: enhanceUnit() now sets default operational_state based on echelon:
 *     * CORPS  'SUPPORT_ACTIVE' (HQ units don't engage directly in combat)
 *     * DIVISION  'COMBAT_ENGAGED' (divisions actively fighting)
 *     * BRIGADE/BATTALION  'COMBAT_ENGAGED' (default for combat units)
 *   - Users can still edit via UnitEditDialog operational_state dropdown
 *   - Cascade already working (Corps  Divisions  Brigades)
 *   - Impact: Proper DNBI calculations for echelon HQs
 *   - Example: Corps (2000 personnel, SUPPORT_ACTIVE)  30 DNBI/10 days
 *            vs Corps (2000 personnel, COMBAT_ENGAGED)  90 DNBI/10 days
 * 
 * VERSION 3.12 CRITICAL BUG FIX (January 31, 2026):
 * - BUG FIX: Operational State NOT included in Combined Casualty Multiplier!
 *   - CRITICAL: Support Active (0.2x) was calculating same as Combat Engaged (1x)
 *   - Result: Support units had 5x too many casualties (2.06x instead of 0.41x)
 *   - FIXED: Added operational_state.battle_casualty_multiplier to calculateTotalMultiplier
 *   - FIXED: Updated display formula to show " Op State (0.2x)" 
 *   - Formula now: Assigned Task  Posture  Unit Type  Force Quality  Op State
 *   - Example: Support Active = 1.5  1.1  1.0  1.0  0.2 = 0.33x (correct!)
 *   - Example: Combat Engaged = 1.5  1.1  1.0  1.0  1.0 = 1.65x (correct!)
 * 
 * VERSION 3.11 FIELD RENAME (January 31, 2026):
 * - RENAMED: "Mission"  "Assigned Task" (clarity with Tab 2 operational mission)
 * - FIELD CHANGE: tactical_in_parent.mission  tactical_in_parent.tactical_role
 * - PURPOSE: Distinguish Tab 1 "Assigned Task" (Main Effort/Supporting/Reserve)
 *            from Tab 2 "Operational Mission" (Attack/Defend/Consolidation)
 * - RATIONALE: "Assigned Task" = order from higher echelon (DivisionBrigade)
 *              "Operational Mission" = type of operation being conducted
 * - Note: mission_multiplier field name unchanged (still casualty multiplier)
 * 
 * VERSION 3.10 BUG FIXES (January 30, 2026):
 * - BUG FIX 1: Removed duplicate "Engineer Brigade" from Unit Type Defaults Reference table
 *   - Added filter to show only first occurrence of each unique label
 *   - ENGINEER and ENGINEERS both point to same template causing duplicate display
 * - BUG FIX 2: CASCADE NOT WORKING - Operational State not propagating
 *   - CRITICAL: operational_state was not cascading from Corps  Division  Brigade
 *   - User sets Corps to "Combat Engaged" but Divisions/Brigades stayed "Support Active"
 *   - Added operational_state to Corps  Divisions cascade
 *   - Added operational_state to Corps  Brigades cascade
 *   - Added operational_state to Division  Brigades cascade
 *   - Now: Change Corps state  All children inherit automatically
 *   - Impact: Fixes major usability issue where cascade appeared broken
 * - COSMETIC FIX: Unit Type Defaults Reference table only shows for BRIGADE/BATTALION editing
 *   - Was showing brigade types when editing CORPS/DIVISION (didn't make sense)
 *   - Now conditional: only displays when editing BRIGADE or BATTALION level
 *   - Cleaner UI for CORPS/DIVISION editing
 * 
 * VERSION 3.8 UI-FIX (January 19, 2026):
 * - UI-FIX: Removed native spinner arrows from Total Strength field
 *   - Before: Total Strength had arrow spinners, other fields had +/- buttons (inconsistent)
 *   - After: Total Strength is plain input, small-number fields use +/- buttons
 *   - Used CSS to hide WebKit/Firefox native spinners
 *   - Improves UI consistency in Quick Build with ForceBuilder section
 * 
 * VERSION 3.7 BUGFIX (January 19, 2026):
 * - FIXED BUG-005: Battalion type dropdown showing duplicate "Mechanized Infantry Battalion"
 *   - Root cause: Keys in BattalionTypeSelector didn't match BATTALION_TYPES in unitTypes.js
 *   - ENGINEER  ENGINEERS (unitTypes.js uses plural form)
 *   - HQ  MAINTENANCE (HQ doesn't exist in BATTALION_TYPES)
 *   - Now dropdown shows correct 12 battalion types without duplicates
 * 
 * VERSION 3.6 BUGFIX (January 18, 2025):
 * - FIXED CRIT-003: DNBI rate showing "undefined/1000" in Operational State dropdown
 *   - Changed line 1681 from state.dnbi_rate_per_1000_day to getDNBIRate(state.value)
 *   - getDNBIRate() was already imported but not used in dropdown
 *   - Now correctly displays 4.5, 1.5, or 0.5 per 1000 per day
 * 
 * VERSION 3.5 CLS Training Feature (January 12, 2025):
 * - ADD: CLS Training Level dropdown in Force Quality Settings
 *   - Combat Lifesaver trained personnel percentage
 *   - Levels: LOW (10%), STANDARD (25%), HIGH (40%), ELITE (60%)
 *   - Affects DOW (Died of Wounds) rates through buddy care quality
 *   - Satisfies validation requirement for cls_training_percentage
 * - ADD: CLS_TRAINING_LEVELS constant with DOW multipliers
 * - ADD: cls_training_percentage to DEFAULT_FORCE_QUALITY (default: STANDARD)
 * 
 * VERSION 3.4 CSS HYBRID APPROACH (January 9, 2025):
 * - CSS-005: Brigade type dropdown now filtered by national standard
 *   - ROU shows: MECHANIZED, ARMOR, MOUNTAIN, ARTILLERY, LOGISTICS, MEDICAL
 *   - USA shows: MECHANIZED, ARMOR, STRYKER, AIRBORNE, AIR_ASSAULT, ARTILLERY, SUSTAINMENT, MEDICAL
 *   - NATO shows both LOGISTICS and SUSTAINMENT for multinational ops
 * - T1-003: Removed +/- buttons from number inputs (standardized with other selectors)
 * - Import getAvailableBrigadeTypes from nationalStandards.js
 * 
 * VERSION 3.3 BUGFIXES (December 30, 2024):
 * - FIXED T1-BUG-001: "Invalid echelon" error - calculateEvacuationAssets now receives
 *   uppercase echelon (editedUnit.type.toUpperCase()) instead of mixed-case
 * - FIXED T1-BUG-002: Battalion tactical_in_parent now fully initialized when null
 *   (was only setting hq_position_feba_km, missing posture/mission/medical fields)
 * 
 * VERSION 3.2 FEATURES (December 24, 2024):
 * 
 * v3.2 BUGFIXES & IMPROVEMENTS:
 * - FIXED: handleUnitTypeChange now updates strength when unit type changes
 * - FIXED: CSS units (LOGISTICS, MEDICAL, SIGNAL) now use SUPPORT_OPERATIONS posture
 *          instead of HASTY_DEFENSE (which made no sense for rear-area units)
 * - FIXED: Multinational division/brigade dropdowns now DYNAMIC from NATIONAL_STANDARDS
 *          (no more hardcoded ROMANIAN/NATO/US - supports any nation in constants)
 * - Enables true modular architecture - add nations in nationalStandards.js only
 * 
 * VERSION 3.0 FEATURES:
 * 
 * Task 1.2 (Auto-Apply): When unit type changes, automatically sets:
 *   - Position from FEBA (typical_position_km)
 *   - Default Posture
 *   - Default Mission
 *   - Operational State (COMBAT_ENGAGED, SUPPORT_ACTIVE, TRUE_RESERVE)
 *   - Casualty Modifier
 *   - Frontage status (has_frontage)
 *   - NEW v3.2: Strength (typical_strength for unit type)
 * 
 * Task 1.4 (Unit Type Defaults Reference):
 *   - Expandable educational panel showing ALL unit type defaults
 *   - Table view with Position, Posture, Op State, Frontage, Casualty
 *   - Highlights currently selected unit type
 *   - Helps students understand tactical defaults
 * 
 * Task 1.5 (Operational State Selector):
 *   - Dropdown selector for COMBAT_ENGAGED, SUPPORT_ACTIVE, TRUE_RESERVE
 *   - Shows casualty multiplier for each state
 *   - Description helper text
 *   - Auto-applied when unit type changes
 * 
 * Task 1.6 (National Caveats for MULTINATIONAL):
 *   - Caveat configuration for units in multinational operations
 *   - Preset options: NONE, STANDARD, DEFENSIVE, RESTRICTED, SECURITY
 *   - Custom caveat selection with categories
 *   - Shows active restrictions and impact summary
 *   - Affects missions, ROE, and medical evacuation
 *
 * Task 1.7 (Battalion-Level Unit Type Support) 
 * FEATURES ADDED:
 * - Battalion-specific unit type selector (11 types)
 * - Auto-applies operational state, position, casualty modifier
 * - Visual indication of combat vs support battalions
 * - Role 1 facility status display
 * - DNBI rate display
 * - Battalion composition summary with weighted casualty modifier
 * - Educational reference table for all battalion types
 * 
 * PREVIOUS FEATURES (v2.8):
 * - FIXED: tactical_in_parent now initializes for ALL echelons
 * - Fixes "Cannot Edit Unit - Unit Not Properly Initialized" error
 * 
 * EXISTING FEATURES (v2.7):
 * - Fixed: Imports from enhanced nationalStandards.js
 * - Fixed: Multinational Division support (brigade standard selection)
 * 
 * EXISTING FEATURES (v2.6):
 * - Force Quality at Corps level (training, equipment, morale, experience)
 * - Cascade inheritance: Corps  Division  Brigade  Battalion
 * - Override capability at any level
 * 
 * Author: Dr. Tatar Radu MD
 * Date: January 18, 2025
 * Version: 3.6 - CRIT-003 DNBI undefined fix
 */

import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Grid,
  Paper,
  Chip,
  Stack,
  Tooltip,
  Switch,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MilitaryTech as BattalionIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalHospital as MedicalIcon,
  FlashOn as QuickBuildIcon,
  Build as ManualBuildIcon,
  Info as InfoIcon,
  Public as GlobalIcon,
  Psychology as TrainingIcon,
  Shield as EquipmentIcon,
  Favorite as MoraleIcon,
  ContentCopy as InheritedIcon,
  Create as OverrideIcon
} from '@mui/icons-material';

// Constants
import { 
  NATIONAL_STANDARDS,
  getDefaultStrength,
  getAvailableBrigadeTypes  // v3.4 CSS-005: Filter brigade types by national standard
} from '../constants/nationalStandards';
import { getBrigadeHQPosition } from '../constants/brigadeTypeOffsets';
import { BRIGADE_CARDS_ON, BRIGADE_CARD_TEMPLATES } from '../constants/brigadeCards';
import { moveAnchoredCards } from '../constants/brigadeSupportStructure';
import { unitTypeLabel, brigadeTypeLabel } from '../constants/unitTypeLabels';
import { 
  UNIT_TYPES as UNIT_TYPES_FROM_CONSTANTS,
  BATTALION_TYPES,
  getBattalionComposition,          // BUG-NEXT-011: generate battalions on handleAddBrigade
  getCasualtyModifier,
  getUnitTypeDefaults,
  isMedicalBrigade,
  getOperationalStatesArray,
  translatePostureForUnitType,      // NEW: CSS posture translation
  validateUnitOperationalSettings,  // NEW: Mode-aware validation
  getOperationalConstraints         // NEW: Get allowed postures/states
} from '../constants/unitTypes';
import {
  FORCE_ECHELONS
} from '../constants/forceEchelons';

// Operational States - from dedicated file
import { 
  OPERATIONAL_STATES,
  getDNBIRate
} from '../constants/operationalStates';

// Enhanced constants
import { 
  getMissionByValue, 
  getMissionsArray 
} from '../constants/missions';

import { 
  getPostureByValue, 
  getPosturesArray 
} from '../constants/postures';

import {
  calculateEvacuationAssets,
  BASE_EVACUATION_ALLOCATIONS,
  getUnitTypeLabel
} from '../constants/evacuationAssetFactors';

import {
  getDefaultOffset,
  getMTFRoleByEchelon,
  getMTFTypeName
} from '../constants/mtfDefaultOffsets';

// Medical Assets - for evacuation allocation
import {
  AUTO_ALLOCATION as EVAC_AUTO_ALLOCATION
} from '../constants/medicalAssets';

// MTF Capability definitions available in mtfCapabilities.js if needed for tooltips
// ROLE_1_DEFINITION, ROLE_2_BASIC_DEFINITION, ROLE_2_ENHANCED_DEFINITION, ROLE_3_DEFINITION

// Force Quality Constants
import {
  TRAINING_LEVELS,
  EQUIPMENT_LEVELS,
  MORALE_LEVELS,
  EXPERIENCE_MODIFIERS,
  TECHNOLOGY_ENVIRONMENT,
  MEDICAL_CAPABILITY,
  calculateForceQualityModifier,
  DEFAULT_FORCE_QUALITY,
  getDefaultForceQuality    // v4.3.5: Returns fresh copy of default force quality
} from '../constants/combatEffectivenessModifiers';

import {
  BODY_ARMOR_LEVELS,
  DEFAULT_BODY_ARMOR_LEVEL,
  getArmorKIAReduction
} from '../constants/bodyArmorProtection';

// Utilities
import { ForceBuilder } from '../services/ForceService';
import { markDirty } from '../services/calculationService.js';

// NEW Task 1.6: National Caveats for Multinational Operations
import {
  CAVEAT_TYPES,
  NATIONAL_CAVEAT_PRESETS,
  getCaveatTypesArray,
  getCaveatPresetOptions,
  calculateCaveatImpact,
  formatCaveatSummary
} from '../constants/nationalCaveats';
import { useTranslation } from 'react-i18next';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// NOTE: CLS_TRAINING_LEVELS and DEFAULT_FORCE_QUALITY are imported from
// ../constants/combatEffectivenessModifiers (moved there in v4.3.5)

/**
 * Get force quality label for display
 */
const getForceQualityLabel = (type, value) => {
  const maps = {
    training_level: TRAINING_LEVELS,
    equipment_level: EQUIPMENT_LEVELS,
    morale_level: MORALE_LEVELS,
    experience_level: EXPERIENCE_MODIFIERS,
    technology_env: TECHNOLOGY_ENVIRONMENT,
    medical_capability: MEDICAL_CAPABILITY,
    body_armor_level: BODY_ARMOR_LEVELS
  };
  return maps[type]?.[value]?.label || value;
};

/**
 * Resolve force quality for a unit by cascading from ancestors
 * 
 * @param {Object} unit - The unit to resolve force quality for
 * @param {Object} forceBuilderResult - Complete force structure
 * @returns {Object} Resolved force quality with inheritance info
 */
const resolveForceQuality = (unit, forceBuilderResult) => {
  if (!forceBuilderResult) {
    return { 
      resolved: DEFAULT_FORCE_QUALITY, 
      inherited_from: 'DEFAULT',
      overrides: {}
    };
  }

  // Start with corps-level defaults
  let baseQuality = forceBuilderResult.corps?.force_quality || 
                    forceBuilderResult.division?.force_quality ||
                    forceBuilderResult.brigade?.force_quality ||
                    DEFAULT_FORCE_QUALITY;
  
  let inheritedFrom = 'CORPS';
  const overrides = {};

  // Helper to find parent units
  const findDivision = (id) => forceBuilderResult.divisions?.find(d => d.id === id);
  const findBrigade = (id) => forceBuilderResult.brigades?.find(b => b.id === id);

  const unitType = unit.type?.toUpperCase();

  // Apply division override if exists
  if (unitType === 'BRIGADE' || unitType === 'BATTALION') {
    let divisionId = unit.parent_division_id;
    if (unitType === 'BATTALION') {
      const parentBrigade = findBrigade(unit.parent_brigade_id);
      divisionId = parentBrigade?.parent_division_id;
    }
    
    const division = findDivision(divisionId);
    if (division?.force_quality_override) {
      Object.keys(division.force_quality_override).forEach(key => {
        if (division.force_quality_override[key] !== null && 
            division.force_quality_override[key] !== undefined) {
          baseQuality = { ...baseQuality, [key]: division.force_quality_override[key] };
          overrides[key] = { level: 'DIVISION', value: division.force_quality_override[key] };
        }
      });
      if (Object.keys(division.force_quality_override).length > 0) {
        inheritedFrom = 'DIVISION';
      }
    }
  }

  // Apply brigade override if exists
  if (unitType === 'BATTALION') {
    const brigade = findBrigade(unit.parent_brigade_id);
    if (brigade?.force_quality_override) {
      Object.keys(brigade.force_quality_override).forEach(key => {
        if (brigade.force_quality_override[key] !== null && 
            brigade.force_quality_override[key] !== undefined) {
          baseQuality = { ...baseQuality, [key]: brigade.force_quality_override[key] };
          overrides[key] = { level: 'BRIGADE', value: brigade.force_quality_override[key] };
        }
      });
      if (Object.keys(brigade.force_quality_override).length > 0) {
        inheritedFrom = 'BRIGADE';
      }
    }
  }

  // Apply unit's own override
  if (unit.force_quality_override) {
    Object.keys(unit.force_quality_override).forEach(key => {
      if (unit.force_quality_override[key] !== null && 
          unit.force_quality_override[key] !== undefined) {
        baseQuality = { ...baseQuality, [key]: unit.force_quality_override[key] };
        overrides[key] = { level: unitType, value: unit.force_quality_override[key] };
      }
    });
    if (Object.keys(unit.force_quality_override).length > 0) {
      inheritedFrom = 'SELF';
    }
  }

  return {
    resolved: baseQuality,
    inherited_from: inheritedFrom,
    overrides
  };
};

// ============================================================================
// TACTICAL POSITIONING UTILITY
// ============================================================================

/**
 * Get tactical position label based on unit position
 */
const getPositionLabel = (position, totalUnits) => {
  if (totalUnits === 1) return 'Center';
  
  if (totalUnits === 2) {
    return position === 1 ? 'Left' : 'Right';
  }
  
  if (totalUnits === 3) {
    if (position === 1) return 'Left';
    if (position === 2) return 'Center';
    return 'Right';
  }
  
  if (totalUnits === 4) {
    if (position === 1) return 'Left';
    if (position === 2) return 'Center-Left';
    if (position === 3) return 'Center-Right';
    return 'Right';
  }
  
  if (totalUnits === 5) {
    if (position === 1) return 'Far Left';
    if (position === 2) return 'Left';
    if (position === 3) return 'Center';
    if (position === 4) return 'Right';
    return 'Far Right';
  }
  
  if (totalUnits >= 6) {
    const leftSector = Math.ceil(totalUnits / 3);
    const rightStart = totalUnits - Math.floor(totalUnits / 3) + 1;
    
    if (position === 1) return 'Far Left';
    if (position <= leftSector) return 'Left Sector';
    if (position >= rightStart) return position === totalUnits ? 'Far Right' : 'Right Sector';
    return 'Center Sector';
  }
  
  return `Position ${position}`;
};

// ============================================================================
// TASK 1.7: BATTALION TYPE HELPERS
// ============================================================================

/**
 * Get battalion type configuration from BATTALION_TYPES constant
 * @param {string} type - Battalion unit type key (INFANTRY, MECHANIZED, etc.)
 * @returns {Object} Battalion type configuration
 */
// 2026-10-17 (Dr. Tatar): engineers work in the forward area (breaching, bridging, mine clearance) but do not hold a sector of
// the front, so they are not 'frontage' in the sense of the first-line fighters. The engine rate is unchanged (task-based engineer
// exposure is on the to-do); only the label says forward area.
const FORWARD_AREA_SUPPORT_TYPES = ['ENGINEERS'];
const isForwardAreaSupport = (config) => FORWARD_AREA_SUPPORT_TYPES.includes(config?.value);

// 2026-10-24: the Role 1 chip of a card shows what was built: the brigade HQ, the support battalion and the signal company cards carry no
// Role 1 whatever their type says (brigadeCards.js role1 false), so the chip follows the facility list when the force has one.
const battalionHasRole1 = (unit, forceBuilderResult, config) => {
  const facilities = forceBuilderResult?.role1Facilities || forceBuilderResult?.role1_facilities;
  if (Array.isArray(facilities) && facilities.length > 0) {
    return facilities.some((f) => f.parent_battalion_id === unit.id);
  }
  return Boolean(config?.needs_role1);
};

const getBattalionTypeConfig = (type) => {
  return BATTALION_TYPES[type] || BATTALION_TYPES.MECHANIZED || {
    label: 'Mechanized Battalion',
    icon: 'BTN',
    casualty_modifier: 1.0,
    has_frontage: true,
    operational_state: 'COMBAT_ENGAGED',
    typical_position_km: 3,
    typical_strength: 800,
    category: 'COMBAT'
  };
};

/**
 * Check if unit is a battalion
 */
const isBattalion = (unit) => {
  return unit?.type?.toUpperCase() === 'BATTALION';
};

/**
 * Calculate battalion composition summary for a brigade
 * Shows weighted casualty modifier and combat/support breakdown
 */
const calculateBattalionCompositionSummary = (battalions) => {
  if (!battalions || battalions.length === 0) {
    return { total: 0, combat: 0, support: 0, weightedModifier: 1.0, totalStrength: 0 };
  }
  
  let totalStrength = 0;
  let weightedModifier = 0;
  let combatCount = 0;
  let supportCount = 0;
  
  battalions.forEach(bn => {
    const bnType = bn.unit_type || 'MECHANIZED';
    const config = getBattalionTypeConfig(bnType);
    const strength = bn.strength || config.typical_strength || 800;
    
    totalStrength += strength;
    weightedModifier += strength * (config.casualty_modifier || 1.0);
    
    if (config.has_frontage) {
      combatCount++;
    } else {
      supportCount++;
    }
  });
  
  return {
    total: battalions.length,
    combat: combatCount,
    support: supportCount,
    totalStrength,
    weightedModifier: totalStrength > 0 ? (weightedModifier / totalStrength) : 1.0
  };
};

// ============================================================================
// CONSTANTS
// ============================================================================

// FORCE_ECHELONS - now imported from forceEchelons.js

const BUILD_MODE = {
  QUICK: 'QUICK',
  MANUAL: 'MANUAL'
};

// BUG-178/179/180 FIX: MANEUVER battalion types — national standard battalion strength is
// authoritative for these. CS/CSS types use doctrinal typical_strength instead.
// Must match ForceService.buildBattalion v3.3.3 MANEUVER_TYPES exactly.
const BATTALION_MANEUVER_TYPES = [
  'INFANTRY', 'MECHANIZED', 'ARMOR', 'RECONNAISSANCE',
  'AIRBORNE', 'AIR_ASSAULT', 'MOUNTAIN', 'HEAVY', 'STRYKER', 'AVIATION'
];

// BUG-190 FIX: MANEUVER brigade types — national standard brigade strength is
// authoritative for these. CS/CSS brigade types use unitTypes.js typical_strength instead.
// Pattern mirrors BATTALION_MANEUVER_TYPES above (BUG-178 fix).
const BRIGADE_MANEUVER_TYPES = [
  'HEAVY', 'INFANTRY', 'MECHANIZED', 'ARMOR', 'AIRBORNE',
  'AIR_ASSAULT', 'MOUNTAIN', 'STRYKER', 'RECONNAISSANCE'
];

// DEFAULT_HQ_POSITIONS - now uses getHQPosition() from nationalStandards.js
// AUTO_ALLOCATION - now uses EVAC_AUTO_ALLOCATION from medicalAssets.js

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getOrdinal = (n) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

/**
 * getMilitaryDisplayName — NATO-style hierarchy label for card headings.
 * Stored unit.name is preserved for calculations; this is display-only.
 *
 * BATTALION  → "1st Bn / 1st Bde / 1st Div"  (div omitted if not parseable)
 * BRIGADE    → "1st Bde / 1st Div"            (div omitted for manual adds)
 * others     → unit.name unchanged
 */
const getMilitaryDisplayName = (unit) => {
  // ── BATTALION ────────────────────────────────────────────────────────────
  if ((unit.type === 'BATTALION') && unit.battalion_number) {
    const bnOrd     = getOrdinal(unit.battalion_number);
    const bdeSource = unit.parent_brigade_name || '';

    // Extract brigade ordinal: "Brigade 1" → "1st Bde"
    const bdeNumMatch = bdeSource.match(/Brigade\s+(\d+)/i);
    const bdeOrd = bdeNumMatch
      ? `${getOrdinal(parseInt(bdeNumMatch[1]))} Bde`
      : bdeSource.replace(/Brigade/i, 'Bde').replace(/Division/i, 'Div') || null;

    // Division ref — try parent_brigade_name first ("1st Division Brigade 1"),
    // then fall back to parent_division_name (set on manually-added brigade battalions).
    const divMatchBde = bdeSource.match(/^(\d+(?:st|nd|rd|th))\s+Division/i);
    let divRef = divMatchBde ? `${divMatchBde[1]} Div` : null;
    if (!divRef && unit.parent_division_name) {
      const divMatchDN = unit.parent_division_name.match(/^(\d+(?:st|nd|rd|th))\s+Division/i);
      divRef = divMatchDN
        ? `${divMatchDN[1]} Div`
        : unit.parent_division_name.replace(/Division/i, 'Div');
    }

    let label = `${bnOrd} Bn`;
    if (bdeOrd) label += ` / ${bdeOrd}`;
    if (divRef) label += ` / ${divRef}`;
    return label;
  }

  // ── BRIGADE ──────────────────────────────────────────────────────────────
  if (unit.type === 'BRIGADE' || unit.type === 'Brigade') {
    const name = unit.name || '';

    // Auto-built pattern: "1st Division Brigade 1"
    const bdeNumMatch = name.match(/Brigade\s+(\d+)/i);
    const divMatch    = name.match(/^(\d+(?:st|nd|rd|th))\s+Division/i);

    if (bdeNumMatch) {
      const bdeOrd = `${getOrdinal(parseInt(bdeNumMatch[1]))} Bde`;
      const divRef = divMatch ? `${divMatch[1]} Div` : null;
      return divRef ? `${bdeOrd} / ${divRef}` : bdeOrd;
    }

    // Manual-add pattern: "4th Brigade" → "4th Bde"
    return name.replace(/Brigade/gi, 'Bde').replace(/Division/gi, 'Div');
  }

  // ── ALL OTHER TYPES (DIVISION, CORPS, …) ─────────────────────────────────
  return unit.name;
};

const calculateUnitPosition = (unitIndex, totalUnits) => {
  return {
    position: getPositionLabel(unitIndex + 1, totalUnits),
    position_auto: getPositionLabel(unitIndex + 1, totalUnits),
    position_user_override: false,
    position_index: unitIndex,
    total_siblings: totalUnits
  };
};

const getMissionMultiplier = (missionValue) => {
  const mission = getMissionByValue(missionValue);
  return mission ? mission.casualty_multiplier : 1.0;
};

const getPostureMultiplier = (postureValue) => {
  const posture = getPostureByValue(postureValue);
  return posture ? posture.casualty_multiplier : 1.0;
};

const isMedicalOperational = (postureValue) => {
  const posture = getPostureByValue(postureValue);
  if (!posture) return true;
  return posture.medical_operational !== false;
};

const getMedicalDelayHours = (postureValue) => {
  const posture = getPostureByValue(postureValue);
  return posture?.medical_delay_hours || 0;
};

const calculateTotalMultiplier = (unit, forceBuilderResult) => {
  if (!unit.tactical_in_parent) return 1.0;
  
  const missionMult = unit.tactical_in_parent.mission_multiplier || 1.0;
  const postureMult = unit.tactical_in_parent.posture_multiplier || 1.0;
  const unitTypeMult = unit.unit_type_modifier || 1.0;
  
  // Add force quality multiplier
  let forceQualityMult = 1.0;
  if (forceBuilderResult) {
    const resolved = resolveForceQuality(unit, forceBuilderResult);
    const fqResult = calculateForceQualityModifier(resolved.resolved);
    forceQualityMult = fqResult.combined_casualty_multiplier;
  }
  
  // Add operational state multiplier
  const operationalStateMult = OPERATIONAL_STATES[unit.operational_state]?.battle_casualty_multiplier || 1.0;
  
  return missionMult * postureMult * unitTypeMult * forceQualityMult * operationalStateMult;
};

const getMultiplierSeverity = (multiplier) => {
  if (multiplier > 2.5) return 'error';
  if (multiplier > 2.0) return 'warning';
  if (multiplier > 1.5) return 'info';
  return 'success';
};

const getDefaultCapacity = (role) => {
  const capacities = {
    'ROLE1': 10,
    'ROLE2_BASIC': 8,
    'ROLE2_ENHANCED': 15,
    'ROLE3': 40
  };
  return capacities[role] || 10;
};

// ============================================================================
// FORCE QUALITY DISPLAY COMPONENT
// ============================================================================

const ForceQualityBadge = ({ unit, forceBuilderResult, compact = false }) => {
  const resolved = resolveForceQuality(unit, forceBuilderResult);
  const fqResult = calculateForceQualityModifier(resolved.resolved);
  
  const getInheritanceColor = () => {
    if (resolved.inherited_from === 'SELF') return 'secondary';
    if (resolved.inherited_from === 'DEFAULT') return 'default';
    return 'primary';
  };
  
  const getInheritanceIcon = () => {
    if (resolved.inherited_from === 'SELF') return <OverrideIcon fontSize="small" />;
    return <InheritedIcon fontSize="small" />;
  };

  if (compact) {
    return (
      <Tooltip title={`Force Quality: ${fqResult.combined_casualty_multiplier}x (${resolved.inherited_from})`}>
        <Chip
          size="small"
          icon={getInheritanceIcon()}
          label={`FQ: ${fqResult.combined_casualty_multiplier}x`}
          color={getInheritanceColor()}
          variant={resolved.inherited_from === 'SELF' ? 'filled' : 'outlined'}
        />
      </Tooltip>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          icon={<TrainingIcon />}
          label={TRAINING_LEVELS[resolved.resolved.training_level]?.label || 'Regular'}
          color={resolved.overrides.training_level ? 'secondary' : 'default'}
          variant={resolved.overrides.training_level ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          icon={<EquipmentIcon />}
          label={EQUIPMENT_LEVELS[resolved.resolved.equipment_level]?.label || 'Adequate'}
          color={resolved.overrides.equipment_level ? 'secondary' : 'default'}
          variant={resolved.overrides.equipment_level ? 'filled' : 'outlined'}
        />
        <Chip
          size="small"
          icon={<MoraleIcon />}
          label={MORALE_LEVELS[resolved.resolved.morale_level]?.label || 'Normal'}
          color={resolved.overrides.morale_level ? 'secondary' : 'default'}
          variant={resolved.overrides.morale_level ? 'filled' : 'outlined'}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Combined FQ modifier: {fqResult.combined_casualty_multiplier}x 
        ({resolved.inherited_from === 'SELF' ? 'Overridden' : `Inherited from ${resolved.inherited_from}`})
      </Typography>
    </Box>
  );
};

// ============================================================================
// FORCE QUALITY PANEL COMPONENT (Corps/Division level)
// ============================================================================

// 2026-10-11 (Dr. Tatar): what the planner sees must be what the engine uses. These displays show numbers the casualty
// engine does not read; they are hidden, not deleted (clean-up stage later). Set a flag to true to show them again.
const SHOW_UNWIRED_FORCE_QUALITY_EFFECTS = false;  // killed and DOW multipliers of the equipment/medical tables, '(DOW: x)' labels
const SHOW_MEDICAL_CAPABILITY = false;              // 2026-10-24 (Dr. Tatar: Tab 1 sets the type of friendly force; medical support is Tab 3). Hidden, not removed: the engine keeps reading force_quality.medical_capability (default GOOD), so no number moves. Medical readiness is to be owned by Tab 3 (see TODO_CURRENT R1-07).
const SHOW_DNBI_DISPLAY = false;                    // DNBI per 1,000 per day chips: three different unsourced numbers, none used by the engine
const SHOW_OLD_METHOD_COMPARISON = false;           // 'Old method: 1.0x -> Error: n%' development leftover

const ForceQualityPanel = ({ forceQuality, onChange, level = 'CORPS', expanded = true }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleChange = (field, value) => {
    onChange({
      ...forceQuality,
      [field]: value
    });
  };

  return (
    <Card sx={{ mb: 2, borderLeft: 4, borderColor: 'secondary.main' }}>
      <CardContent>
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <TrainingIcon color="secondary" />
          <Typography variant="h6">
            {t('tab1.friendly_force_quality')}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small">
            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
          </IconButton>
        </Stack>

        <Collapse in={isExpanded}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab1.force_quality')}</AlertTitle>
            {t('tab1.force_quality_cascade_info')}
          </Alert>

          <Grid container spacing={2}>
            {/* Training Level */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.training_level')}</InputLabel>
                <Select
                  value={forceQuality.training_level || 'REGULAR'}
                  label={t('tab1.training_level')}
                  onChange={(e) => handleChange('training_level', e.target.value)}
                >
                  {Object.entries(TRAINING_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} ({val.casualty_multiplier}x)
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.affects_casualty')}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Equipment Level */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.equipment_level')}</InputLabel>
                <Select
                  value={forceQuality.equipment_level || 'ADEQUATE'}
                  label={t('tab1.equipment_level')}
                  onChange={(e) => handleChange('equipment_level', e.target.value)}
                >
                  {Object.entries(EQUIPMENT_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} ({val.casualty_multiplier}x)
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.affects_equipment')}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Morale Level */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.morale_level')}</InputLabel>
                <Select
                  value={forceQuality.morale_level || 'NORMAL'}
                  label={t('tab1.morale_level')}
                  onChange={(e) => handleChange('morale_level', e.target.value)}
                >
                  {Object.entries(MORALE_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} ({val.casualty_multiplier}x)
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.affects_morale')}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Experience Level */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.experience_level')}</InputLabel>
                <Select
                  value={forceQuality.experience_level || 'OVER_180_DAYS'}
                  label={t('tab1.experience_level')}
                  onChange={(e) => handleChange('experience_level', e.target.value)}
                >
                  {Object.entries(EXPERIENCE_MODIFIERS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} ({val.casualty_multiplier}x)
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.time_in_combat')}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Technology environment (drones, electronic warfare): moved to Tab 2 on 2026-10-25 (Dr. Tatar: it is the environment of the operation, Tab 2 owns it) */}

            {/* Medical Capability (hidden by SHOW_MEDICAL_CAPABILITY: medical readiness belongs to Tab 3) */}
            {SHOW_MEDICAL_CAPABILITY && (
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.medical_capability')}</InputLabel>
                <Select
                  value={forceQuality.medical_capability || 'GOOD'}
                  label={t('tab1.medical_capability')}
                  onChange={(e) => handleChange('medical_capability', e.target.value)}
                >
                  {Object.entries(MEDICAL_CAPABILITY).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label}{SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? ` (DOW: ${val.dow_multiplier}x)` : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.affects_dow_rtd')}</FormHelperText>
              </FormControl>
            </Grid>
            )}

            {/* CLS training: moved to the Tab 3 card 'Care at the point of injury' (R1-07, 2026-10-25; STANAG 2122 requirement, shown in both modes) */}

            {/* Body Armor Standard — ENH-BAP */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tab1.body_armor')}</InputLabel>
                <Select
                  value={forceQuality.body_armor_level || DEFAULT_BODY_ARMOR_LEVEL}
                  label={t('tab1.body_armor')}
                  onChange={(e) => handleChange('body_armor_level', e.target.value)}
                >
                  {Object.entries(BODY_ARMOR_LEVELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.label} — {Math.round(val.kia_reduction * 100)}% KIA reduction
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('tab1.body_armor_note')}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          {/* Combined Effect Preview */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>{t('tab1.combined_effect')}</Typography>
            {(() => {
              const result = calculateForceQualityModifier({ ...forceQuality, technology_env: 'CONVENTIONAL' });   // 2026-10-25: without the technology environment (Tab 2)

              // BUG-130 FIX: Apply body armor KIA reduction to preview.
              // calculateForceQualityModifier does not include body_armor_level —
              // the calculator applies it separately when reading force_quality.
              // The preview must mirror that step so the selected armor level is visible.
              const armorLevel = forceQuality.body_armor_level || DEFAULT_BODY_ARMOR_LEVEL;
              const armorReduction = getArmorKIAReduction(armorLevel);
              const displayKIA = Math.round(result.combined_kia_multiplier * (1 - armorReduction) * 100) / 100;

              // BUG-129: Technology environment contributes to DOW via
              // (tech.casualty_multiplier × 0.3 + 0.7) inside calculateForceQualityModifier.
              // For DRONE_CONTESTED (×1.25) this produces a ×1.08 DOW factor that was
              // previously invisible to the user ("unknown factor contributing +0.04").
              // Show it as a caption so the user understands the full breakdown.
              const tech = TECHNOLOGY_ENVIRONMENT.CONVENTIONAL;   // 2026-10-25: the technology environment is set in Tab 2 and is not part of these figures
              const techDowFactor = Math.round((tech.casualty_multiplier * 0.3 + 0.7) * 100) / 100;
              // the killed share shown is the armor factor; the equipment effect (vehicle-borne units only) depends on the unit mix of each brigade
              // and the technology environment (Tab 2) is applied in the calculation on top
              const killedShareView = Math.round((1 - armorReduction) * 100) / 100;

              return (
                <Grid container spacing={2}>
                  <Grid size={{ xs: SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? 4 : 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('tab1.casualty_rate')}</Typography>
                    <Typography variant="h6" color={result.combined_casualty_multiplier > 1.0 ? 'error.main' : 'success.main'}>
                      {result.combined_casualty_multiplier}x
                    </Typography>
                  </Grid>
                  {/* The engine applies body armor only to the killed share of casualties (equipment and medical killed/DOW multipliers are not read) */}
                  <Grid size={{ xs: SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? 4 : 6 }}>
                    <Typography variant="body2" color="text.secondary">{SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? t('tab1.kia_rate') : t('tab1.killed_share')}</Typography>
                    <Typography variant="h6" color={(SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? displayKIA : killedShareView) > 1.0 ? 'error.main' : 'success.main'}>
                      {SHOW_UNWIRED_FORCE_QUALITY_EFFECTS ? displayKIA : killedShareView}x
                    </Typography>
                    {armorReduction > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('tab1.armor_applied')} −{Math.round(armorReduction * 100)}%
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tab1.killed_share_equipment_note')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tab1.technology_in_tab2')}
                    </Typography>
                  </Grid>
                  {SHOW_UNWIRED_FORCE_QUALITY_EFFECTS && (
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="body2" color="text.secondary">{t('tab1.dow_rate')}</Typography>
                    <Typography variant="h6" color={result.combined_dow_multiplier > 1.0 ? 'error.main' : 'success.main'}>
                      {result.combined_dow_multiplier}x
                    </Typography>
                    {techDowFactor !== 1.0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        incl. {tech.label} ×{techDowFactor}
                      </Typography>
                    )}
                  </Grid>
                  )}
                </Grid>
              );
            })()}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// FORCE QUALITY OVERRIDE SECTION (for UnitEditDialog)
// ============================================================================

const ForceQualityOverrideSection = ({ 
  unit, 
  forceBuilderResult, 
  override, 
  onOverrideChange,
  onOverrideToggle,
  useOverride 
}) => {
  const { t } = useTranslation();
  const resolved = resolveForceQuality(unit, forceBuilderResult);

  const handleFieldOverride = (field, value) => {
    onOverrideChange({
      ...override,
      [field]: value
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ my: 2 }} />
      
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TrainingIcon color="secondary" />
        <Typography variant="subtitle1">{t('tab1.force_quality_override')}</Typography>
        <Chip
          size="small"
          label={useOverride ? t('tab1.custom') : t('tab1.inherited')}
          color={useOverride ? 'secondary' : 'default'}
          variant={useOverride ? 'filled' : 'outlined'}
        />
      </Stack>

      {/* Current inherited values */}
      <Alert severity="info" sx={{ mb: 2 }} icon={<InheritedIcon />}>
        <AlertTitle>{t('tab1.inherited_from')} {resolved.inherited_from}</AlertTitle>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`Training: ${getForceQualityLabel('training_level', resolved.resolved.training_level)}`} />
          <Chip size="small" label={`Equipment: ${getForceQualityLabel('equipment_level', resolved.resolved.equipment_level)}`} />
          <Chip size="small" label={`Morale: ${getForceQualityLabel('morale_level', resolved.resolved.morale_level)}`} />
        </Stack>
      </Alert>

      {/* Override toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={useOverride}
            onChange={(e) => onOverrideToggle(e.target.checked)}
            color="secondary"
          />
        }
        label={t('tab1.override_inherited')}
      />

      {/* Override fields */}
      <Collapse in={useOverride}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Training Level Override */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab1.training_level')}</InputLabel>
              <Select
                value={override?.training_level || ''}
                label={t('tab1.training_level')}
                onChange={(e) => handleFieldOverride('training_level', e.target.value)}
              >
                <MenuItem value="">
                  <em>Use inherited ({getForceQualityLabel('training_level', resolved.resolved.training_level)})</em>
                </MenuItem>
                {Object.entries(TRAINING_LEVELS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label} ({val.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Equipment Level Override */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab1.equipment_level')}</InputLabel>
              <Select
                value={override?.equipment_level || ''}
                label={t('tab1.equipment_level')}
                onChange={(e) => handleFieldOverride('equipment_level', e.target.value)}
              >
                <MenuItem value="">
                  <em>Use inherited ({getForceQualityLabel('equipment_level', resolved.resolved.equipment_level)})</em>
                </MenuItem>
                {Object.entries(EQUIPMENT_LEVELS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label} ({val.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Morale Level Override */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab1.morale_level')}</InputLabel>
              <Select
                value={override?.morale_level || ''}
                label={t('tab1.morale_level')}
                onChange={(e) => handleFieldOverride('morale_level', e.target.value)}
              >
                <MenuItem value="">
                  <em>Use inherited ({getForceQualityLabel('morale_level', resolved.resolved.morale_level)})</em>
                </MenuItem>
                {Object.entries(MORALE_LEVELS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label} ({val.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Experience Level Override */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab1.experience_level')}</InputLabel>
              <Select
                value={override?.experience_level || ''}
                label={t('tab1.experience_level')}
                onChange={(e) => handleFieldOverride('experience_level', e.target.value)}
              >
                <MenuItem value="">
                  <em>Use inherited ({getForceQualityLabel('experience_level', resolved.resolved.experience_level)})</em>
                </MenuItem>
                {Object.entries(EXPERIENCE_MODIFIERS).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label} ({val.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Effect preview */}
        {useOverride && Object.keys(override || {}).length > 0 && (
          <Alert severity="secondary" sx={{ mt: 2 }}>
            <AlertTitle>{t('tab1.override_effect')}</AlertTitle>
            {(() => {
              const effectiveQuality = { ...resolved.resolved, ...override };
              const result = calculateForceQualityModifier(effectiveQuality);
              return (
                <Typography variant="body2">
                  With overrides: Casualty {result.combined_casualty_multiplier}x, 
                  KIA {result.combined_kia_multiplier}x, 
                  DOW {result.combined_dow_multiplier}x
                </Typography>
              );
            })()}
          </Alert>
        )}
      </Collapse>
    </Box>
  );
};

// ============================================================================
// TASK 1.7: BATTALION TYPE UI COMPONENTS
// ============================================================================

/**
 * BattalionTypeSelector - Battalion-specific unit type dropdown with grouped options
 */
const BattalionTypeSelector = ({ value, onChange, disabled = false, nationalStandard = null }) => {
  const { t } = useTranslation();
  const currentConfig = getBattalionTypeConfig(value);
  
  // BUG-182 FIX: Updated to match BATTALION_TYPES keys in unitTypes.js v2.12 exactly.
  // Old COMBAT_TYPES had stale 'CAVALRY' key (renamed RECONNAISSANCE in v2.11) →
  // getBattalionTypeConfig('CAVALRY') fell back to MECHANIZED → duplicate entry.
  // Added AIRBORNE, AIR_ASSAULT, MOUNTAIN (new in v2.12).
  // Added AVIATION to CS group; TRANSPORT, SUPPLY, MEDICAL_SUPPORT to CSS group.
  const COMBAT_TYPES = ['INFANTRY', 'MECHANIZED', 'ARMOR', 'RECONNAISSANCE', 'AIRBORNE', 'AIR_ASSAULT', 'MOUNTAIN', 'SPECIAL_FORCES'];
  const COMBAT_SUPPORT_TYPES = ['ARTILLERY', 'AIR_DEFENSE', 'ENGINEERS', 'AVIATION', 'ATTACK_AVIATION', 'MILITARY_POLICE', 'CBRN_DEFENSE'];
  const CSS_TYPES = ['LOGISTICS', 'SIGNAL', 'MEDICAL', 'MAINTENANCE', 'TRANSPORT', 'SUPPLY', 'MEDICAL_SUPPORT', 'HQ', 'CIVIL_AFFAIRS', 'PSYCHOLOGICAL_OPS'];   // 2026-10-24: HQ and the six defined functions added (every BATTALION_TYPES key is in a group: test)
  
  const renderMenuItem = (type) => {
    const config = getBattalionTypeConfig(type);
    if (!config) return null;
    return (
      <MenuItem key={type} value={type}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{config.icon}</span>
            <span>{unitTypeLabel(type, nationalStandard, config.label)}</span>   {/* 2026-10-24 naming audit step 5: the national term when one is listed */}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Chip 
              size="small" 
              label={`${config.casualty_modifier}x`} 
              color={config.casualty_modifier > 1 ? 'error' : config.casualty_modifier < 0.5 ? 'success' : 'warning'}
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            <Chip
              size="small"
              label={config.has_frontage ? (isForwardAreaSupport(config) ? '[C] Forward area' : '[C] Frontage') : '[S] Support'}
              color={config.has_frontage ? 'primary' : 'secondary'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          </Stack>
        </Box>
      </MenuItem>
    );
  };
  
  return (
    <FormControl fullWidth>
      <InputLabel>{t('tab1.battalion_type')}</InputLabel>
      <Select
        value={value}
        label={t('tab1.battalion_type')}
        onChange={onChange}
        disabled={disabled}
      >
        {/* Combat Battalion Types */}
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', bgcolor: 'grey.100' }}>
          [Combat] Combat Battalions (Hold Frontage, Have Role 1)
        </MenuItem>
        {COMBAT_TYPES.map(renderMenuItem)}
        
        {/* Combat Support Battalion Types */}
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', bgcolor: 'grey.100' }}>
          [CS] Combat Support Battalions
        </MenuItem>
        {COMBAT_SUPPORT_TYPES.map(renderMenuItem)}
        
        {/* Combat Service Support Battalion Types */}
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', bgcolor: 'grey.100' }}>
          [S] Combat Service Support Battalions
        </MenuItem>
        {CSS_TYPES.map(renderMenuItem)}
      </Select>
      <FormHelperText>
        [>>] Auto-applies: Position ({currentConfig.typical_position_km}km), 
        State ({currentConfig.operational_state?.replace(/_/g, ' ')}), 
        {currentConfig.needs_role1 ? ' Role 1 [OK]' : ' No Role 1'}
      </FormHelperText>
    </FormControl>
  );
};

/**
 * BattalionTypeInfoAlert - Shows key info about selected battalion type
 */
const BattalionTypeInfoAlert = ({ battalionType, nationalStandard = null }) => {
  const config = getBattalionTypeConfig(battalionType);
  // DNBI base rate from OPERATIONAL_STATES constants
  const dnbiRate = OPERATIONAL_STATES[config.operational_state]?.dnbi?.base_rate || 1.0;
  
  return (
    <Alert 
      severity={config.has_frontage ? 'warning' : 'info'} 
      icon={<BattalionIcon />}
      sx={{ py: 0.5 }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
          {config.icon} {unitTypeLabel(battalionType, nationalStandard, config.label)}:
        </Typography>
        <Chip size="small" label={`${config.typical_position_km} km from FEBA`} />
        <Chip 
          size="small" 
          label={config.operational_state?.replace(/_/g, ' ')} 
          color={config.operational_state === 'COMBAT_ENGAGED' ? 'error' : 'default'}
        />
        <Chip 
          size="small" 
          label={config.has_frontage ? (isForwardAreaSupport(config) ? '[C] Combat support (forward area)' : '[C] Combat (Frontage)') : '[S] Support (No Frontage)'}
          color={config.has_frontage ? 'primary' : 'secondary'}
          variant="outlined"
        />
        <Chip 
          size="small" 
          label={config.needs_role1 ? '[R1] Has Role 1' : '[X] No Role 1'}
          color={config.needs_role1 ? 'success' : 'default'}
          variant="outlined"
        />
        {SHOW_DNBI_DISPLAY && (
        <Chip 
          size="small" 
          label={`DNBI: ${dnbiRate}/1000/day`}
          variant="outlined"
        />
        )}
      </Stack>
    </Alert>
  );
};

/**
 * BattalionTypeReferenceTable - Educational panel showing all battalion types (collapsible)
 */
const BattalionTypeReferenceTable = ({ currentType }) => {
  const { t } = useTranslation();
  // BUG-182 FIX: Updated to include all 18 BATTALION_TYPES keys (unitTypes.js v2.12)
  const ALL_TYPES = [
    // Combat
    'INFANTRY', 'MECHANIZED', 'ARMOR', 'RECONNAISSANCE', 'AIRBORNE', 'AIR_ASSAULT', 'MOUNTAIN',
    // Combat Support
    'ARTILLERY', 'AIR_DEFENSE', 'ENGINEERS', 'AVIATION',
    // Combat Service Support
    'LOGISTICS', 'SIGNAL', 'MEDICAL', 'MAINTENANCE', 'TRANSPORT', 'SUPPLY', 'MEDICAL_SUPPORT'
  ];
  
  return (
    <Accordion sx={{ bgcolor: 'grey.50' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoIcon color="action" fontSize="small" />
          <Typography variant="body2" fontWeight="bold">
            {t('tab1.bn_type_ref')}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Each battalion type has specific casualty modifiers and operational characteristics.
          Combat battalions hold frontage and have Role 1 facilities; support battalions don't.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Battalion Type</strong></TableCell>
                <TableCell align="center"><strong>Position</strong></TableCell>
                <TableCell align="center"><strong>Op State</strong></TableCell>
                <TableCell align="center"><strong>Frontage</strong></TableCell>
                <TableCell align="center"><strong>Role 1</strong></TableCell>
                <TableCell align="center"><strong>Casualty</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ALL_TYPES.map(key => {
                const bt = getBattalionTypeConfig(key);
                return (
                  <TableRow 
                    key={key} 
                    selected={currentType === key}
                    sx={{ 
                      bgcolor: currentType === key ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{bt.icon}</span>
                        <Typography variant="body2">{bt.label}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{bt.typical_position_km} km</TableCell>
                    <TableCell align="center">
                      <Chip 
                        size="small" 
                        label={bt.operational_state?.replace(/_/g, ' ') || 'SUPPORT'}
                        color={bt.operational_state === 'COMBAT_ENGAGED' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {bt.has_frontage ? '[C] Yes' : '[S] No'}
                    </TableCell>
                    <TableCell align="center">
                      {bt.has_frontage ? '[OK] Yes' : '[X] No'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        size="small"
                        label={`${bt.casualty_modifier}x`}
                        color={bt.casualty_modifier > 0.5 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * BattalionCompositionSummary - Shows brigade's battalion composition with weighted modifier
 */
const BattalionCompositionSummary = ({ battalions, brigadeStrength }) => {
  const { t } = useTranslation();
  if (!battalions || battalions.length === 0) return null;
  
  const summary = calculateBattalionCompositionSummary(battalions);
  // 2026-10-25 (Dr. Tatar: the total of the brigade must be derived from the personnel it holds): a brigade type without a card list (logistics,
  // reconnaissance, air defense) holds more people than its visible cards; the difference is shown, not hidden.
  const cardsTotal = battalions.reduce((sum, b) => sum + (b.strength || 0), 0);
  const outsideCards = typeof brigadeStrength === 'number' ? Math.round(brigadeStrength - cardsTotal) : 0;
  const oldMethodError = ((1.0 - summary.weightedModifier) / summary.weightedModifier * -100);
  
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <AlertTitle>{t('tab1.bn_composition_title')}</AlertTitle>
      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
        <Chip label={`${summary.total} Battalions`} />
        <Chip label={`${summary.combat} ${t('tab1.combat_frontage')}`} color="error" variant="outlined" />
        <Chip label={`${summary.support} ${t('tab1.support')}`} color="info" variant="outlined" />
        {outsideCards > 0 && (
          <Chip label={t('tab1.bn_outside_cards', { n: outsideCards.toLocaleString() })} color="warning" variant="outlined" />
        )}
        <Chip 
          label={`${t('tab1.weighted_modifier')} ${summary.weightedModifier.toFixed(3)}x`}
          color={summary.weightedModifier < 0.8 ? 'success' : 'warning'}
        />
        {SHOW_OLD_METHOD_COMPARISON && (
        <Typography variant="caption" color="text.secondary">
          (Old method: 1.0x -> Error: {Math.abs(oldMethodError).toFixed(0)}%)
        </Typography>
        )}
      </Stack>
    </Alert>
  );
};

// ============================================================================
// UNIT EDIT DIALOG COMPONENT (Enhanced with Force Quality)
// ============================================================================

const UnitEditDialog = ({ open, onClose, unit, mode, onSave, forceBuilderResult }) => {
  const { t } = useTranslation();
  const [editedUnit, setEditedUnit] = useState(null);
  const [useForceQualityOverride, setUseForceQualityOverride] = useState(false);
  const [forceQualityOverride, setForceQualityOverride] = useState({});
  // v3.0 VALIDATION: Track Unit Type validation error
  const [unitTypeError, setUnitTypeError] = useState(null);
  const [unitTypeInfo, setUnitTypeInfo] = useState(null); // v4.3.3: Info message for auto-actions (e.g. MEDICAL auto-delete battalions)
  // v4.3.7: HQ/Support breakdown state
  const [hqPersonnel, setHQPersonnel] = useState(0);
  const [supportPersonnel, setSupportPersonnel] = useState(0);
  
  useEffect(() => {
    if (unit && open) {
      // Safe deep clone
      const safeDeepClone = (obj, seen = new WeakMap()) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (seen.has(obj)) return { __circular: true };
        
        if (Array.isArray(obj)) {
          const arrCopy = [];
          seen.set(obj, arrCopy);
          obj.forEach(item => arrCopy.push(safeDeepClone(item, seen)));
          return arrCopy;
        }
        
        const cloned = {};
        seen.set(obj, cloned);
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            cloned[key] = safeDeepClone(obj[key], seen);
          }
        }
        return cloned;
      };
      
      const plainUnit = safeDeepClone(unit);
      if (!plainUnit.unit_type) plainUnit.unit_type = 'MECHANIZED';
      if (!plainUnit.unit_type_modifier) plainUnit.unit_type_modifier = 1.0;
      
      setEditedUnit(plainUnit);
      setUnitTypeError(null);   // v4.3.3: Reset validation state on each open
      setUnitTypeInfo(null);    // v4.3.3: Reset info state on each open
      
      // v4.3.7: Initialize HQ/Support breakdown
      // Check if unit has manual breakdown, otherwise use defaults from nationalStandards
      // BUG-067 FIX: If unit is a brigade saved with breakdown.support===0 (caused by the
      // type case bug below), treat it as if no breakdown exists so the default re-derives
      // the correct support count from the national standard. Repairs all saved scenarios.
      const isBrigadeWithZeroSupport =
        plainUnit.type?.toUpperCase() === 'BRIGADE' &&
        plainUnit.breakdown &&
        (plainUnit.breakdown.support === 0 || plainUnit.breakdown.support == null);

      if (plainUnit.breakdown && !isBrigadeWithZeroSupport) {
        setHQPersonnel(plainUnit.breakdown.command || 0);
        setSupportPersonnel(plainUnit.breakdown.support || 0);
      } else {
        // Load defaults from nationalStandards based on unit type
        const standard = NATIONAL_STANDARDS[plainUnit.nationalStandard];
        if (standard && plainUnit.type) {
          const echelonKey = plainUnit.type.toLowerCase();
          
          // BUG FIX (Feb 16, 2026): Different logic for BRIGADE vs CORPS/DIVISION
          // BRIGADE: Fixed HQ count + Support as % of total strength
          // CORPS/DIVISION: Apply breakdown_ratios to HQ personnel count
          // BUG-067 FIX: use toUpperCase() — handleAddBrigade sets type='Brigade' not 'BRIGADE'
          if (plainUnit.type?.toUpperCase() === 'BRIGADE') {
            // For brigades: HQ is fixed count, Support is ~20% of total strength
            const hqCount = standard.brigade?.hq_personnel || 200;
            const supportPercent = standard.brigade?.support_ratio || 0.20; // 20% default
            const supportCount = Math.round(plainUnit.strength * supportPercent);
            
            setHQPersonnel(hqCount);
            setSupportPersonnel(supportCount);
          } else {
            // For Corps/Division HQ echelons: Apply ratios to HQ strength
            const hqTotal = standard[echelonKey]?.hq_personnel || plainUnit.strength || 0;
            const ratios = standard[echelonKey]?.breakdown_ratios;
            
            if (ratios) {
              setHQPersonnel(Math.round(hqTotal * ratios.command));
              setSupportPersonnel(Math.round(hqTotal * ratios.support));
            } else {
              // Fallback if no ratios defined
              setHQPersonnel(0);
              setSupportPersonnel(0);
            }
          }
        }
      }
      
      // v4.3.4 FIX: Recalculate position_feba_km if stored value is invalid (≤0)
      // Caused by old data built before ForceService v3.0.5 fix (negative sign error)
      // Only applies to BRIGADE level — battalions use typical_position_km from unitTypes
      if (plainUnit.type?.toUpperCase() === 'BRIGADE' && 
          plainUnit.unit_type && 
          (plainUnit.position_feba_km === undefined || plainUnit.position_feba_km <= 0)) {
        try {
          // v4.6.2 DEBUG: Log when position recalculation triggers
          const correctedPosition = getBrigadeHQPosition(plainUnit.nationalStandard, plainUnit.unit_type);
          setEditedUnit({ ...plainUnit, position_feba_km: correctedPosition });
        } catch (e) {
          // Unknown type — leave as-is, user can correct manually
        }
      }
      
      // Initialize force quality override state
      if (plainUnit.force_quality_override && Object.keys(plainUnit.force_quality_override).length > 0) {
        setUseForceQualityOverride(true);
        setForceQualityOverride(plainUnit.force_quality_override);
      } else {
        setUseForceQualityOverride(false);
        setForceQualityOverride({});
      }
    }
  }, [unit, open]);
  
  // Validate Operational State vs Operational Posture compatibility
  // Must be before early returns (React Hooks rules)
  const statePostureValidation = useMemo(() => {
    if (!editedUnit) return { isValid: true, severity: null, message: null, explanation: null, suggestion: null };
    
    const state = editedUnit.operational_state;
    const posture = editedUnit.tactical_in_parent?.posture;
    
    // NO FALLBACKS - If data missing, it's a bug that must be exposed
    if (!state || !posture) {
      return {
        isValid: false,
        severity: 'error',
        message: 'CRITICAL: Unit missing required operational data',
        explanation: `This unit was not properly initialized by ForceBuilder. Missing: ${!state ? 'operational_state' : ''} ${!posture ? 'posture' : ''}`.trim(),
        suggestion: 'Report this as a bug - ForceBuilder should always set operational_state and posture during unit creation'
      };
    }
    
    // NEW: Validate unit type constraints (CSS units can't be in combat postures, etc.)
    // Get operational mode from localStorage (Tab2 config)
    const operationsConfig = JSON.parse(localStorage.getItem('operationsConfig') || '{}');
    const operationalMode = operationsConfig.operationalMode || 'STANDARD';
    
    const unitTypeValidation = validateUnitOperationalSettings(
      editedUnit.unit_type,
      posture,
      state,
      operationalMode
    );
    
    if (unitTypeValidation) {
      return unitTypeValidation; // Returns error in STANDARD, warning in EXPERT
    }
    
    // Define posture categories (from postures.js)
    const activeCombatPostures = [
      // Offensive Operations
      'OFFENSIVE',
      'DELIBERATE_ATTACK',
      'HASTY_ATTACK',
      'EXPLOITATION',
      'PURSUIT',
      'MOVEMENT_TO_CONTACT',
      // Defensive Operations
      'DELIBERATE_DEFENSE',
      'HASTY_DEFENSE',
      'AREA_DEFENSE',
      'MOBILE_DEFENSE',
      // Retrograde Operations
      'DELAY',
      'WITHDRAWAL',
      'RETIREMENT',
      // Special Operations
      'RIVER_CROSSING',
      'AIRBORNE_OPERATION'
    ];
    const reservePostures = [
      'RESERVE',              // Actual value (not IN_RESERVE)
      'RECONSTITUTION',
      'SUPPORT_OPERATIONS',
      'NON_COMBAT'
    ];
    
    // TRUE_RESERVE should NOT be actively engaged in combat
    if (state === 'TRUE_RESERVE' && activeCombatPostures.includes(posture)) {
      return {
        isValid: false,
        severity: 'error',
        message: `Logical conflict: TRUE RESERVE units cannot be in ${posture.replace(/_/g, ' ')}`,
        explanation: 'True Reserve means units are in rest, regeneration, or strategic reserve - not actively engaged in combat operations.',
        suggestion: 'Change Operational Posture to "In Reserve" or "Support Operations", or change Operational State to "Combat Engaged"'
      };
    }
    
    // COMBAT_ENGAGED should NOT be in reserve posture (unless preparing to engage)
    if (state === 'COMBAT_ENGAGED' && reservePostures.includes(posture)) {
      return {
        isValid: true,
        severity: 'warning',
        message: `Unusual combination: COMBAT ENGAGED units in ${posture.replace(/_/g, ' ')}`,
        explanation: 'Combat Engaged typically means units are actively fighting. Units in reserve or support operations are usually "Support Active" or "True Reserve".',
        suggestion: 'Change Operational State to "Support Active" if unit is in reserve preparing to engage, or to "True Reserve" if resting/regenerating'
      };
    }
    
    // SUPPORT_ACTIVE in active combat posture is valid but worth noting
    if (state === 'SUPPORT_ACTIVE' && activeCombatPostures.includes(posture)) {
      return {
        isValid: true,
        severity: 'info',
        message: 'Support Active units in combat posture',
        explanation: 'Support Active in defensive/offensive posture is valid for units providing direct combat support that are not on the frontage (artillery, air defense, CSS). Lower casualty multiplier reflects reduced exposure.',
        suggestion: null
      };
    }
    
    return { isValid: true, severity: null, message: null, explanation: null, suggestion: null };
  }, [editedUnit]);
  
  if (!editedUnit) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('common.loading')}</DialogTitle>
        <DialogContent>
          <Alert severity="info">{t('tab1.loading_unit')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!editedUnit.tactical_in_parent && !isBattalion(editedUnit)) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('tab1.cannot_edit_unit')}</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            <AlertTitle>{t('tab1.unit_not_initialized')}</AlertTitle>
            {t('tab1.missing_tactical')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  // TASK 1.7: Check if editing a battalion
  const isEditingBattalion = isBattalion(editedUnit);
  
  // v3.0.1: Check if editing CORPS or DIVISION (these don't need Unit Type)
  const isCorpsDivisionLevel = editedUnit.type === 'CORPS' || editedUnit.type === 'DIVISION';

  // BUG-092 FIX: Detect brigades under a MULTINATIONAL division (MND).
  // forceBuilderResult.nationalStandard is the CORPS-level standard — it is 'ROMANIAN'/'NATO'/
  // etc. for non-multinational corps. But an individual division within that corps can still
  // be MND (nationalStandard === 'MULTINATIONAL'). Brigades under such a division need the
  // national standard selector even though the corps-level standard is not MULTINATIONAL.
  const parentDivIsMultinational =
    editedUnit.parent_division_id != null &&
    forceBuilderResult?.divisions?.find(d => d.id === editedUnit.parent_division_id)
      ?.nationalStandard === 'MULTINATIONAL';

  // TASK 1.7: Battalion Type Change Handler
  const handleBattalionTypeChange = (e) => {
    const newType = e.target.value;
    const config = getBattalionTypeConfig(newType);

    // BUG-178 FIX: Strength must update to the type-correct value when battalion type changes.
    // Old logic: _natBnStrength > 0 ? _natBnStrength : typical_strength
    // — national standard won for ALL types including ADA (400), LOGISTICS (300), SIGNAL (400).
    // Fix: gate national standard on MANEUVER_TYPES list, matching ForceService.buildBattalion
    // v3.3.3 exactly. CS/CSS types use their doctrinal typical_strength instead.
    const newStrength = BATTALION_MANEUVER_TYPES.includes(newType)
      ? (getDefaultStrength(editedUnit.nationalStandard, 'BATTALION') || config.typical_strength || editedUnit.strength)
      : (config.typical_strength || getDefaultStrength(editedUnit.nationalStandard, 'BATTALION') || editedUnit.strength);

    // Derive correct posture for new type — CSS units must not be in combat postures.
    const defaultPosture = config.has_frontage ? 'HASTY_DEFENSE' : 'SUPPORT_OPERATIONS';
    const defaultPostureMultiplier = config.has_frontage ? 1.0 : 0.3;
    const defaultTacticalRole = config.has_frontage ? 'MAIN_DEFENSE' : 'SUPPORTING';
    const defaultMissionMultiplier = config.has_frontage ? 1.0 : 0.5;

    // BUG-198 FIX (March 25, 2026): Regenerate battalion display name when type changes.
    // Previous: name stayed as old type label (e.g. "1/Brigade Mechanized" after changing to ARTILLERY).
    // Pattern: "${battalion_number}/${parent_brigade_name} ${typeLabel}" per ForceService._generateBattalionName.
    // HQ type uses "${parent_brigade_name} HQ" — preserved separately.
    // If battalion_number or parent_brigade_name is missing, keep existing name as fallback.
    const _bnTypeLabels = {
      INFANTRY: 'Infantry', MECHANIZED: 'Mechanized', ARMOR: 'Armor', RECONNAISSANCE: 'Reconnaissance',
      SPECIAL_FORCES: 'Special Forces', ATTACK_AVIATION: 'Attack Aviation', MILITARY_POLICE: 'Military Police',
      CBRN_DEFENSE: 'CBRN Defence', CIVIL_AFFAIRS: 'Civil Affairs', PSYCHOLOGICAL_OPS: 'Psychological Operations',
      ARTILLERY: 'Artillery', AIR_DEFENSE: 'Air Defense',
      ENGINEERS: 'Engineer',
      SUPPLY: 'Supply', TRANSPORT: 'Transport', MAINTENANCE: 'Maintenance',
      LOGISTICS: 'Logistics', SIGNAL: 'Signal', MEDICAL: 'Medical', HQ: 'HQ'
    };
    const _newTypeLabel = _bnTypeLabels[newType] || newType;
    const _bnNum = editedUnit.battalion_number;
    const _bdeNm = editedUnit.parent_brigade_name;
    const _newName = (_bnNum && _bdeNm)
      ? (newType === 'HQ' ? `${_bdeNm} HQ` : `${_bnNum}/${_bdeNm} ${_newTypeLabel}`)
      : editedUnit.name;  // fallback: keep existing name

    setEditedUnit({
      ...editedUnit,
      unit_type: newType,
      unit_type_modifier: config.casualty_modifier,
      operational_state: config.operational_state,
      has_frontage: config.has_frontage,
      position_feba_km: config.typical_position_km,
      is_frontline: config.has_frontage,
      strength: newStrength,  // BUG-173 FIX: type-correct strength
      name: _newName,
      tactical_in_parent: editedUnit.tactical_in_parent ? {
        // BUG-173 FIX: reset posture when type changes — stale combat posture on CSS
        // type produced "Invalid posture" warning and wrong casualty multiplier.
        ...editedUnit.tactical_in_parent,
        posture:           defaultPosture,
        posture_multiplier: defaultPostureMultiplier,
        tactical_role:     defaultTacticalRole,
        mission_multiplier: defaultMissionMultiplier
      } : {
        posture:           defaultPosture,
        posture_multiplier: defaultPostureMultiplier,
        tactical_role:     defaultTacticalRole,
        mission_multiplier: defaultMissionMultiplier,
        medical_operational: true,
        medical_delay_hours: 0,
        mtf_deployment_hours: 2
      }
    });
  };
  
  // Handlers
  /**
   * ENHANCED: Handle unit type change with auto-apply defaults
   * Task 1.2 Implementation
   * v3.2 FIXED: Now updates strength and uses SUPPORT_OPERATIONS for CSS units
   * 
   * When unit type changes, automatically apply:
   * - Position from FEBA (typical_position_km)
   * - Default Posture (SUPPORT_OPERATIONS for CSS units)
   * - Default Mission
   * - Operational State
   * - Casualty Modifier
   * - Frontage status
   * - NEW v3.2: Strength (typical_strength)
   */
  const handleUnitTypeChange = (e) => {
    const newType = e.target.value;
    
    // v4.3.3: Clear any prior error/info first, then re-evaluate for new type
    setUnitTypeError(null);
    setUnitTypeInfo(null);

    // v4.3.3: MEDICAL brigade — auto-delete battalions on save, show info message
    if (isMedicalBrigade(newType) && editedUnit.type?.toUpperCase() === 'BRIGADE' && !BRIGADE_CARDS_ON) {   // 2026-10-25: the medical brigade has cards now
      const childBattalions = forceBuilderResult.battalions?.filter(
        bn => bn.parent_brigade_id === editedUnit.id
      ) || [];
      if (childBattalions.length > 0) {
        setUnitTypeInfo(
          `${childBattalions.length} battalion(s) will be removed automatically — Medical Brigades do not have organic combat battalions.`
        );
      }
      // Allow the change to proceed — deletion happens on Save
    }
    
    // Get complete defaults for the new unit type
    const defaults = getUnitTypeDefaults(newType);
    
    // NEW: Translate posture if new type doesn't support current posture
    const currentPosture = editedUnit.tactical_in_parent?.posture || defaults.posture;
    const translatedPosture = translatePostureForUnitType(newType, currentPosture);
    
    // NEW: Get correct operational state for unit type
    const correctState = UNIT_TYPES_FROM_CONSTANTS[newType]?.operational_state || defaults.operational_state;
    
    // Calculate new evacuation assets
    // T1-BUG-001 FIX: Convert editedUnit.type to uppercase for echelon parameter
    const unitEchelon = editedUnit.type?.toUpperCase() || 'BRIGADE';
    const newAssets = calculateEvacuationAssets(newType, unitEchelon, BASE_EVACUATION_ALLOCATIONS);
    
    // Get posture-related values for the TRANSLATED posture
    const postureMultiplier = getPostureMultiplier(translatedPosture);
    const medicalOperational = isMedicalOperational(translatedPosture);
    const medicalDelayHours = getMedicalDelayHours(translatedPosture);
    
    // Get mission multiplier for the default mission
    const missionMultiplier = getMissionMultiplier(defaults.mission);
    
    // BUG-126 FIX (March 17, 2026): Brigade type changes now apply national standard strength.
    // Previously used defaults.typical_strength (unitTypes.js composition-based, not nation-based),
    // so changing to HEAVY for a Romanian brigade gave 5,000 (NATO HEAVY) not 4,000 (Romanian).
    // Fix: for BRIGADE echelon, prefer national standard brigade strength; for other echelons
    // (or if no national standard defined), fall back to type-typical strength.
    let newStrength = editedUnit.strength; // Keep current as fallback
    
    if (unitEchelon === 'BRIGADE' && BRIGADE_CARDS_ON && BRIGADE_CARD_TEMPLATES[newType]) {
      // 2026-10-25 (Dr. Tatar): a brigade type that has cards holds the sum of its cards, calculated, not the national standard brigade strength
      // (5,000 for NATO while the cards of an infantry brigade add up to 4,468). Saving rebuilds the units from the same cards.
      newStrength = BRIGADE_CARD_TEMPLATES[newType].reduce((sum, card) => sum + card.strength, 0);
    } else if (unitEchelon === 'BRIGADE') {
      // BUG-190 FIX: National standard strength is authoritative for MANEUVER brigade types only.
      // CS/CSS types (LOGISTICS, ENGINEER, MEDICAL, SIGNAL, etc.) have doctrinal strengths
      // that differ significantly from the maneuver brigade standard (e.g. LOGISTICS ~2000 vs
      // Romanian maneuver 4000). Gate the national standard win to BRIGADE_MANEUVER_TYPES.
      if (BRIGADE_MANEUVER_TYPES.includes(newType)) {
        const _natBrigadeStrength = editedUnit.nationalStandard && editedUnit.nationalStandard !== 'MULTINATIONAL'
          ? getDefaultStrength(editedUnit.nationalStandard, 'BRIGADE')
          : 0;
        if (_natBrigadeStrength > 0) {
          newStrength = _natBrigadeStrength;
        } else if (defaults.typical_strength) {
          newStrength = defaults.typical_strength;
        } else if (UNIT_TYPES_FROM_CONSTANTS[newType]?.typical_strength) {
          newStrength = UNIT_TYPES_FROM_CONSTANTS[newType].typical_strength;
        } else if (BATTALION_TYPES[newType]?.typical_strength) {
          newStrength = BATTALION_TYPES[newType].typical_strength;
        }
      } else {
        // BUG-S102-001 FIX: CS/CSS brigade types — preserve current strength if already set.
        // Changing from a maneuver type (e.g. MECHANIZED 4,000 Romanian) to a CSS type
        // (e.g. MEDICAL typical 2,500) was silently overriding the configured strength,
        // causing a 37% apparent "drop" in the brigade (and total force) display.
        // Only apply doctrinal typical_strength as a seed value when no strength is set yet.
        if (!editedUnit.strength) {
          if (UNIT_TYPES_FROM_CONSTANTS[newType]?.typical_strength) {
            newStrength = UNIT_TYPES_FROM_CONSTANTS[newType].typical_strength;
          } else if (defaults.typical_strength) {
            newStrength = defaults.typical_strength;
          } else if (BATTALION_TYPES[newType]?.typical_strength) {
            newStrength = BATTALION_TYPES[newType].typical_strength;
          }
        }
        // else: newStrength retains editedUnit.strength — user's configured value preserved
      }
    } else if (defaults.typical_strength) {
      newStrength = defaults.typical_strength;
    } else if (UNIT_TYPES_FROM_CONSTANTS[newType]?.typical_strength) {
      newStrength = UNIT_TYPES_FROM_CONSTANTS[newType].typical_strength;
    } else if (BATTALION_TYPES[newType]?.typical_strength) {
      newStrength = BATTALION_TYPES[newType].typical_strength;
    }
    
    // Apply all defaults
    setEditedUnit({
      ...editedUnit,
      
      // Unit type identification
      unit_type: newType,
      unit_type_modifier: defaults.casualty_modifier,
      
      // v3.2: Update strength
      strength: newStrength,
      
      // NEW: Use correct operational state for unit type
      operational_state: correctState,
      
      // Frontage status
      has_frontage: defaults.has_frontage,
      
      // BUG FIX (Feb 11, 2026): Use correct HQ position by echelon
      // BRIGADE: getBrigadeHQPosition = national base + unit type offset (e.g. NATO Artillery = 18+5 = 23km)
      // BATTALION: defaults.position_km = battalion-level typical position (5km for infantry)
      // Bug: defaults.position_km for brigades returned battalion value (5km for Infantry)
      position_feba_km: unitEchelon === 'BRIGADE'
        ? (() => {
            // v4.6.2 DEBUG: Log when type change triggers position calculation
            return getBrigadeHQPosition(editedUnit.nationalStandard, newType);
          })()
        : defaults.position_km,
      
      // Evacuation assets
      evacuation_assets: newAssets,
      
      // Tactical defaults
      tactical_in_parent: {
        ...editedUnit.tactical_in_parent,
        
        // v2.4 Phase 2.1: Removed hq_position_feba_km - ForceService calculates this
        
        // NEW: Use TRANSLATED posture (combat  support for CSS)
        posture: translatedPosture,
        posture_multiplier: postureMultiplier,
        medical_operational: medicalOperational,
        medical_delay_hours: medicalDelayHours,
        
        // Assigned Task and related
        tactical_role: defaults.mission,
        mission_multiplier: missionMultiplier
      }
    });

    // FIX-C: Auto-reset HQ/Support personnel to defaults for the new unit type.
    // Mirrors handleResetBreakdown logic — runs immediately on type change so the
    // user sees correct defaults without needing to press "Reset to Default".
    const standard = NATIONAL_STANDARDS[editedUnit.nationalStandard];
    if (standard) {
      if (unitEchelon === 'BRIGADE') {
        const hqCount = standard.brigade?.hq_personnel || 200;
        const supportPercent = standard.brigade?.support_ratio || 0.20;
        const supportCount = Math.round(newStrength * supportPercent);
        setHQPersonnel(hqCount);
        setSupportPersonnel(supportCount);
      } else {
        const echelonKey = unitEchelon.toLowerCase();
        const hqTotal = standard[echelonKey]?.hq_personnel || editedUnit.strength || 0;
        const ratios = standard[echelonKey]?.breakdown_ratios;
        if (ratios) {
          setHQPersonnel(Math.round(hqTotal * ratios.command));
          setSupportPersonnel(Math.round(hqTotal * ratios.support));
        }
      }
    }
  };
  
  const handleAssignedTaskChange = (e) => {
    const newTask = e.target.value;
    const multiplier = getMissionMultiplier(newTask);
    setEditedUnit({
      ...editedUnit,
      tactical_in_parent: {
        ...editedUnit.tactical_in_parent,
        tactical_role: newTask,
        mission_multiplier: multiplier
      }
    });
  };
  
  const handlePostureChange = (e) => {
    const newPosture = e.target.value;
    const multiplier = getPostureMultiplier(newPosture);
    const operational = isMedicalOperational(newPosture);
    const delayHours = getMedicalDelayHours(newPosture);
    
    setEditedUnit({
      ...editedUnit,
      tactical_in_parent: {
        ...editedUnit.tactical_in_parent,
        posture: newPosture,
        posture_multiplier: multiplier,
        medical_operational: operational,
        medical_delay_hours: delayHours
      }
    });
  };
  
  const handleHQPositionChange = (e) => {
    const newPos = parseFloat(e.target.value) || 5;
    setEditedUnit({
      ...editedUnit,
      position_feba_km: newPos
      // v2.4 Phase 2.1: Removed tactical_in_parent.hq_position_feba_km update
      // Only position_feba_km is used (passed to ForceService if needed)
    });
  };
  
  const handleStrengthChange = (e) => {
    const newStrength = parseInt(e.target.value) || 0;
    setEditedUnit({
      ...editedUnit,
      strength: newStrength
    });
  };
  
  // v4.3.7: Handle HQ personnel change
  const handleHQPersonnelChange = (e) => {
    const newHQ = parseInt(e.target.value) || 0;
    setHQPersonnel(newHQ);
    // Auto-update total strength
    const newTotal = newHQ + supportPersonnel;
    setEditedUnit({
      ...editedUnit,
      strength: newTotal
    });
  };
  
  // v4.3.7: Handle Support personnel change
  const handleSupportPersonnelChange = (e) => {
    const newSupport = parseInt(e.target.value) || 0;
    setSupportPersonnel(newSupport);
    // Auto-update total strength
    const newTotal = hqPersonnel + newSupport;
    setEditedUnit({
      ...editedUnit,
      strength: newTotal
    });
  };
  
  // v4.3.7: Reset to national standard defaults
  const handleResetBreakdown = () => {
    const standard = NATIONAL_STANDARDS[editedUnit.nationalStandard];
    if (standard && editedUnit.type) {
      const echelonKey = editedUnit.type.toLowerCase();
      
      // BUG FIX (Feb 16, 2026): Different logic for BRIGADE vs CORPS/DIVISION
      // BUG-067 FIX: toUpperCase() for case-insensitive comparison
      if (editedUnit.type?.toUpperCase() === 'BRIGADE') {
        // For brigades: HQ is fixed count, Support is ~20% of total strength
        const hqCount = standard.brigade?.hq_personnel || 200;
        const supportPercent = standard.brigade?.support_ratio || 0.20; // 20% default
        const supportCount = Math.round(editedUnit.strength * supportPercent);
        
        setHQPersonnel(hqCount);
        setSupportPersonnel(supportCount);
        setEditedUnit({
          ...editedUnit,
          strength: hqCount + supportCount
        });
      } else {
        // For Corps/Division HQ echelons: Apply ratios to HQ strength
        const hqTotal = standard[echelonKey]?.hq_personnel || editedUnit.strength || 0;
        const ratios = standard[echelonKey]?.breakdown_ratios;
        
        if (ratios) {
          const defaultHQ = Math.round(hqTotal * ratios.command);
          const defaultSupport = Math.round(hqTotal * ratios.support);
          setHQPersonnel(defaultHQ);
          setSupportPersonnel(defaultSupport);
          setEditedUnit({
            ...editedUnit,
            strength: defaultHQ + defaultSupport
          });
        }
      }
    }
  };
  
  const handleSave = () => {
    // v3.0 VALIDATION: Check if Unit Type is selected (for Brigade level)
    const isBrigadeLevel = editedUnit.type?.toUpperCase() === 'BRIGADE';  // BUG-067 FIX
    
    if (isBrigadeLevel && !editedUnit.unit_type) {
      setUnitTypeError('Unit Type is required. Please select a brigade type before saving.');
      return; // Don't save
    }
    
    // Clear validation error if it was set
    setUnitTypeError(null);
    
    // v4.3.7: Include breakdown if HQ or Support values were modified
    // Only include breakdown for higher echelons (Corps, Division, Brigade)
    const includesBreakdown = editedUnit.type !== 'BATTALION' && (hqPersonnel > 0 || supportPersonnel > 0);
    
    const breakdown = includesBreakdown ? {
      command: hqPersonnel,
      support: supportPersonnel
    } : editedUnit.breakdown; // Preserve existing breakdown if not modified
    
    // Include force quality override if enabled
    const unitToSave = {
      ...editedUnit,
      breakdown: breakdown,  // v4.3.7: Add breakdown to saved unit
      force_quality_override: useForceQualityOverride ? forceQualityOverride : null
    };
    onSave(unitToSave);
    onClose();
  };
  
  const totalMultiplier = calculateTotalMultiplier(editedUnit, forceBuilderResult);
  const multiplierSeverity = getMultiplierSeverity(totalMultiplier);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          {isEditingBattalion ? <BattalionIcon /> : <EditIcon />}
          <span>Edit {editedUnit.name}</span>
          <Chip label={editedUnit.type} size="small" />
          {isEditingBattalion && (
            <Chip label={t('tab1.task_1_7')} size="small" color="success" variant="outlined" />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {/* v3.0 VALIDATION: Show error alert if Unit Type not selected */}
        {unitTypeError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUnitTypeError(null)}>
            <AlertTitle>{t('tab1.unit_type_required')}</AlertTitle>
            {unitTypeError}
          </Alert>
        )}
        {/* v4.3.3: Show info alert for auto-actions (e.g. MEDICAL removes battalions on save) */}
        {unitTypeInfo && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUnitTypeInfo(null)}>
            {unitTypeInfo}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          
          {/* TASK 1.7: BATTALION-SPECIFIC EDITING */}
          {isEditingBattalion ? (
            <>
              {/* Battalion Type Selector */}
              <Grid size={{ xs: 12, md: 6 }}>
                <BattalionTypeSelector
                  value={editedUnit.unit_type}
                  onChange={handleBattalionTypeChange}
                  nationalStandard={editedUnit.nationalStandard || editedUnit.national_standard}
                />
              </Grid>
              
              {/* Strength */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab1.strength')}
                  value={editedUnit.strength || 0}
                  onChange={handleStrengthChange}
                  inputProps={{ min: 100, max: 2000 }}
                  helperText={`Typical: ${getBattalionTypeConfig(editedUnit.unit_type).typical_strength || 800}`}
                />
              </Grid>
              
              {/* Battalion Info Alert */}
              <Grid size={{ xs: 12 }}>
                <BattalionTypeInfoAlert battalionType={editedUnit.unit_type} nationalStandard={editedUnit.nationalStandard || editedUnit.national_standard} />
              </Grid>
              
              {/* Battalion Type Reference Table */}
              <Grid size={{ xs: 12 }}>
                <BattalionTypeReferenceTable currentType={editedUnit.unit_type} />
              </Grid>
              
              {/* Position from FEBA */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab1.position_feba')}
                  value={editedUnit.position_feba_km || 5}
                  onChange={(e) => {
                    const newPos = parseFloat(e.target.value) || 5;
                    setEditedUnit({
                      ...editedUnit,
                      position_feba_km: newPos
                      // v2.4 Phase 2.1: Removed tactical_in_parent.hq_position_feba_km update
                      // ForceService calculates HQ position, Tab1 just stores user input
                    });
                  }}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  helperText={`Typical: ${getBattalionTypeConfig(editedUnit.unit_type).typical_position_km} km`}
                />
              </Grid>

              {/* Operational State */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('tab1.operational_state')}</InputLabel>
                  <Select
                    value={editedUnit.operational_state}
                    label={t('tab1.operational_state')}
                    onChange={(e) => setEditedUnit({
                      ...editedUnit,
                      operational_state: e.target.value
                    })}
                  >
                    {getOperationalStatesArray().map(state => (
                      <MenuItem key={state.value} value={state.value}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{state.label}</span>
                          <Stack direction="row" spacing={0.5}>
                            <Chip 
                              size="small" 
                              label={`Battle: ${state.battle_casualty_multiplier}x`}
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                            {SHOW_DNBI_DISPLAY && (
                            <Chip 
                              size="small" 
                              label={`DNBI: ${getDNBIRate(state.value)}/1000`}
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                            )}
                          </Stack>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {OPERATIONAL_STATES[editedUnit.operational_state]?.description || 'Unit engagement level'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              {/* Operational State vs Posture Validation Alert */}
              {statePostureValidation.severity && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity={statePostureValidation.severity}>
                    <AlertTitle>{statePostureValidation.message}</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {statePostureValidation.explanation}
                    </Typography>
                    {statePostureValidation.suggestion && (
                      <Typography variant="body2" fontWeight="bold">
                        [i] {statePostureValidation.suggestion}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
              
              {/* Battalion Casualty Summary */}
              <Grid size={{ xs: 12 }}>
                <Alert severity={getMultiplierSeverity(editedUnit.unit_type_modifier || 1.0)}>
                  <AlertTitle>{t('tab1.battalion_casualty_modifier')}</AlertTitle>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h4">
                      {(editedUnit.unit_type_modifier || 1.0).toFixed(2)}x
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      = Battalion Type ({unitTypeLabel(editedUnit.unit_type, editedUnit.nationalStandard || editedUnit.national_standard, getBattalionTypeConfig(editedUnit.unit_type).label)})
                    </Typography>
                  </Stack>
                </Alert>
              </Grid>
            </>
          ) : (
            <>
              {/* STANDARD NON-BATTALION EDITING */}

              {/* BUG-075 FIX: National Standard selector for brigades in MULTINATIONAL force.
                  Root cause: editedUnit.nationalStandard was read-only after Quick Build.
                  Condition: force is MULTINATIONAL AND editing a Brigade (not CORPS/DIVISION/BATTALION).
                  MULTINATIONAL excluded from options — brigades must have a specific nation.
                  On change: update editedUnit.nationalStandard; unit type filter re-evaluates
                  automatically via getAvailableBrigadeTypes(editedUnit.nationalStandard).
                  BUG-092 FIX: Also show when parent division is MULTINATIONAL (MND case) —
                  corps may be Romanian/NATO but an individual division can still be MND. */}
              {(forceBuilderResult?.nationalStandard === 'MULTINATIONAL' || parentDivIsMultinational) &&
               editedUnit.type !== 'CORPS' && editedUnit.type !== 'DIVISION' && editedUnit.type !== 'Battalion' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tab1.national_standard')}</InputLabel>
                    <Select
                      value={editedUnit.nationalStandard || ''}
                      label={t('tab1.national_standard')}
                      onChange={(e) => {
                        const newStd = e.target.value;
                        setEditedUnit(prev => ({
                          ...prev,
                          nationalStandard: newStd,
                          // Reset unit_type if it is no longer valid for the new standard
                          unit_type: getAvailableBrigadeTypes(newStd).includes(prev.unit_type)
                            ? prev.unit_type
                            : ''
                        }));
                      }}
                    >
                      {sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
                        .filter(([key]) => key !== 'MULTINATIONAL')
                        .map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value.flag} {value.label}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      Brigade national standard — changes unit type filter
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}

              {/* Unit Type - Enhanced with Auto-Apply */}
              {/* v3.4 CSS-005: Filtered by national standard */}
              {/* v3.0 VALIDATION: Required field for brigades */}
              {/* Hidden for CORPS/DIVISION - they are HQ, not tactical unit types */}
              {(editedUnit.type !== 'CORPS' && editedUnit.type !== 'DIVISION') && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required error={!!unitTypeError}>
                  <InputLabel required>{t('tab1.unit_type')} *</InputLabel>
                  <Select
                    value={editedUnit.unit_type || ''}
                    label={`${t('tab1.unit_type')} *`}
                    onChange={handleUnitTypeChange}
                    error={!!unitTypeError}
                  >
                    {Object.entries(UNIT_TYPES_FROM_CONSTANTS)
                      .filter(([key]) => {
                        // v3.4 CSS-005: Filter by available types for national standard
                        const availableTypes = getAvailableBrigadeTypes(editedUnit.nationalStandard);
                        return availableTypes.includes(key);
                      })
                      .map(([key, val]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <span>{val.icon} {brigadeTypeLabel(key, editedUnit.nationalStandard, val.label)}</span>
                          <Stack direction="row" spacing={0.5}>
                            <Chip 
                              size="small" 
                              label={`${val.casualty_modifier || 1.0}x`} 
                              color={(val.casualty_modifier || 1.0) > 1 ? 'error' : (val.casualty_modifier || 1.0) < 1 ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Stack>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!unitTypeError}>
                    {unitTypeError ? '\u00a0' : '[>>] Auto-applies: Position, Posture, Mission, Op State - Filtered by ' + editedUnit.nationalStandard + ' standard'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              )}

              {/* v4.3.7: Personnel Breakdown (HQ/Support) */}
              {/* Only show for higher echelons (Corps, Division, Brigade), not Battalion */}
              {editedUnit.type !== 'BATTALION' ? (
                <>
                  {/* Total Strength - Read-only, auto-calculated */}
                  {/* OBS-S109-001 FIX: type="number" with readOnly still renders native spinner
                      arrows in all browsers, implying the field is editable. Suppress via CSS. */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('tab1.total_strength')}
                      value={editedUnit.strength || 0}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-calculated from HQ + Support"
                      sx={{
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                    />
                  </Grid>
                  
                  {/* HQ Personnel - Editable */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('tab1.hq_personnel')}
                      value={hqPersonnel}
                      onChange={handleHQPersonnelChange}
                      inputProps={{ min: 0, max: 100000 }}
                      helperText="Command staff"
                    />
                  </Grid>

                  {/* Support Personnel - Editable */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('tab1.support_personnel')}
                      value={supportPersonnel}
                      onChange={handleSupportPersonnelChange}
                      inputProps={{ min: 0, max: 100000 }}
                      helperText="Support units"
                    />
                  </Grid>
                  
                  {/* Breakdown Info + Reset Button */}
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2">
                          <strong>Default ({editedUnit.nationalStandard}):</strong> {' '}
                          {(() => {
                            const standard = NATIONAL_STANDARDS[editedUnit.nationalStandard];
                            const echelonKey = editedUnit.type?.toLowerCase();
                            
                            // BUG FIX (Feb 16, 2026): Different display for BRIGADE vs CORPS/DIVISION
                            // BUG-067 FIX: toUpperCase() for case-insensitive comparison
                            if (editedUnit.type?.toUpperCase() === 'BRIGADE') {
                              const hqCount = standard?.brigade?.hq_personnel || 200;
                              const supportPercent = standard?.brigade?.support_ratio || 0.20;
                              const supportCount = Math.round(editedUnit.strength * supportPercent);
                              return `${hqCount} HQ / ${supportCount} Support`;
                            } else {
                              const ratios = standard?.[echelonKey]?.breakdown_ratios;
                              const hqTotal = standard?.[echelonKey]?.hq_personnel || 0;
                              if (ratios && hqTotal) {
                                return `${Math.round(hqTotal * ratios.command)} HQ / ${Math.round(hqTotal * ratios.support)} Support`;
                              }
                            }
                            return 'N/A';
                          })()}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleResetBreakdown}
                        >
                          {t('tab1.reset_to_default')}
                        </Button>
                      </Stack>
                    </Alert>
                  </Grid>
                </>
              ) : (
                /* Strength field for Battalion - Keep original behavior */
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab1.strength')}
                    value={editedUnit.strength || 0}
                    onChange={handleStrengthChange}
                    inputProps={{ min: 100, max: 100000 }}
                  />
                </Grid>
              )}


          {/* Auto-Apply Status Display */}
          {editedUnit.unit_type && (
            <Grid size={{ xs: 12 }}>
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ py: 0.5 }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    {UNIT_TYPES_FROM_CONSTANTS[editedUnit.unit_type]?.icon} {brigadeTypeLabel(editedUnit.unit_type, editedUnit.nationalStandard || editedUnit.national_standard, UNIT_TYPES_FROM_CONSTANTS[editedUnit.unit_type]?.label)}:
                  </Typography>
                  <Chip size="small" label={`${editedUnit.position_feba_km || 0} km`} />
                  <Chip size="small" label={editedUnit.tactical_in_parent?.posture?.replace(/_/g, ' ') || 'N/A'} />
                  <Chip size="small" label={editedUnit.operational_state?.replace(/_/g, ' ') || 'SUPPORT'} />
                  <Chip 
                    size="small" 
                    label={UNIT_TYPES_FROM_CONSTANTS[editedUnit.unit_type]?.has_frontage ? 'Frontage' : 'Support'}
                    color={UNIT_TYPES_FROM_CONSTANTS[editedUnit.unit_type]?.has_frontage ? 'primary' : 'secondary'}
                    variant="outlined"
                  />
                </Stack>
              </Alert>
            </Grid>
          )}

          {/* Task 1.4: Unit Type Defaults Reference Panel - Only for BRIGADE/BATTALION */}
          {/* BUG-067 FIX: toUpperCase() for case-insensitive comparison */}
          {(editedUnit.type?.toUpperCase() === 'BRIGADE' || editedUnit.type?.toUpperCase() === 'BATTALION') && (
          <Grid size={{ xs: 12 }}>
            <Accordion sx={{ bgcolor: 'grey.50' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoIcon color="action" fontSize="small" />
                  <Typography variant="body2" fontWeight="bold">
                    {t('tab1.unit_type_ref')}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  When you change Unit Type, these defaults are auto-applied. You can override them manually.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Unit Type</strong></TableCell>
                        <TableCell align="center"><strong>Position</strong></TableCell>
                        <TableCell align="center"><strong>Posture</strong></TableCell>
                        <TableCell align="center"><strong>Op State</strong></TableCell>
                        <TableCell align="center"><strong>Frontage</strong></TableCell>
                        <TableCell align="center"><strong>Casualty</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(UNIT_TYPES_FROM_CONSTANTS)
                        .filter(([key], index, arr) => {
                          // BUG FIX 1: Filter duplicate labels (e.g., ENGINEER vs ENGINEERS)
                          // Keep the first occurrence of each unique label
                          const label = UNIT_TYPES_FROM_CONSTANTS[key]?.label;
                          const firstIndex = arr.findIndex(([k]) => 
                            UNIT_TYPES_FROM_CONSTANTS[k]?.label === label
                          );
                          return index === firstIndex;
                        })
                        .map(([key, ut]) => (
                        <TableRow 
                          key={key} 
                          selected={editedUnit.unit_type === key}
                          sx={{ 
                            bgcolor: editedUnit.unit_type === key ? 'action.selected' : 'inherit',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{ut.icon}</span>
                              <Typography variant="body2">{ut.label}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">{ut.typical_position_km} km</TableCell>
                          <TableCell align="center">
                            <Chip 
                              size="small" 
                              label={ut.default_posture?.replace(/_/g, ' ') || 'DEF'} 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              size="small" 
                              label={ut.operational_state?.replace(/_/g, ' ') || 'SUPPORT'}
                              color={ut.operational_state === 'COMBAT_ENGAGED' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {ut.has_frontage ? '[C] Yes' : '[S] No'}
                          </TableCell>
                          <TableCell align="center">{ut.casualty_modifier}x</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>
          )}

          {/* Assigned Task */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('tab1.assigned_task')}</InputLabel>
              <Select
                value={editedUnit.tactical_in_parent?.tactical_role || 'SUPPORTING'}
                label={t('tab1.assigned_task')}
                onChange={handleAssignedTaskChange}
              >
                {getMissionsArray().map(mission => (
                  <MenuItem key={mission.value} value={mission.value}>
                    {mission.label} ({mission.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Posture */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('tab1.operational_posture')}</InputLabel>
              <Select
                value={editedUnit.tactical_in_parent?.posture}
                label={t('tab1.operational_posture')}
                onChange={handlePostureChange}
              >
                {getPosturesArray()
                  .filter(posture => {
                    // NEW: Filter by unit type constraints
                    const constraints = getOperationalConstraints(editedUnit.unit_type);
                    return constraints.allowed_postures.includes(posture.value);
                  })
                  .map(posture => (
                  <MenuItem key={posture.value} value={posture.value}>
                    {posture.label} ({posture.casualty_multiplier}x)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Operational State - NEW for Task 1.2 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('tab1.operational_state')}</InputLabel>
              <Select
                value={editedUnit.operational_state}
                label={t('tab1.operational_state')}
                onChange={(e) => setEditedUnit({
                  ...editedUnit,
                  operational_state: e.target.value
                })}
              >
                {getOperationalStatesArray()
                  .filter(state => {
                    // NEW: Filter by unit type constraints
                    const constraints = getOperationalConstraints(editedUnit.unit_type);
                    return constraints.allowed_states.includes(state.value);
                  })
                  .map(state => (
                  <MenuItem key={state.value} value={state.value}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{state.label}</span>
                      <Chip 
                        size="small" 
                        label={`${state.battle_casualty_multiplier}x`}
                        variant="outlined"
                        sx={{ ml: 1, height: 20 }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {OPERATIONAL_STATES[editedUnit.operational_state]?.description || 'Unit engagement level'}
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Operational State vs Posture Validation Alert */}
          {statePostureValidation.severity && (
            <Grid size={{ xs: 12 }}>
              <Alert severity={statePostureValidation.severity}>
                <AlertTitle>{statePostureValidation.message}</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {statePostureValidation.explanation}
                </Typography>
                {statePostureValidation.suggestion && (
                  <Typography variant="body2" fontWeight="bold">
                    [i] {statePostureValidation.suggestion}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}

          {/* Task 1.6: National Caveats Section - For MULTINATIONAL operations */}
          {(editedUnit.nationalStandard !== 'MULTINATIONAL' && forceBuilderResult?.nationalStandard === 'MULTINATIONAL') && (
            <Grid size={{ xs: 12 }}>
              <Accordion defaultExpanded={editedUnit.national_caveats?.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GlobalIcon color="primary" />
                    <Typography fontWeight="bold">
                       National Caveats ({editedUnit.nationalStandard})
                    </Typography>
                    {editedUnit.national_caveats?.length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${editedUnit.national_caveats.length} active`}
                        color="warning"
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    National caveats are restrictions that nations place on their forces in multinational operations.
                    These affect available missions, ROE, and medical evacuation.
                  </Typography>
                  
                  {/* Caveat Preset Selector */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('tab1.caveat_preset')}</InputLabel>
                    <Select
                      value={editedUnit.caveat_preset || 'NONE'}
                      label={t('tab1.caveat_preset')}
                      onChange={(e) => {
                        const preset = e.target.value;
                        const caveats = preset === 'CUSTOM' 
                          ? (editedUnit.national_caveats || [])
                          : (NATIONAL_CAVEAT_PRESETS[preset]?.caveats || []);
                        setEditedUnit({
                          ...editedUnit,
                          caveat_preset: preset,
                          national_caveats: caveats
                        });
                      }}
                    >
                      {getCaveatPresetOptions().map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Custom Caveat Selection */}
                  {editedUnit.caveat_preset === 'CUSTOM' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                        Select Individual Caveats:
                      </Typography>
                      <Grid container spacing={1}>
                        {getCaveatTypesArray().map(caveat => (
                          <Grid size={{ xs: 12, sm: 6 }} key={caveat.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={editedUnit.national_caveats?.includes(caveat.id) || false}
                                  onChange={(e) => {
                                    const current = editedUnit.national_caveats || [];
                                    const updated = e.target.checked
                                      ? [...current, caveat.id]
                                      : current.filter(c => c !== caveat.id);
                                    setEditedUnit({
                                      ...editedUnit,
                                      national_caveats: updated
                                    });
                                  }}
                                />
                              }
                              label={
                                <Tooltip title={caveat.description}>
                                  <Typography variant="body2">
                                    {caveat.icon} {caveat.label}
                                  </Typography>
                                </Tooltip>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  
                  {/* Active Caveats Display - v3.2: Enhanced with calculateCaveatImpact */}
                  {editedUnit.national_caveats?.length > 0 && (() => {
                    const impact = calculateCaveatImpact(editedUnit.national_caveats);
                    return (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <AlertTitle>Active Restrictions ({editedUnit.national_caveats.length})</AlertTitle>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          {editedUnit.national_caveats.map(caveatId => {
                            const caveat = CAVEAT_TYPES[caveatId];
                            return caveat ? (
                              <Chip 
                                key={caveatId}
                                size="small"
                                label={`${caveat.icon} ${caveat.label}`}
                                variant="outlined"
                                color="warning"
                              />
                            ) : null;
                          })}
                        </Stack>
                        {/* v3.2: Detailed impact from calculateCaveatImpact */}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                          <strong>Operational Impact:</strong>
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip 
                            size="small" 
                            label={`Casualty: ${impact.casualty_modifier.toFixed(2)}x`}
                            color={impact.casualty_modifier > 1.1 ? 'error' : 'default'}
                          />
                          <Chip 
                            size="small" 
                            label={`MEDEVAC: ${impact.medevac_modifier.toFixed(2)}x`}
                            color={impact.medevac_modifier > 1.1 ? 'warning' : 'default'}
                          />
                          <Chip 
                            size="small" 
                            label={`${impact.restricted_missions.length} missions restricted`}
                            color={impact.restricted_missions.length > 2 ? 'error' : 'default'}
                          />
                        </Stack>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          {formatCaveatSummary(editedUnit.national_caveats)}
                        </Typography>
                      </Alert>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* HQ Position */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('tab1.hq_distance_feba')}
              value={editedUnit.position_feba_km || 5}
              onChange={handleHQPositionChange}
              inputProps={{ min: 0, max: 200, step: 1 }}
              helperText="Position of headquarters relative to front line"
            />
          </Grid>

          {/* Medical Status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Alert
              severity={editedUnit.tactical_in_parent?.medical_operational ? 'success' : 'warning'}
              icon={<MedicalIcon />}
            >
              <AlertTitle>{t('tab1.medical_status')}</AlertTitle>
              {editedUnit.tactical_in_parent?.medical_operational 
                ? 'MTF Operational' 
                : `MTF Delayed: +${editedUnit.tactical_in_parent?.medical_delay_hours || 0} hours`}
            </Alert>
          </Grid>

          {/* Combined Multiplier Preview */}
          <Grid size={{ xs: 12 }}>
            <Alert severity={multiplierSeverity}>
              <AlertTitle>{t('tab1.combined_multiplier')}</AlertTitle>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h4">{totalMultiplier.toFixed(2)}x</Typography>
                <Typography variant="body2" color="text.secondary">
                  = Assigned Task ({editedUnit.tactical_in_parent?.mission_multiplier?.toFixed(2) || 1}x) 
                  x Posture ({editedUnit.tactical_in_parent?.posture_multiplier?.toFixed(2) || 1}x) 
                  x Unit Type ({editedUnit.unit_type_modifier?.toFixed(2) || 1}x)
                  x Force Quality
                  x Op State ({(OPERATIONAL_STATES[editedUnit.operational_state]?.battle_casualty_multiplier || 1).toFixed(2)}x)
                </Typography>
              </Stack>
            </Alert>
          </Grid>
            </>
          )}
        </Grid>

        {/* Force Quality Override Section - only for non-battalions */}
        {!isEditingBattalion && (
          <ForceQualityOverrideSection
            unit={editedUnit}
            forceBuilderResult={forceBuilderResult}
            override={forceQualityOverride}
            onOverrideChange={setForceQualityOverride}
            onOverrideToggle={setUseForceQualityOverride}
            useOverride={useForceQualityOverride}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!editedUnit.unit_type && !isCorpsDivisionLevel}
        >
          {t('tab1.save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// ENHANCED UNIT CARD COMPONENT
// ============================================================================

const EnhancedUnitCard = ({ unit, mode, onEdit, onDelete, onAddBrigade, onAddBattalion, onDeleteBattalion, depth = 0, forceBuilderResult }) => {
  const { t } = useTranslation();
  const bgColors = ['grey.50', 'grey.100', 'grey.200'];
  const totalMultiplier = calculateTotalMultiplier(unit, forceBuilderResult);
  const multiplierSeverity = getMultiplierSeverity(totalMultiplier);
  
  const isExpandable = unit.type?.toUpperCase() === 'DIVISION' || unit.type?.toUpperCase() === 'CORPS';
  
  // TASK 1.7: Check if this is a battalion and get its config
  const isUnitBattalion = isBattalion(unit);
  const battalionConfig = isUnitBattalion ? getBattalionTypeConfig(unit.unit_type) : null;
  
  // v3.2: Check if this is a medical brigade
  const isUnitMedicalBrigade = !isUnitBattalion && unit.type?.toUpperCase() === 'BRIGADE' && 
    isMedicalBrigade(unit.unit_type);
  
  // v3.2: Get DNBI rate for display
  const dnbiRate = getDNBIRate(unit.operational_state || 'COMBAT_ENGAGED');
  
  return (
    <Card 
      sx={{ 
        ml: depth * 3, 
        mb: 1, 
        bgcolor: bgColors[depth] || 'grey.200',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        // TASK 1.7: Highlight combat vs support battalions with colored border
        // v3.2: Also highlight medical brigades
        borderLeft: (isUnitBattalion || isUnitMedicalBrigade) ? 4 : 0,
        borderColor: isUnitMedicalBrigade 
          ? 'success.main'  // Green for medical brigades
          : (isUnitBattalion 
            ? (battalionConfig?.has_frontage ? 'error.main' : 'info.main')
            : 'transparent')
      }}
      onClick={() => onEdit && onEdit(unit)}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Grid container spacing={2} alignItems="center">
          {/* Unit Name and Type */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                {/* getMilitaryDisplayName: NATO hierarchy label (display-only, stored name unchanged).
                    BATTALION → "1st Bn / 1st Bde / 1st Div"
                    BRIGADE   → "1st Bde / 1st Div"
                    others    → unit.name */}
                {getMilitaryDisplayName(unit)}
              </Typography>
              <Chip label={unit.type} size="small" color="primary" variant="outlined" />
              {/* v3.2: Show medical brigade badge */}
              {isUnitMedicalBrigade && (
                <Chip 
                  icon={<MedicalIcon />}
                  label="Medical" 
                  size="small" 
                  color="success"
                  variant="filled"
                />
              )}
              {unit.nationalStandard && (
                <Chip 
                  label={
                    // BUG-127 FIX (March 17, 2026): Division HQ for MULTINATIONAL force stored
                    // nationalStandard='NATO' (ForceService uses NATO as HQ baseline for calculations).
                    // Display-only fix: detect MULTINATIONAL by checking if brigades in the result
                    // have mixed nationalStandards. If so, show MULTINATIONAL label for the division.
                    // Calculation values unchanged — NATO baseline is architecturally correct for HQ.
                    // BUG-127b FIX (April 4, 2026): Same root cause affects CORPS unit. CORPS HQ also
                    // stored as 'NATO' internally. For CORPS, use forceBuilderResult.nationalStandard
                    // directly (more reliable than brigade heuristic for corps level).
                    (() => {
                      // BUG-NEXT-009 FIX: DIVISION chip shows NATO even when force is MULTINATIONAL.
                      // Root cause: mixed-brigades heuristic fails when all brigades get 'MULTINATIONAL'
                      // nationalStandard (BUG-NEXT-008 pattern) — set size = 1, not "mixed".
                      // BUG-S109-002 FIX (revised): Use unit.nationalStandard === 'MULTINATIONAL' for
                      // DIVISION (not forceBuilderResult.nationalStandard). The top-level result field
                      // is the CORPS-level standard (stamped in handleQuickBuild). Using it for
                      // DIVISION caused ALL divisions to show "Multinational" even when a division has
                      // a single national standard (e.g. French-only division → French flag expected).
                      // unit.nationalStandard is set per-division by enhanceForceBuilderResult PASS 3
                      // from divisionStandards[] — it is the authoritative per-division value.
                      const isMultinationalForce =
                        (unit.type === 'CORPS' && forceBuilderResult?.nationalStandard === 'MULTINATIONAL') ||
                        (unit.type === 'DIVISION' && (
                          unit.nationalStandard === 'MULTINATIONAL' ||
                          (() => {
                            const divBdes = (forceBuilderResult?.brigades || []).filter(b => b.parent_division_id === unit.id);
                            return divBdes.length > 0 && new Set(divBdes.map(b => b.nationalStandard)).size > 1;
                          })()
                        ));
                      const displayStd = isMultinationalForce ? 'MULTINATIONAL' : unit.nationalStandard;
                      return NATIONAL_STANDARDS[displayStd]
                        ? `${NATIONAL_STANDARDS[displayStd].flag} ${NATIONAL_STANDARDS[displayStd].label}`
                        : displayStd;
                    })()
                  }
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            {/* TASK 1.7: Show battalion type info for battalions */}
            <Typography variant="body2" color="text.secondary">
              {isUnitBattalion ? (
                <>
                  {battalionConfig?.icon} {unitTypeLabel(unit.unit_type, unit.nationalStandard || unit.national_standard, battalionConfig?.label)} - {unit.strength?.toLocaleString() || 0} personnel
                  {/* v3.2: Show DNBI rate (hidden 2026-10-11: x1000 unit bug and an unsourced number the engine does not use) */}
                  {SHOW_DNBI_DISPLAY && (
                  <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                    (DNBI: {(dnbiRate * 1000).toFixed(1)}/1000/day)
                  </Typography>
                  )}
                </>
              ) : (
                <>
                  {(unit.unit_type || unit.brigade_type) ? brigadeTypeLabel(unit.unit_type || unit.brigade_type, unit.nationalStandard || unit.national_standard, getUnitTypeLabel(unit.unit_type || unit.brigade_type)) : 'Not specified'} - 
                  {unit.strength?.toLocaleString() || 0} personnel
                </>
              )}
            </Typography>
          </Grid>

          {/* Tactical Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {/* TASK 1.7: Battalion-specific chips */}
              {isUnitBattalion ? (
                <>
                  <Chip 
                    size="small" 
                    label={battalionConfig?.has_frontage ? '[C] Combat' : '[S] Support'}
                    color={battalionConfig?.has_frontage ? 'error' : 'info'}
                    variant="outlined"
                  />
                  <Chip 
                    size="small" 
                    label={battalionHasRole1(unit, forceBuilderResult, battalionConfig) ? 'Role 1 [OK]' : 'No Role 1'}
                    color={battalionHasRole1(unit, forceBuilderResult, battalionConfig) ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Chip 
                    size="small" 
                    label={`${unit.position_feba_km || battalionConfig?.typical_position_km || 5}km`}
                    variant="outlined"
                  />
                </>
              ) : (
                <>
                  <Chip 
                    size="small" 
                    label={getMissionByValue(unit.tactical_in_parent?.tactical_role)?.label || 'Supporting'} 
                    color="info"
                    variant="outlined"
                  />
                  <Chip 
                    size="small" 
                    label={getPostureByValue(unit.tactical_in_parent?.posture)?.label || 'Defense'} 
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip 
                    size="small" 
                    label={`HQ: ${unit.position_feba_km || 5}km`}
                    variant="outlined"
                  />
                </>
              )}
            </Stack>
          </Grid>

          {/* Multiplier and Actions */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Chip 
                label={isUnitBattalion 
                  ? `${(unit.unit_type_modifier || battalionConfig?.casualty_modifier || 1.0).toFixed(2)}x`
                  : `${totalMultiplier.toFixed(2)}x`
                }
                color={multiplierSeverity}
                size="small"
              />
              {!isUnitBattalion && (
                <ForceQualityBadge unit={unit} forceBuilderResult={forceBuilderResult} compact />
              )}
              {unit.tactical_in_parent?.medical_operational === false && (
                <Tooltip title="Medical facility delayed">
                  <WarningIcon color="warning" fontSize="small" />
                </Tooltip>
              )}
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit && onEdit(unit); }}>
                <EditIcon fontSize="small" />
              </IconButton>
              {/* Brigade: delete brigade */}
              {onDelete && (
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(unit); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {/* Division: add subordinate brigade */}
              {onAddBrigade && isExpandable && (
                <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); onAddBrigade(unit); }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
              {/* Brigade: add battalion */}
              {onAddBattalion && (unit.type === 'BRIGADE' || unit.type === 'Brigade') && (
                <Tooltip title={t('tab1.add_battalion')}>
                  <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); onAddBattalion(unit); }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Battalion: delete battalion */}
              {onDeleteBattalion && unit.type === 'BATTALION' && (
                <Tooltip title={t('tab1.delete_battalion')}>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDeleteBattalion(unit); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// TACTICAL EDITOR COMPONENT
// ============================================================================

const TacticalEditor = ({ forceBuilderResult, echelon, mode, onUpdate, orbatLocked = false }) => {
  const { t } = useTranslation();
  const [editingUnit, setEditingUnit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  // Battalion management state
  const [showAddBattalionDialog, setShowAddBattalionDialog]         = useState(null); // brigade object
  const [addBattalionType, setAddBattalionType]                     = useState('MECHANIZED');
  const [showDeleteBattalionConfirm, setShowDeleteBattalionConfirm] = useState(null); // battalion object
  // INC-009: surface unit-build failures to user (previously swallowed as console.warn)
  const [unitBuildError, setUnitBuildError] = useState(null);

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
  };
  
  const updateUnitInArray = (arr, updated) => {
    if (!arr) return arr;
    return arr.map(item => item.id === updated.id ? { ...item, ...updated } : item);
  };
  
  const handleSaveUnit = (updatedUnit) => {
    if (!forceBuilderResult) return;
    
    const newResult = { ...forceBuilderResult };
    const unitType = updatedUnit.type?.toUpperCase();
    
    // TASK 1.7: Handle battalion updates
    if (unitType === 'BATTALION') {
      // BUG-175 FIX: BATTALION echelon stores the unit in forceBuilderResult.battalion
      // (singular), not in forceBuilderResult.battalions[] (always empty for this echelon).
      // Previously updateUnitInArray() wrote to the empty array and the edit was silently lost.
      if (newResult.battalion && newResult.battalion.id === updatedUnit.id) {
        newResult.battalion = { ...newResult.battalion, ...updatedUnit };
      } else {
        // Fallback: brigade/division/corps echelons store battalions in the array
        // 2026-10-24: with the brigade cards the brigade total is the sum of its cards: a changed card strength moves it by the difference
        const _before = BRIGADE_CARDS_ON ? (newResult.battalions || []).find(b => b.id === updatedUnit.id) : null;
        newResult.battalions = updateUnitInArray(newResult.battalions, updatedUnit);
        if (_before && typeof updatedUnit.strength === 'number' && updatedUnit.strength !== _before.strength) {
          const _delta = updatedUnit.strength - _before.strength;
          const _bump = (b) => (b && b.id === _before.parent_brigade_id ? { ...b, strength: (b.strength || 0) + _delta } : b);
          if (newResult.brigades) newResult.brigades = newResult.brigades.map(_bump);
          if (newResult.brigade) newResult.brigade = _bump(newResult.brigade);
        }
      }
      onUpdate(newResult);
      setEditingUnit(null);
      return;
    }
    
    if (unitType === 'CORPS') {
      newResult.corps = { ...newResult.corps, ...updatedUnit };
      
      // CASCADE: Corps  All Divisions (posture, tactical_role, unit_type, operational_state)
      const corpsPosture = updatedUnit.tactical_in_parent?.posture;
      const corpsPostureMultiplier = updatedUnit.tactical_in_parent?.posture_multiplier;
      const corpsTacticalRole = updatedUnit.tactical_in_parent?.tactical_role;
      const corpsMissionMultiplier = updatedUnit.tactical_in_parent?.mission_multiplier;
      const corpsUnitType = updatedUnit.unit_type;
      const corpsUnitTypeModifier = updatedUnit.unit_type_modifier;
      const corpsOperationalState = updatedUnit.operational_state; // BUG FIX: Added cascade
      
      if (newResult.divisions) {
        newResult.divisions = newResult.divisions.map(div => ({
          ...div,
          unit_type: corpsUnitType || div.unit_type,
          unit_type_modifier: corpsUnitTypeModifier || div.unit_type_modifier,
          operational_state: corpsOperationalState || div.operational_state, // BUG FIX: Cascade operational state
          tactical_in_parent: {
            ...div.tactical_in_parent,
            posture: corpsPosture || div.tactical_in_parent?.posture,
            posture_multiplier: corpsPostureMultiplier || div.tactical_in_parent?.posture_multiplier,
            tactical_role: corpsTacticalRole || div.tactical_in_parent?.tactical_role,
            mission_multiplier: corpsMissionMultiplier || div.tactical_in_parent?.mission_multiplier
          }
        }));
      }
      
      // CASCADE to brigades too
      if (newResult.brigades) {
        newResult.brigades = newResult.brigades.map(bde => ({
          ...bde,
          unit_type: corpsUnitType || bde.unit_type,
          unit_type_modifier: corpsUnitTypeModifier || bde.unit_type_modifier,
          operational_state: corpsOperationalState || bde.operational_state, // BUG FIX: Cascade operational state
          tactical_in_parent: {
            ...bde.tactical_in_parent,
            posture: corpsPosture || bde.tactical_in_parent?.posture,
            posture_multiplier: corpsPostureMultiplier || bde.tactical_in_parent?.posture_multiplier,
            tactical_role: corpsTacticalRole || bde.tactical_in_parent?.tactical_role,
            mission_multiplier: corpsMissionMultiplier || bde.tactical_in_parent?.mission_multiplier
          }
        }));
      }
      
    } else if (unitType === 'DIVISION') {
      if (newResult.division?.id === updatedUnit.id) {
        newResult.division = { ...newResult.division, ...updatedUnit };
      }
      newResult.divisions = updateUnitInArray(newResult.divisions, updatedUnit);
      
      // CASCADE: Division  Brigades in this division (posture, tactical_role, unit_type, operational_state)
      const divPosture = updatedUnit.tactical_in_parent?.posture;
      const divPostureMultiplier = updatedUnit.tactical_in_parent?.posture_multiplier;
      const divTacticalRole = updatedUnit.tactical_in_parent?.tactical_role;
      const divMissionMultiplier = updatedUnit.tactical_in_parent?.mission_multiplier;
      const divUnitType = updatedUnit.unit_type;
      const divUnitTypeModifier = updatedUnit.unit_type_modifier;
      const divOperationalState = updatedUnit.operational_state; // BUG FIX: Added cascade
      
      if (newResult.brigades) {
        newResult.brigades = newResult.brigades.map(bde => {
          if (bde.parent_division_id === updatedUnit.id) {
            return {
              ...bde,
              unit_type: divUnitType || bde.unit_type,
              unit_type_modifier: divUnitTypeModifier || bde.unit_type_modifier,
              operational_state: divOperationalState || bde.operational_state, // BUG FIX: Cascade operational state
              tactical_in_parent: {
                ...bde.tactical_in_parent,
                posture: divPosture || bde.tactical_in_parent?.posture,
                posture_multiplier: divPostureMultiplier || bde.tactical_in_parent?.posture_multiplier,
                tactical_role: divTacticalRole || bde.tactical_in_parent?.tactical_role,
                mission_multiplier: divMissionMultiplier || bde.tactical_in_parent?.mission_multiplier
              }
            };
          }
          return bde;
        });
      }
    } else if (unitType === 'BRIGADE') {
      // BUG-131 FIX (v4.16.0): Rebuild sub_units when unit_type changed.
      // handleUnitTypeChange (BUG-126) correctly sets brigade.strength to the
      // national-standard value but the sub_units are not regenerated — they
      // still carry the old type's composition and strength sum. The v3.0
      // calculator reads sub_units, so the wrong sum propagates to Tab 5/6.
      // 2026-10-25: a brigade of a brigade-echelon force (quick build) lives in forceBuilderResult.brigade, with brigades[] empty; the previous
      // type was looked up in brigades[] only, so a type change was never noticed and the old cards stayed (Dr. Tatar).
      const previousType = (forceBuilderResult.brigades?.find(b => b.id === updatedUnit.id)
        || (forceBuilderResult.brigade?.id === updatedUnit.id ? forceBuilderResult.brigade : null))?.unit_type;
      const typeChanged = previousType && previousType !== updatedUnit.unit_type;

      let unitToStore = updatedUnit;
      // 2026-10-25 (Dr. Tatar: a brigade whose type is changed kept the old cards): the units of the new type are built as a fresh quick build builds
      // them (the cards of the type); the brigade total is the sum of the cards. The medical brigade has cards too since 2026-10-25 (older path only when BRIGADE_CARDS_ON is false).
      let rebuiltUnits = null;
      if (typeChanged && (!isMedicalBrigade(updatedUnit.unit_type) || BRIGADE_CARDS_ON)) {   // 2026-10-25: the medical brigade has cards now
        const _divisionOfBrigade = newResult.division?.id === updatedUnit.parent_division_id
          ? newResult.division
          : newResult.divisions?.find(d => d.id === updatedUnit.parent_division_id);
        try {
          rebuiltUnits = ForceBuilder.buildBrigadeUnitsForType({
            id: updatedUnit.id,
            name: updatedUnit.name,
            unit_type: updatedUnit.unit_type,
            nationalStandard: updatedUnit.nationalStandard,
            parent_division_id: updatedUnit.parent_division_id,
            parent_division_name: _divisionOfBrigade?.name || null,
            parent_corps_name: newResult.corps?.name || null,
            position_feba_km: updatedUnit.position_feba_km   // 2026-10-25: the cards anchored to the brigade stand where the brigade stands (the medical brigade was built at 50 km, the brigade at 33)
          });
          unitToStore = {
            ...updatedUnit,
            brigade_type: updatedUnit.unit_type,
            strength: rebuiltUnits.strength,
            current_strength: rebuiltUnits.strength,   // 2026-10-25: a brigade of a new type is at full strength; the old current strength (4,743 of the mechanized brigade) stayed and Tab 2 showed it
            sub_units: rebuiltUnits.sub_units,
            composition_source: rebuiltUnits.composition_source
          };
        } catch (err) {
          console.error('[handleSaveUnit] brigade units of the new type could not be built:', err.message);
          setUnitBuildError('The brigade type was saved but the units of the new type could not be built. Review the brigade composition manually.');
        }
      }
      if (typeChanged && !rebuiltUnits) {
        try {
          const newSubUnits = ForceBuilder.generateScaledSubUnitsForTypeChange(
            updatedUnit.unit_type,
            updatedUnit.nationalStandard,
            updatedUnit.strength,
            updatedUnit.id,
            updatedUnit.name
          );
          if (newSubUnits !== null) {
            unitToStore = {
              ...updatedUnit,
              sub_units: newSubUnits,
              composition_source: 'doctrinal'
            };
          }
        } catch (err) {
          // generateScaledSubUnitsForTypeChange threw (unknown type) — keep
          // existing sub_units rather than crash. This should not happen for
          // any type present in unitTypes.js doctrinal_composition.
          console.error('[BUG-131] generateScaledSubUnitsForTypeChange failed:', err.message);
        }
      }

      // 2026-10-25 (Dr. Tatar, X-01): the headquarters, signal and support cards stand where their brigade stands; when the planner changes the brigade
      // HQ distance they follow it (the calculation reads the anchor too, so the casualties follow in every case)
      const _previousBrigade = (forceBuilderResult.brigades?.find(b => b.id === updatedUnit.id))
        || (forceBuilderResult.brigade?.id === updatedUnit.id ? forceBuilderResult.brigade : null);
      if (_previousBrigade && typeof unitToStore.position_feba_km === 'number' && unitToStore.position_feba_km !== _previousBrigade.position_feba_km) {
        newResult.battalions = moveAnchoredCards(newResult.battalions, unitToStore.id, unitToStore.position_feba_km);
      }

      if (newResult.brigade?.id === unitToStore.id) {
        newResult.brigade = { ...newResult.brigade, ...unitToStore };
      }
      newResult.brigades = updateUnitInArray(newResult.brigades, unitToStore);

      // Regenerate battalions whenever brigade type changes.
      // MEDICAL brigades: delete battalions (no organic battalion structure).
      // All other types: rebuild from doctrinal template for the new type.
      if (typeChanged) {
        // Track old battalion IDs so we can clean up their Role-1 facilities.
        const oldBnIds = new Set(
          (newResult.battalions || [])
            .filter(bn => bn.parent_brigade_id === unitToStore.id)
            .map(bn => bn.id)
        );
        // Remove old battalions for this brigade.
        newResult.battalions = (newResult.battalions || []).filter(
          bn => bn.parent_brigade_id !== unitToStore.id
        );
        // Remove Role-1 facilities that belonged to those battalions.
        if (newResult.role1Facilities && oldBnIds.size > 0) {
          newResult.role1Facilities = newResult.role1Facilities.filter(
            r1 => !oldBnIds.has(r1.parent_battalion_id)
          );
        }

        if (rebuiltUnits) {
          // the cards of the new type, built with the brigade (same ids, same parents)
          newResult.battalions = [...(newResult.battalions || []), ...rebuiltUnits.battalions];
          if (rebuiltUnits.role1_facilities.length > 0) {
            newResult.role1Facilities = [...(newResult.role1Facilities || []), ...rebuiltUnits.role1_facilities];
          }
        } else if (!isMedicalBrigade(unitToStore.unit_type)) {
          // Regenerate doctrinal battalion set for the new brigade type.
          const divisionUnit =
            newResult.division?.id === unitToStore.parent_division_id
              ? newResult.division
              : newResult.divisions?.find(d => d.id === unitToStore.parent_division_id);
          try {
            const composition = getBattalionComposition(unitToStore.unit_type);
            let bnIdx = 0;
            composition.template.forEach(({ type, count }) => {
              for (let i = 0; i < count; i++) {
                const bnNumber = bnIdx + 1;
                const typeLabel = BATTALION_TYPES[type]?.label || type;
                const bnResult = ForceBuilder.buildBattalion({
                  name: `${bnNumber}/${unitToStore.name} ${typeLabel}`,
                  battalion_number: bnNumber,
                  unit_type: type,
                  nationalStandard: unitToStore.nationalStandard,
                  strength: BATTALION_TYPES[type]?.typical_strength || 800,
                  position_feba_km: BATTALION_TYPES[type]?.typical_position_km || 8,
                  mission: 'SUPPORTING',
                  is_frontline: BATTALION_TYPES[type]?.has_frontage ?? true,
                  parent_brigade_id: unitToStore.id,
                  parent_brigade_name: unitToStore.name,
                  parent_division_id: unitToStore.parent_division_id,
                  parent_division_name: divisionUnit?.name || null,
                  skip_role1: false
                });
                newResult.battalions = [...(newResult.battalions || []), bnResult.battalion];
                if (bnResult.role1) {
                  newResult.role1Facilities = [
                    ...(newResult.role1Facilities || []),
                    bnResult.role1
                  ];
                }
                bnIdx++;
              }
            });
          } catch (err) {
            console.warn('[handleSaveUnit] battalion regen on type change failed:', err.message);
            setUnitBuildError('Sub-unit regeneration failed — unit type was saved but battalion structure could not be rebuilt. Review the brigade composition manually.');
          }
        }
      }
    } else if (unitType === 'BATTALION') {
      if (newResult.battalion?.id === updatedUnit.id) {
        newResult.battalion = { ...newResult.battalion, ...updatedUnit };
      }
      newResult.battalions = updateUnitInArray(newResult.battalions, updatedUnit);
    }
    
    onUpdate(newResult);
    setEditingUnit(null);
  };
  
  // FIXED: handleAddBrigade now uses getDefaultStrength() instead of hardcoded value
  const handleAddBrigade = (division) => {
    const newResult = { ...forceBuilderResult };
    const brigadeCount = newResult.brigades?.filter(b => b.parent_division_id === division.id).length || 0;
    
    // FIXED: Use national standard strength instead of hardcoded 4500
    const divisionStandard = division.nationalStandard;
    const brigadeStrength = getDefaultStrength(divisionStandard, 'BRIGADE') || 4000;
    
    const newBrigadeId = `bde_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // BUG-NEXT-008 FIX: 'MULTINATIONAL' is a corps/division-level standard only.
    // Brigades must have a nation-level standard. Default to 'NATO' when parent division is MULTINATIONAL.
    const brigadeNationalStandard = divisionStandard === 'MULTINATIONAL' ? 'NATO' : divisionStandard;

    const newBrigade = {
      id: newBrigadeId,
      name: `${brigadeCount + 1}${getOrdinal(brigadeCount + 1).slice(-2)} Brigade`,
      type: 'Brigade',
      parent_division_id: division.id,
      strength: brigadeStrength,  // FIXED: Now uses national standard
      nationalStandard: brigadeNationalStandard,
      unit_type: 'MECHANIZED',
      unit_type_modifier: 1.0,
      // BUG-NEXT-006 FIX: operational_state was missing → CRITICAL alert in edit dialog
      operational_state: division.operational_state || 'COMBAT_ENGAGED',
      tactical_in_parent: {
        mission: 'SUPPORTING',
        mission_multiplier: 1.0,
        posture: 'DELIBERATE_DEFENSE',
        posture_multiplier: 0.70,
        position: getPositionLabel(brigadeCount + 1, brigadeCount + 1),
        // v2.4 Phase 2.1: Removed hq_position_feba_km - ForceService calculates this
        medical_operational: true
      },
      evacuation_assets: calculateEvacuationAssets('MECHANIZED', 'BRIGADE', BASE_EVACUATION_ALLOCATIONS)
    };
    
    if (!newResult.brigades) newResult.brigades = [];
    newResult.brigades.push(newBrigade);

    // BUG-NEXT-011 FIX: Manually added brigades had no battalions.
    // Generate default battalion set from the brigade's unit_type template.
    // Uses getBattalionComposition (same source as ForceService.buildBrigade) +
    // ForceBuilder.buildBattalion for full Role-1 / DNBI setup.
    if (!newResult.battalions) newResult.battalions = [];
    // 2026-10-25: the cards of the type, built as a fresh quick build builds them (the brigade total is the sum of the cards); before, the older
    // doctrinal template with typical battalion strengths was used here.
    try {
      const rebuilt = ForceBuilder.buildBrigadeUnitsForType({
        id: newBrigadeId,
        name: newBrigade.name,
        unit_type: newBrigade.unit_type,
        nationalStandard: brigadeNationalStandard,
        parent_division_id: division.id,
        parent_division_name: division.name,
        parent_corps_name: newResult.corps?.name || null
      });
      newBrigade.brigade_type = newBrigade.unit_type;
      newBrigade.strength = rebuilt.strength;
      newBrigade.sub_units = rebuilt.sub_units;
      newBrigade.composition_source = rebuilt.composition_source;
      newResult.battalions.push(...rebuilt.battalions);
      if (rebuilt.role1_facilities.length > 0) {
        newResult.role1Facilities = [...(newResult.role1Facilities || []), ...rebuilt.role1_facilities];
      }
    } catch (err) {
      console.warn('handleAddBrigade: unit generation failed —', err.message);
      setUnitBuildError('Brigade added but its units could not be built. Review the brigade composition.');
    }

    onUpdate(newResult);
  };

  const handleDeleteBrigade = (brigade) => {
    setShowDeleteConfirm(brigade);
  };
  
  const confirmDeleteBrigade = () => {
    if (!showDeleteConfirm) return;
    
    const newResult = { ...forceBuilderResult };
    newResult.brigades = newResult.brigades?.filter(b => b.id !== showDeleteConfirm.id);
    newResult.battalions = newResult.battalions?.filter(bn => bn.parent_brigade_id !== showDeleteConfirm.id);
    
    onUpdate(newResult);
    setShowDeleteConfirm(null);
  };
  
  // ── Battalion management handlers ────────────────────────────────────────

  const BATTALION_TYPE_OPTIONS = [
    { value: 'MECHANIZED',     label: '🚜 Mechanized Infantry' },
    { value: 'INFANTRY',       label: '🪖 Infantry' },
    { value: 'ARMOR',          label: '🛡️ Armor / Tank' },
    { value: 'RECONNAISSANCE', label: '🔭 Cavalry / Recon' },
    { value: 'ARTILLERY',      label: '💥 Artillery' },
    { value: 'ENGINEERS',      label: '🔧 Engineers' },
    { value: 'AIR_DEFENSE',    label: '🎯 Air Defense' },
    { value: 'AVIATION',       label: '🚁 Aviation' },
    { value: 'SIGNAL',         label: '📡 Signal' },
    { value: 'SUPPLY',         label: '📦 Supply' },
    { value: 'MAINTENANCE',    label: '🔩 Maintenance' },
    { value: 'TRANSPORT',      label: '🚛 Transportation' },
  ];

  const handleAddBattalion = (brigade) => {
    // NOTE: do NOT reset addBattalionType here — keep the last selection so
    // the user doesn't have to re-select the same type when adding multiple
    // battalions of the same kind. The Select still shows 'MECHANIZED' on first
    // open (initial useState value) which is the correct default for a new session.
    setShowAddBattalionDialog(brigade);
  };

  const confirmAddBattalion = () => {
    if (!showAddBattalionDialog) return;
    const brigade   = showAddBattalionDialog;
    const newResult = { ...forceBuilderResult };

    const existingBns = (newResult.battalions || []).filter(
      bn => bn.parent_brigade_id === brigade.id
    );
    const bnNumber  = existingBns.length + 1;
    const typeLabel = BATTALION_TYPES[addBattalionType]?.label || addBattalionType;

    const divisionUnit =
      newResult.division?.id === brigade.parent_division_id
        ? newResult.division
        : newResult.divisions?.find(d => d.id === brigade.parent_division_id);

    try {
      const bnResult = ForceBuilder.buildBattalion({
        name:               `${bnNumber}/${brigade.name} ${typeLabel}`,
        battalion_number:   bnNumber,
        unit_type:          addBattalionType,
        nationalStandard:   brigade.nationalStandard || 'NATO',
        strength:           BATTALION_TYPES[addBattalionType]?.typical_strength || 800,
        position_feba_km:   BATTALION_TYPES[addBattalionType]?.typical_position_km || 8,
        mission:            'SUPPORTING',
        is_frontline:       BATTALION_TYPES[addBattalionType]?.has_frontage ?? true,
        parent_brigade_id:   brigade.id,
        parent_brigade_name: brigade.name,
        parent_division_id:  brigade.parent_division_id,
        parent_division_name: divisionUnit?.name || null,
        skip_role1: false
      });
      // BUG-NEXT-010b FIX: buildBattalion() returns Battalion class instances.
      // All original battalions are plain JSON objects (serialized via formatBuildResult →
      // toJSON()). Mixing class instances with plain objects in the same array causes
      // unpredictable unit_type reads during render. Serialize to plain object first.
      const bnPlain   = bnResult.battalion?.toJSON   ? bnResult.battalion.toJSON()   : bnResult.battalion;
      const role1Plain = bnResult.role1?.toJSON ? bnResult.role1.toJSON() : bnResult.role1;
      newResult.battalions = [...(newResult.battalions || []), bnPlain];
      if (role1Plain) {
        newResult.role1Facilities = [...(newResult.role1Facilities || []), role1Plain];
      }
      // BUG-NEXT-011: Increment parent brigade strength so handleUnitUpdate's
      // brigades[].strength sum reflects the added battalion's manpower.
      // (Targeted delta — does NOT overwrite national-standard brigade strength,
      //  avoiding the BUG-191 regression where sum(bns) replaced the full 5,000.)
      if (newResult.brigades && bnPlain?.strength) {
        newResult.brigades = newResult.brigades.map(b =>
          b.id === brigade.id
            ? { ...b, strength: (b.strength || 0) + bnPlain.strength }
            : b
        );
      }
      onUpdate(newResult);
    } catch (err) {
      console.warn('[confirmAddBattalion] buildBattalion failed:', err.message);
      setUnitBuildError('Battalion build failed — the unit was not added. Check unit type configuration.');
    }
    setShowAddBattalionDialog(null);
  };

  const handleDeleteBattalion = (battalion) => {
    // Guard: cannot delete last battalion in a brigade
    const brigadeCount = (forceBuilderResult.battalions || []).filter(
      bn => bn.parent_brigade_id === battalion.parent_brigade_id
    ).length;
    if (brigadeCount <= 1) return; // silently block — button should be disabled too
    setShowDeleteBattalionConfirm(battalion);
  };

  const confirmDeleteBattalion = () => {
    if (!showDeleteBattalionConfirm) return;
    const bn        = showDeleteBattalionConfirm;
    const newResult = { ...forceBuilderResult };

    // Remove battalion
    newResult.battalions = (newResult.battalions || []).filter(b => b.id !== bn.id);

    // Remove its Role-1 facility
    if (newResult.role1Facilities) {
      newResult.role1Facilities = newResult.role1Facilities.filter(
        r1 => r1.parent_battalion_id !== bn.id
      );
    }

    // BUG-NEXT-011: Decrement parent brigade strength to match the removed battalion.
    // Symmetric with confirmAddBattalion — targeted delta, not a full recalculate.
    if (newResult.brigades && bn.strength) {
      newResult.brigades = newResult.brigades.map(b =>
        b.id === bn.parent_brigade_id
          ? { ...b, strength: Math.max(0, (b.strength || 0) - bn.strength) }
          : b
      );
    }

    // Renumber remaining battalions for this brigade (keep sequential)
    let counter = 1;
    newResult.battalions = newResult.battalions.map(b =>
      b.parent_brigade_id === bn.parent_brigade_id
        ? { ...b, battalion_number: counter++ }
        : b
    );

    onUpdate(newResult);
    setShowDeleteBattalionConfirm(null);
  };

  // Render units based on echelon
  const renderUnits = () => {
    if (echelon === 'CORPS' && forceBuilderResult.corps) {
      return (
        <>
          <EnhancedUnitCard 
            unit={forceBuilderResult.corps} 
            mode={mode} 
            onEdit={handleEditUnit}
            forceBuilderResult={forceBuilderResult}
          />
          {forceBuilderResult.divisions?.map(div => (
            <React.Fragment key={div.id}>
              <EnhancedUnitCard
                unit={div}
                mode={mode}
                onEdit={handleEditUnit}
                onAddBrigade={orbatLocked ? undefined : handleAddBrigade}
                depth={1}
                forceBuilderResult={forceBuilderResult}
              />
              {forceBuilderResult.brigades?.filter(b => b.parent_division_id === div.id).map(bde => (
                <EnhancedUnitCard
                  key={bde.id}
                  unit={bde}
                  mode={mode}
                  onEdit={handleEditUnit}
                  onDelete={orbatLocked ? undefined : handleDeleteBrigade}
                  depth={2}
                  forceBuilderResult={forceBuilderResult}
                />
              ))}
            </React.Fragment>
          ))}
        </>
      );
    }
    
    if (echelon === 'DIVISION' && forceBuilderResult.division) {
      return (
        <>
          <EnhancedUnitCard
            unit={forceBuilderResult.division}
            mode={mode}
            onEdit={handleEditUnit}
            onAddBrigade={orbatLocked ? undefined : handleAddBrigade}
            forceBuilderResult={forceBuilderResult}
          />
          {forceBuilderResult.brigades?.map(bde => (
            // BUG-NEXT-007 FIX: Apply 2-echelon-down rule at DIVISION echelon.
            // Brigades are depth=1; their battalions are depth=2 (same as brigades under corps).
            <React.Fragment key={bde.id}>
              <EnhancedUnitCard
                unit={bde}
                mode={mode}
                onEdit={handleEditUnit}
                onDelete={orbatLocked ? undefined : handleDeleteBrigade}
                onAddBattalion={orbatLocked ? undefined : handleAddBattalion}
                depth={1}
                forceBuilderResult={forceBuilderResult}
              />
              {(forceBuilderResult.battalions || [])
                .filter(bn => bn.parent_brigade_id === bde.id)
                .map(bn => {
                  const brigadeCount = (forceBuilderResult.battalions || [])
                    .filter(b => b.parent_brigade_id === bde.id).length;
                  return (
                    <EnhancedUnitCard
                      key={bn.id}
                      unit={bn}
                      mode={mode}
                      onEdit={handleEditUnit}
                      onDeleteBattalion={orbatLocked || brigadeCount <= 1 ? undefined : handleDeleteBattalion}
                      depth={2}
                      forceBuilderResult={forceBuilderResult}
                    />
                  );
                })}
            </React.Fragment>
          ))}
        </>
      );
    }
    
    if (echelon === 'BRIGADE' && forceBuilderResult.brigade) {
      return (
        <>
          <EnhancedUnitCard
            unit={forceBuilderResult.brigade}
            mode={mode}
            onEdit={handleEditUnit}
            onAddBattalion={orbatLocked ? undefined : handleAddBattalion}
            forceBuilderResult={forceBuilderResult}
          />
          {/* TASK 1.7: Battalion composition summary */}
          <BattalionCompositionSummary battalions={forceBuilderResult.battalions} brigadeStrength={forceBuilderResult.brigade?.strength} />
          {forceBuilderResult.battalions?.map(bn => {
            const brigadeCount = (forceBuilderResult.battalions || []).length;
            return (
              <EnhancedUnitCard
                key={bn.id}
                unit={bn}
                mode={mode}
                onEdit={handleEditUnit}
                onDeleteBattalion={orbatLocked || brigadeCount <= 1 ? undefined : handleDeleteBattalion}
                depth={1}
                forceBuilderResult={forceBuilderResult}
              />
            );
          })}
        </>
      );
    }
    
    if (echelon === 'BATTALION' && forceBuilderResult.battalion) {
      return (
        <EnhancedUnitCard 
          unit={forceBuilderResult.battalion} 
          mode={mode} 
          onEdit={handleEditUnit}
          forceBuilderResult={forceBuilderResult}
        />
      );
    }
    
    return null;
  };
  
  return (
    <>
    {/* INC-009: surface unit-build failures (handleSaveUnit, handleAddBrigade, confirmAddBattalion) */}
    <Snackbar
      open={!!unitBuildError}
      autoHideDuration={8000}
      onClose={() => setUnitBuildError(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" onClose={() => setUnitBuildError(null)} sx={{ width: '100%' }}>
        {unitBuildError}
      </Alert>
    </Snackbar>
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">{t('tab1.unit_config')}</Typography>
          <Chip label={t('tab1.click_to_edit')} size="small" />
          {echelon === 'BRIGADE' && (
            <Chip label={t('tab1.battalion_types_enabled')} size="small" color="success" variant="outlined" />
          )}
        </Stack>

        {renderUnits()}
        
        {/* Edit Dialog */}
        <UnitEditDialog
          open={!!editingUnit}
          onClose={() => setEditingUnit(null)}
          unit={editingUnit}
          mode={mode}
          onSave={handleSaveUnit}
          forceBuilderResult={forceBuilderResult}
        />
        
        {/* Delete Brigade Confirmation */}
        <Dialog open={!!showDeleteConfirm} onClose={() => setShowDeleteConfirm(null)}>
          <DialogTitle>{t('tab1.confirm_delete')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              Delete <strong>{showDeleteConfirm?.name}</strong>?
              This will also remove all subordinate battalions.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(null)}>{t('common.cancel')}</Button>
            <Button onClick={confirmDeleteBrigade} color="error" variant="contained">{t('tab1.confirm_delete')}</Button>
          </DialogActions>
        </Dialog>

        {/* Add Battalion Dialog */}
        <Dialog open={!!showAddBattalionDialog} onClose={() => setShowAddBattalionDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('tab1.add_battalion')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Adding to: <strong>{showAddBattalionDialog?.name}</strong>
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('tab1.battalion_type')}</InputLabel>
              <Select
                value={addBattalionType}
                label={t('tab1.battalion_type')}
                onChange={(e) => setAddBattalionType(e.target.value)}
              >
                {BATTALION_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddBattalionDialog(null)}>{t('common.cancel')}</Button>
            <Button onClick={confirmAddBattalion} color="success" variant="contained">{t('tab1.add_battalion')}</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Battalion Confirmation */}
        <Dialog open={!!showDeleteBattalionConfirm} onClose={() => setShowDeleteBattalionConfirm(null)}>
          <DialogTitle>{t('tab1.delete_battalion')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              Delete <strong>{getMilitaryDisplayName(showDeleteBattalionConfirm || {})}</strong>?
              Role-1 facility will also be removed.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteBattalionConfirm(null)}>{t('common.cancel')}</Button>
            <Button onClick={confirmDeleteBattalion} color="error" variant="contained">{t('tab1.delete_battalion')}</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
    </>
  );
};

// ============================================================================
// ============================================================================
// NATIONAL STANDARD SORT UTILITY (v4.7.2 FLAG-SORT)
// ============================================================================

/**
 * Sorts Object.entries(NATIONAL_STANDARDS) in preferred display order:
 *   1. MULTINATIONAL (if present)
 *   2. NATO
 *   3. ROMANIAN
 *   4. All remaining nations — alphabetical by label
 *
 * Usage: sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
 *   .filter(...).map(([key, value]) => ...)
 */
const NATION_SORT_PRIORITY = { MULTINATIONAL: 0, NATO: 1, ROMANIAN: 2 };

const sortNationalStandards = (entries) =>
  [...entries].sort(([keyA, valA], [keyB, valB]) => {
    const pa = NATION_SORT_PRIORITY[keyA] ?? 99;
    const pb = NATION_SORT_PRIORITY[keyB] ?? 99;
    if (pa !== pb) return pa - pb;
    return (valA.label || keyA).localeCompare(valB.label || keyB);
  });

// ============================================================================
// FORCE STRUCTURE PREVIEW
// ============================================================================

// ============================================================================
// IMP-011: SUPPORT FORMATIONS PANEL (editable)
// Replaced read-only display with inline-editable unit rows + add/delete.
// Sourced from ForceService v2.14.0 fields; user overrides stored in
// scenario.force.support_overrides and patched onto forceBuilderResult before save.
// ============================================================================

/** Map provides[] to a short Tab3-relevant capability label. */
const getSupportUnitTab3Tag = (provides) => {
  if (!provides || provides.length === 0) return null;
  if (provides.some(p => p === 'role_3' || p === 'role_4')) return 'Role 3';
  if (provides.some(p => p === 'role_2_enhanced')) return 'Role 2E';
  if (provides.some(p => p === 'role_2_basic')) return 'Role 2B';
  if (provides.some(p => p === 'role_1')) return 'Role 1';
  if (provides.some(p => p === 'medevac')) return 'MEDEVAC';
  return null;
};

// Curated capability tag picker for "Add Unit" dialog
const PROVIDES_OPTIONS = [
  { value: 'role_1',            label: 'Role 1 (Aid Station)' },
  { value: 'role_2_basic',      label: 'Role 2B (Medical)' },
  { value: 'role_2_enhanced',   label: 'Role 2E (Surgical)' },
  { value: 'role_3',            label: 'Role 3 (Hospital)' },
  { value: 'medevac',           label: 'MEDEVAC' },
  { value: 'theater_logistics', label: 'Theater Logistics' },
  { value: 'communications',    label: 'Signal / Comms' },
  { value: 'mobility',          label: 'Engineer' },
  { value: 'intelligence',      label: 'Intelligence / ISR' },
  { value: 'fire_support',      label: 'Fire Support' },
  { value: 'shorad',            label: 'Air Defense' },
  { value: 'attack',            label: 'Aviation' },
];

const SupportFormationsPanel = ({
  echelonSupportTroops,
  divisionSupportGroups,
  echelon,
  onUnitUpdate,
  onUnitDelete,
  onUnitAdd,
  onGroupReset,
  hasOverrides,
}) => {
  // Hooks must always be called — early-return comes AFTER all hooks
  const { t } = useTranslation();
  const [addDialog, setAddDialog] = useState({ open: false, groupKey: null });
  const [addForm,   setAddForm]   = useState({ name: '', strength: '', provides: [] });

  if (echelon !== 'CORPS' && echelon !== 'DIVISION') return null;

  const openAddDialog = (groupKey) => {
    setAddForm({ name: '', strength: '', provides: [] });
    setAddDialog({ open: true, groupKey });
  };
  const closeAddDialog  = () => setAddDialog({ open: false, groupKey: null });
  const toggleProvide   = (val) => setAddForm(prev => ({
    ...prev,
    provides: prev.provides.includes(val)
      ? prev.provides.filter(p => p !== val)
      : [...prev.provides, val],
  }));
  const handleAddConfirm = () => {
    if (!addForm.name.trim() || !addForm.strength) return;
    onUnitAdd?.(addDialog.groupKey, {
      name:     addForm.name.trim(),
      strength: parseInt(addForm.strength, 10) || 0,
      provides: addForm.provides,
    });
    closeAddDialog();
  };

  // Fires / AD are maneuver assets — exclude from CSS display at division level
  const NON_CSS_PROVIDES = new Set(['fire_support', 'mlrs', 'atacms', 'shorad', 'c_uas']);
  const cssOnly = (units) => (units || []).filter(
    u => !(u.provides || []).some(p => NON_CSS_PROVIDES.has(p))
  );

  const accordionSx = {
    mb: 0.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300',
    '&:before': { display: 'none' }, boxShadow: 'none',
  };
  const summarySx = { minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } };

  // Render inline-editable unit rows
  const renderEditableRows = (units, groupKey) => {
    if (!units || units.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
          No units. Click + Add to create one.
        </Typography>
      );
    }
    return (
      <Table size="small">
        <TableBody>
          {units.map((unit, i) => {
            const tag     = getSupportUnitTab3Tag(unit.provides);
            const unitKey = unit.code || String(i);
            return (
              <TableRow key={unitKey} sx={{ '&:last-child td': { border: 0 } }}>
                {/* Name — editable */}
                <TableCell sx={{ py: 0.25, minWidth: 150 }}>
                  <TextField
                    variant="standard" size="small" fullWidth
                    value={unit.name}
                    onChange={e => onUnitUpdate?.(groupKey, unitKey, { name: e.target.value })}
                    inputProps={{ style: { fontSize: '0.78rem', padding: '1px 0' } }}
                  />
                </TableCell>
                {/* Strength — editable */}
                <TableCell align="right" sx={{ py: 0.25, width: 90 }}>
                  <TextField
                    variant="standard" size="small" type="number"
                    value={unit.strength}
                    onChange={e => onUnitUpdate?.(groupKey, unitKey, { strength: parseInt(e.target.value, 10) || 0 })}
                    inputProps={{ style: { fontSize: '0.78rem', textAlign: 'right', padding: '1px 0' }, min: 0 }}
                  />
                </TableCell>
                {/* Tab3 tag chip */}
                <TableCell sx={{ py: 0.25, width: 80 }}>
                  {tag && (
                    <Chip label={tag} size="small"
                      color={tag.startsWith('Role 3') || tag === 'MEDEVAC' ? 'error' : 'warning'}
                      variant="outlined"
                      sx={{ fontSize: '0.60rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                    />
                  )}
                </TableCell>
                {/* Delete */}
                <TableCell sx={{ py: 0.25, width: 32 }}>
                  <Tooltip title="Remove unit">
                    <IconButton size="small" color="error"
                      onClick={() => onUnitDelete?.(groupKey, unitKey)}>
                      <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  // Accordion header: title + strength chip + MODIFIED badge + Reset + Add buttons
  const renderGroupHeader = (title, strength, groupKey, hasGroupOverride) => (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" fontWeight="medium">{title}</Typography>
      <Chip label={strength.toLocaleString() + ' pers'} size="small" variant="outlined"
        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
      {hasGroupOverride && (
        <Chip label="MODIFIED" size="small" color="warning"
          sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }} />
      )}
      {/* Actions — stopPropagation so accordion doesn't toggle on button click. 2026-10-26: the buttons are spans: the accordion header is already a button (nested button warning) */}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }} onClick={e => e.stopPropagation()}>
        {hasGroupOverride && (
          <Tooltip title={t('tab1.reset_to_defaults_tooltip')}>
            <Button component="span" role="button" tabIndex={0} size="small" color="warning" sx={{ fontSize: '0.65rem', py: 0, px: 0.5, minWidth: 0 }}
              onClick={() => onGroupReset?.(groupKey)}>
              Reset ↺
            </Button>
          </Tooltip>
        )}
        <Tooltip title={t('tab1.add_unit_tooltip')}>
          <Button component="span" role="button" tabIndex={0} size="small" color="primary" sx={{ fontSize: '0.65rem', py: 0, px: 0.5, minWidth: 0 }}
            onClick={() => openAddDialog(groupKey)}>
            {t('tab1.add_unit_btn')}
          </Button>
        </Tooltip>
      </Box>
    </Stack>
  );

  // ── CORPS: Corps Support Group + per-Division Support Groups ───────────────
  if (echelon === 'CORPS') {
    const corpsUnits    = echelonSupportTroops?.organic_units || [];
    const corpsStrength = echelonSupportTroops?.total_organic_strength || 0;
    const divGroups     = divisionSupportGroups || [];

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('tab1.organic_support_editable')}{hasOverrides && ` ${t('tab1.overrides_active')}`}
        </Typography>

        {/* Corps Support Group */}
        <Accordion disableGutters defaultExpanded={false} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            {renderGroupHeader(t('tab1.corps_support_group'), corpsStrength, 'corps',
              hasOverrides)}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {renderEditableRows(corpsUnits, 'corps')}
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Units in CSA (40–100 km from FEBA). Casualties bypass Role 2B — direct to Role 3.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Per-Division Support Groups */}
        {divGroups.map((divGroup, i) => {
          const divCssUnits   = cssOnly(divGroup.organic_units);
          const divCssStrength = divCssUnits.reduce((s, u) => s + (u.strength || 0), 0);
          const divKey        = divGroup.division_id || String(i);
          return (
            <Accordion key={divKey} disableGutters defaultExpanded={false} sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                {renderGroupHeader(
                  t('tab1.div_support_group_name', { divName: divGroup.division_name }),
                  divCssStrength, divKey, hasOverrides
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {renderEditableRows(divCssUnits, divKey)}
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  {t('tab1.nationality_units_in_dsa', { nat: divGroup.national_standard })}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Add Unit dialog */}
        <Dialog open={addDialog.open} onClose={closeAddDialog} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1, fontSize: '0.95rem' }}>{t('tab1.add_support_unit_title')}</DialogTitle>
          <DialogContent>
            <TextField label={t('tab1.unit_name')} fullWidth size="small" sx={{ mt: 1, mb: 1.5 }} autoFocus required
              placeholder='e.g. "Medical Regiment", "CIMIC Battalion"'
              value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} />
            <TextField label={t('tab1.strength_personnel')} fullWidth size="small" type="number"
              sx={{ mb: 1.5 }} value={addForm.strength} inputProps={{ min: 0 }} required
              onChange={e => setAddForm(p => ({ ...p, strength: e.target.value }))} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {t('tab1.capability_tags_hint')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {PROVIDES_OPTIONS.map(opt => (
                <Chip key={opt.value} label={opt.label} size="small" clickable
                  color={addForm.provides.includes(opt.value) ? 'primary' : 'default'}
                  variant={addForm.provides.includes(opt.value) ? 'filled' : 'outlined'}
                  onClick={() => toggleProvide(opt.value)}
                  sx={{ fontSize: '0.65rem', height: 22 }} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAddDialog}>{t('tab1.cancel')}</Button>
            <Button variant="contained" onClick={handleAddConfirm}
              disabled={!addForm.name.trim() || !addForm.strength}>
              {t('tab1.add_unit_confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── DIVISION: single division support group ────────────────────────────────
  if (echelon === 'DIVISION') {
    const divCssUnits    = cssOnly(echelonSupportTroops?.organic_units);
    const divCssStrength = divCssUnits.reduce((s, u) => s + (u.strength || 0), 0);

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('tab1.organic_support_editable')}{hasOverrides && ` ${t('tab1.overrides_active')}`}
        </Typography>
        <Accordion disableGutters defaultExpanded={false} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            {renderGroupHeader(t('tab1.division_support_group'), divCssStrength, 'corps', hasOverrides)}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {renderEditableRows(divCssUnits, 'corps')}
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              {t('tab1.units_in_dsa_role2e')}
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Add Unit dialog */}
        <Dialog open={addDialog.open} onClose={closeAddDialog} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1, fontSize: '0.95rem' }}>{t('tab1.add_support_unit_title')}</DialogTitle>
          <DialogContent>
            <TextField label={t('tab1.unit_name')} fullWidth size="small" sx={{ mt: 1, mb: 1.5 }} autoFocus required
              placeholder='e.g. "Medical Regiment", "CIMIC Battalion"'
              value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} />
            <TextField label={t('tab1.strength_personnel')} fullWidth size="small" type="number"
              sx={{ mb: 1.5 }} value={addForm.strength} inputProps={{ min: 0 }} required
              onChange={e => setAddForm(p => ({ ...p, strength: e.target.value }))} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {t('tab1.capability_tags_hint')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {PROVIDES_OPTIONS.map(opt => (
                <Chip key={opt.value} label={opt.label} size="small" clickable
                  color={addForm.provides.includes(opt.value) ? 'primary' : 'default'}
                  variant={addForm.provides.includes(opt.value) ? 'filled' : 'outlined'}
                  onClick={() => toggleProvide(opt.value)}
                  sx={{ fontSize: '0.65rem', height: 22 }} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAddDialog}>{t('tab1.cancel')}</Button>
            <Button variant="contained" onClick={handleAddConfirm}
              disabled={!addForm.name.trim() || !addForm.strength}>
              {t('tab1.add_unit_confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return null;
};

const ForceStructurePreview = ({
  forceBuilderResult, echelon,
  // IMP-011: support formations editing callbacks
  echelonSupportTroops, divisionSupportGroups,
  onUnitUpdate, onUnitDelete, onUnitAdd, onGroupReset, hasOverrides,
}) => {
  const { t } = useTranslation();

  if (!forceBuilderResult) return null;
  
  const getSummary = () => {
    // ARCH-012 FIX: single uniform function — no echelon branching.
    // Reads whatever is present in forceBuilderResult and counts/sums it.
    // Works for all echelons without modification. Adding a new echelon
    // never requires a new branch here.
    const r = forceBuilderResult;

    const corpsCount      = r.corps     ? 1 : 0;
    const divisionCount   = (r.divisions?.length || 0) + (r.division  ? 1 : 0);
    const brigadeCount    = (r.brigades?.length  || 0) + (r.brigade   ? 1 : 0);
    const battalionCount  = (r.battalions?.length || 0) + (r.battalion ? 1 : 0);

    // Aggregate all battalions across all paths for weighted modifier
    const allBattalions = [
      ...(r.battalions || []),
      ...(r.battalion  ? [r.battalion] : [])
    ];
    const bnSummary = calculateBattalionCompositionSummary(allBattalions);

    const result = {
      strength: r.total_strength || 0
    };
    if (corpsCount     > 0) result.corps     = corpsCount;
    if (divisionCount  > 0) result.divisions  = divisionCount;
    if (brigadeCount   > 0) result.brigades   = brigadeCount;
    if (battalionCount > 0) {
      result.battalions       = battalionCount;
      result.combatBattalions = bnSummary.combat;
      result.supportBattalions = bnSummary.support;
      // ENH-GAP-001: show weighted modifier for any echelon that has battalions
      result.weightedModifier = bnSummary.weightedModifier;
    }
    return result;
  };
  
  const summary = getSummary();
  
  return (
    <Card sx={{ mb: 3, bgcolor: 'success.light' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" color="success.dark">
            {t('tab1.force_structure_created')}
          </Typography>
          {echelon === 'BRIGADE' && (
            <Chip label={t('tab1.mixed_battalion_chip')} size="small" color="success" />
          )}
        </Stack>
        
        <Grid container spacing={2}>
          {/* v4.3.6: Display top-level echelon count */}
          {summary.corps !== undefined && (
            <Grid size={{ xs: 3 }}>
              <Typography variant="h4" color="success.dark">{summary.corps}</Typography>
              <Typography variant="caption">{t('tab1.stat_corps')}</Typography>
            </Grid>
          )}
          {summary.divisions !== undefined && (
            <Grid size={{ xs: 3 }}>
              <Typography variant="h4" color="success.dark">{summary.divisions}</Typography>
              <Typography variant="caption">{t('tab1.stat_divisions')}</Typography>
            </Grid>
          )}
          {summary.brigades !== undefined && (
            <Grid size={{ xs: 3 }}>
              <Typography variant="h4" color="success.dark">{summary.brigades}</Typography>
              <Typography variant="caption">{t('tab1.stat_brigades')}</Typography>
            </Grid>
          )}
          {summary.battalions !== undefined && (
            <Grid size={{ xs: 3 }}>
              <Typography variant="h4" color="success.dark">{summary.battalions}</Typography>
              <Typography variant="caption">{t('tab1.stat_battalions')}</Typography>
              {summary.combatBattalions !== undefined && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {t('tab1.stat_combat_support', { combat: summary.combatBattalions, support: summary.supportBattalions })}
                </Typography>
              )}
            </Grid>
          )}
          <Grid size={{ xs: 3 }}>
            <Typography variant="h4" color="success.dark">{summary.strength?.toLocaleString()}</Typography>
            <Typography variant="caption">{t('tab1.stat_personnel')}</Typography>
            {summary.weightedModifier !== undefined && (
              <Typography variant="caption" display="block" color="text.secondary">
                {t('tab1.stat_weighted', { mod: summary.weightedModifier.toFixed(2) })}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* IMP-011: Support Formations — CORPS and DIVISION only — now editable */}
        {(echelon === 'CORPS' || echelon === 'DIVISION') && (
          <SupportFormationsPanel
            echelonSupportTroops={echelonSupportTroops}
            divisionSupportGroups={divisionSupportGroups}
            echelon={echelon}
            onUnitUpdate={onUnitUpdate}
            onUnitDelete={onUnitDelete}
            onUnitAdd={onUnitAdd}
            onGroupReset={onGroupReset}
            hasOverrides={hasOverrides}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// QUICK BUILD PANEL (Enhanced with Force Quality)
// ============================================================================

const QuickBuildPanel = ({ echelon, nationalStandard, corpsLeadNation, divisionStandards, brigadeStandards, numDivisions, numBrigades, onBuild, onForceQualityChange, forceQuality, scenario, orbatLocked = false }) => {
  const { t } = useTranslation();
  const [quickConfig, setQuickConfig] = useState({
    name: '',
    strength: 0,
    unit_type: 'MECHANIZED',  // BUG-172: required by buildBrigade — was never in quickConfig
    battalions: 3,
    brigades: 3,
    divisions: 2,
    battalions_per_brigade: 3,
    brigades_per_division: 3
  });
  // BUG-174c FIX: ref always holds latest quickConfig so handleQuickBuild
  // never reads a stale closure capture of unit_type (root cause of MECHANIZED always building).
  const quickConfigRef = useRef(quickConfig);
  useEffect(() => { quickConfigRef.current = quickConfig; }, [quickConfig]);
  // BUG-172: surface build errors that were previously swallowed silently
  const [quickBuildError, setQuickBuildError] = useState(null);

  useEffect(() => {
    if (echelon && nationalStandard) {
      if (nationalStandard === 'MULTINATIONAL') {
        setQuickConfig({
          name: `1st ${echelon}`,
          strength: 0,
          battalions: 3,
          brigades: 3,
          divisions: 2,
          battalions_per_brigade: 3,
          brigades_per_division: 3
        });
        return;
      }
      
      const standard = NATIONAL_STANDARDS[nationalStandard];
      // BUG-180 FIX: for BATTALION echelon, display strength depends on battalion type.
      // MANEUVER types use national standard battalion strength; CS/CSS types use their
      // doctrinal typical_strength. Old check (category === 'CSS' || 'SUPPORT') excluded
      // FIRES/ADA which have has_frontage=false but are not CSS — they got national standard
      // incorrectly. Fix: use BATTALION_MANEUVER_TYPES list, matching ForceService v3.3.3.
      const getNatBatStrength = (unitType) => {
        const typeConfig = BATTALION_TYPES[unitType];
        if (BATTALION_MANEUVER_TYPES.includes(unitType)) {
          return standard.battalion?.strength || standard[echelon.toLowerCase()]?.strength || 0;
        }
        return typeConfig?.typical_strength || standard.battalion?.strength || 0;
      };
      setQuickConfig(prev => ({
        ...prev,
        name: `1st ${standard[echelon.toLowerCase()].defaultName}`,
        strength: echelon === FORCE_ECHELONS.BATTALION
          ? getNatBatStrength(prev.unit_type)
          : standard[echelon.toLowerCase()].strength,
        battalions: 3,
        brigades: standard.division?.brigades || 3,
        divisions: standard.corps?.divisions || 2,
        battalions_per_brigade: 3,
        brigades_per_division: standard.division?.brigades || 3
        // unit_type preserved from prev — user selection survives echelon/nation change
      }));
    }
  }, [echelon, nationalStandard]);

  // BUG FIX (Feb 5, 2026): Validate all required flags are selected before allowing Quick Build
  // Option B: Disable button proactively instead of showing error after click
  // FIX v4.2.1: Use numDivisions/numBrigades instead of quickConfig values
  // BUG-071 FIX (Mar 12, 2026): Non-MULTINATIONAL CORPS (Romanian/US/NATO) do not use
  // per-division flag selectors — divisionStandards is always {} for them, causing the
  // loop to always return false and permanently disabling the Quick Build button.
  // Short-circuit: only check divisionStandards when nationalStandard === 'MULTINATIONAL'.
  const allFlagsSelected = useMemo(() => {
    if (echelon === FORCE_ECHELONS.CORPS) {
      if (nationalStandard !== 'MULTINATIONAL') {
        // Romanian/US/NATO corps: standard already selected — no per-division flags needed
        return !!nationalStandard;
      }
      // MULTINATIONAL corps: every division slot must have a flag selected
      // FIX: Use numDivisions (actual UI state) instead of quickConfig.divisions
      for (let i = 1; i <= numDivisions; i++) {
        if (!divisionStandards[i]) return false;
      }
      return true;
    }
    
    if (echelon === FORCE_ECHELONS.DIVISION) {
      if (nationalStandard !== 'MULTINATIONAL') {
        return !!nationalStandard;
      }
      // MULTINATIONAL division: every brigade slot must have a flag selected
      // FIX: Use numBrigades (actual UI state) instead of quickConfig.brigades
      for (let i = 1; i <= numBrigades; i++) {
        if (!brigadeStandards[i]) return false;
      }
      return true;
    }
    
    if (echelon === FORCE_ECHELONS.BRIGADE || echelon === FORCE_ECHELONS.BATTALION) {
      // Check: nationalStandard is selected and not MULTINATIONAL
      return nationalStandard && nationalStandard !== 'MULTINATIONAL';
    }
    
    return true; // Default: allow build
  }, [echelon, numDivisions, numBrigades, divisionStandards, brigadeStandards, nationalStandard]);

  const handleQuickBuild = () => {
    setQuickBuildError(null);
    // BUG-174c FIX: read from ref, not closed-over quickConfig state.
    // quickConfig captured in the closure is stale — unit_type was always 'MECHANIZED'
    // because the closure was created before the user changed the dropdown.
    const cfg = quickConfigRef.current;
    try {
      let result = null;

      if (echelon === FORCE_ECHELONS.BATTALION) {
        const rawResult = ForceBuilder.buildBattalion({
          name: cfg.name,
          unit_type: cfg.unit_type || 'MECHANIZED',
          nationalStandard: nationalStandard,
          scenario: scenario
        });
        // BUG-NEW FIX: buildBattalion() returns { battalion: Battalion_class_instance, ... }.
        // Storing the raw class instance in React state causes safeDeepClone inside
        // UnitEditDialog to produce a plain-object clone whose id comparison against
        // newResult.battalion.id (class instance) silently fails in handleSaveUnit —
        // the Battalion was never updated, always reverting to MECHANIZED on save.
        // formatBuildResult calls battalion.toJSON() → plain object → reliable comparison.
        result = ForceBuilder.formatBuildResult('BATTALION', rawResult);
      } else if (echelon === FORCE_ECHELONS.BRIGADE) {
        result = ForceBuilder.buildBrigade({
          name: cfg.name,
          unit_type: cfg.unit_type,  // BUG-172 FIX
          strength: cfg.strength,
          battalions: cfg.battalions,
          nationalStandard: nationalStandard === 'MULTINATIONAL' ? 'NATO' : nationalStandard,
          scenario: scenario
        });
      } else if (echelon === FORCE_ECHELONS.DIVISION) {
        result = ForceBuilder.buildDivision({
          name: cfg.name,
          brigades: nationalStandard === 'MULTINATIONAL' ? numBrigades : cfg.brigades,
          battalions_per_brigade: cfg.battalions_per_brigade,
          nationalStandard: nationalStandard === 'MULTINATIONAL' ? 'NATO' : nationalStandard,
          brigadeStandards: nationalStandard === 'MULTINATIONAL' ? brigadeStandards : {},
          scenario: scenario
        });
      } else if (echelon === FORCE_ECHELONS.CORPS) {
        result = ForceBuilder.buildCorps({
          name: cfg.name,
          divisions: nationalStandard === 'MULTINATIONAL' ? numDivisions : cfg.divisions,
          brigades_per_division: cfg.brigades_per_division,
          battalions_per_brigade: cfg.battalions_per_brigade,
          nationalStandard: nationalStandard === 'MULTINATIONAL' ? 'NATO' : nationalStandard,
          divisionStandards: nationalStandard === 'MULTINATIONAL' ? divisionStandards : {},
          corpsLeadNation: nationalStandard === 'MULTINATIONAL' ? (corpsLeadNation || null) : null,
          scenario: scenario
        });
      }

      if (result && onBuild) {
        // Add force quality to top-level unit
        if (result.corps) result.corps.force_quality = { ...forceQuality };
        if (result.division) result.division.force_quality = { ...forceQuality };
        if (result.brigade) result.brigade.force_quality = { ...forceQuality };
        if (result.battalion) result.battalion.force_quality = { ...forceQuality };
        
        onBuild(result);
      }
    } catch (error) {
      console.error('Quick build error:', error);
      setQuickBuildError(error.message || 'Build failed — check console for details');
    }
  };

  return (
    <Card sx={{ mb: 3, border: '2px solid #4caf50' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <QuickBuildIcon color="success" />
          <Typography variant="h6" color="success.main">
            {t('tab1.qb_title')}
          </Typography>
          <Chip label="ENHANCED v2.7" color="success" size="small" />
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab1.qb_alert_title')}</AlertTitle>
          {t('tab1.qb_alert_body')}
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('tab1.qb_force_name')}
              value={quickConfig.name}
              onChange={(e) => setQuickConfig({ ...quickConfig, name: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {(nationalStandard === 'MULTINATIONAL' || echelon === FORCE_ECHELONS.CORPS || echelon === FORCE_ECHELONS.DIVISION || (BRIGADE_CARDS_ON && echelon === FORCE_ECHELONS.BRIGADE && !!BRIGADE_CARD_TEMPLATES[quickConfig.unit_type])) ? (
              // BUG-072 FIX (Mar 12, 2026): Corps and Division strength is computed by ForceService
              // from subordinate unit national standards — quickConfig.strength is set once from
              // NATIONAL_STANDARDS at mount and never recalculates when numDivisions changes.
              // It is also deliberately not passed to buildCorps()/buildDivision() (commented out).
              // Displaying a stale editable value misleads the user. Show disabled informational field.
              <TextField
                fullWidth
                label={t('tab1.qb_total_strength')}
                value={t('tab1.qb_calculated')}
                disabled
                helperText={
                  echelon === FORCE_ECHELONS.CORPS
                    ? t('tab1.qb_strength_corps')
                    : echelon === FORCE_ECHELONS.DIVISION
                      ? t('tab1.qb_strength_division')
                      : (echelon === FORCE_ECHELONS.BRIGADE && nationalStandard !== 'MULTINATIONAL')
                        ? t('tab1.qb_strength_cards')
                        : t('tab1.qb_strength_multi')
                }
              />
            ) : (
              <TextField
                fullWidth
                type="number"
                label={t('tab1.qb_total_strength')}
                value={quickConfig.strength}
                onChange={(e) => setQuickConfig({ ...quickConfig, strength: parseInt(e.target.value) })}
                helperText={t('tab1.qb_strength_default', { n: (NATIONAL_STANDARDS[nationalStandard]?.[echelon?.toLowerCase()]?.strength || 0).toLocaleString() })}
                // UI-FIX: Hide native spinner arrows for consistency with +/- button fields
                sx={{
                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
              />
            )}
          </Grid>

          {echelon === FORCE_ECHELONS.BATTALION && (
            <Grid size={{ xs: 12, md: 6 }}>
              {/* BUG-174 FIX: unit_type selector — was missing, buildBattalion always
                  defaulted to MECHANIZED regardless of selection. Same class as BUG-172. */}
              <FormControl fullWidth>
                <InputLabel>{t('tab1.battalion_type')}</InputLabel>
                <Select
                  value={quickConfig.unit_type || 'MECHANIZED'}
                  label={t('tab1.battalion_type')}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const typeConfig = BATTALION_TYPES[newType];
                    const natStrength = NATIONAL_STANDARDS[nationalStandard]?.battalion?.strength;
                    // BUG-179 FIX: CS/CSS types use doctrinal typical_strength; MANEUVER types
                    // use national standard. Old check (category === 'CSS' || 'SUPPORT') treated
                    // FIRES/ADA as maneuver — wrong. Fix: BATTALION_MANEUVER_TYPES list.
                    const newStrength = BATTALION_MANEUVER_TYPES.includes(newType)
                      ? (natStrength || typeConfig?.typical_strength || 0)
                      : (typeConfig?.typical_strength || natStrength || 0);
                    const newConfig = { ...quickConfigRef.current, unit_type: newType, strength: newStrength };
                    quickConfigRef.current = newConfig; // sync ref immediately — don't wait for useEffect
                    setQuickConfig(newConfig);
                  }}
                >
                  {Object.values(BATTALION_TYPES).map(bt => (
                    <MenuItem key={bt.value} value={bt.value}>
                      {bt.icon ? `${bt.icon} ` : ''}{bt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {echelon === FORCE_ECHELONS.BRIGADE && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* BUG-172 FIX: unit_type selector — was missing, buildBrigade threw silently */}
                <FormControl fullWidth>
                  <InputLabel>{t('tab1.brigade_type')}</InputLabel>
                  <Select
                    value={quickConfig.unit_type || 'MECHANIZED'}
                    label={t('tab1.brigade_type')}
                    onChange={(e) => {
                      const newConfig = { ...quickConfigRef.current, unit_type: e.target.value };
                      quickConfigRef.current = newConfig;
                      setQuickConfig(newConfig);
                    }}
                  >
                    {getAvailableBrigadeTypes(nationalStandard).map(typeKey => {
                      const typeDef = UNIT_TYPES_FROM_CONSTANTS[typeKey];
                      return (
                        <MenuItem key={typeKey} value={typeKey}>
                          {typeDef?.icon ? `${typeDef.icon} ` : ''}{brigadeTypeLabel(typeKey, nationalStandard, typeDef?.label || typeKey)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>{t('tab1.brigade_type_note')}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* 2026-10-25 (Dr. Tatar): with the brigade cards on, the builder gives the brigade all the cards of its type and ignores this number
                    (3 selected, 8 cards built). The field shows the real number and is locked; battalions are added or removed after the build. */}
                {(BRIGADE_CARDS_ON && !!BRIGADE_CARD_TEMPLATES[quickConfig.unit_type]) ? (
                  <TextField
                    fullWidth
                    label={t('tab1.qb_num_battalions')}
                    value={BRIGADE_CARD_TEMPLATES[quickConfig.unit_type].length}
                    disabled
                    helperText={t('tab1.qb_battalions_cards')}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab1.qb_num_battalions')}
                    value={quickConfig.battalions}
                    onChange={(e) => setQuickConfig({ ...quickConfig, battalions: parseInt(e.target.value) || 3 })}
                    inputProps={{ min: 3, max: 5, style: { textAlign: 'center' } }}
                  />
                )}
              </Grid>
            </>
          )}

          {echelon === FORCE_ECHELONS.DIVISION && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab1.qb_num_brigades')}
                  value={nationalStandard === 'MULTINATIONAL' ? numBrigades : quickConfig.brigades}
                  onChange={(e) => setQuickConfig({ ...quickConfig, brigades: parseInt(e.target.value) || 3 })}
                  disabled={orbatLocked || nationalStandard === 'MULTINATIONAL'}
                  inputProps={{ min: 3, max: 5, style: { textAlign: 'center' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* 2026-10-25: the brigades of a division or corps are built with the cards of their type; this number is not used then */}
                {BRIGADE_CARDS_ON ? (
                  <TextField
                    fullWidth
                    label={t('tab1.qb_batts_per_bde')}
                    value={t('tab1.qb_calculated')}
                    disabled
                    helperText={t('tab1.qb_battalions_cards_each')}
                  />
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab1.qb_batts_per_bde')}
                    value={quickConfig.battalions_per_brigade}
                    onChange={(e) => setQuickConfig({ ...quickConfig, battalions_per_brigade: parseInt(e.target.value) || 3 })}
                    inputProps={{ min: 3, max: 5, style: { textAlign: 'center' } }}
                  />
                )}
              </Grid>
            </>
          )}

          {echelon === FORCE_ECHELONS.CORPS && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab1.qb_num_divisions')}
                  value={nationalStandard === 'MULTINATIONAL' ? numDivisions : quickConfig.divisions}
                  onChange={(e) => setQuickConfig({ ...quickConfig, divisions: parseInt(e.target.value) || 2 })}
                  disabled={orbatLocked || nationalStandard === 'MULTINATIONAL'}
                  inputProps={{ min: 2, max: 4, style: { textAlign: 'center' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tab1.qb_bdes_per_div')}
                  value={quickConfig.brigades_per_division}
                  onChange={(e) => setQuickConfig({ ...quickConfig, brigades_per_division: parseInt(e.target.value) || 3 })}
                  inputProps={{ min: 3, max: 5, style: { textAlign: 'center' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                {/* 2026-10-25: the brigades of a division or corps are built with the cards of their type; this number is not used then */}
                {BRIGADE_CARDS_ON ? (
                  <TextField
                    fullWidth
                    label={t('tab1.qb_batts_per_bde')}
                    value={t('tab1.qb_calculated')}
                    disabled
                    helperText={t('tab1.qb_battalions_cards_each')}
                  />
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('tab1.qb_batts_per_bde')}
                    value={quickConfig.battalions_per_brigade}
                    onChange={(e) => setQuickConfig({ ...quickConfig, battalions_per_brigade: parseInt(e.target.value) || 3 })}
                    inputProps={{ min: 3, max: 5, style: { textAlign: 'center' } }}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>

        {/* Force Quality Settings */}
        <Box sx={{ mt: 3 }}>
          <ForceQualityPanel
            forceQuality={forceQuality}
            onChange={onForceQualityChange}
            level={echelon}
            expanded={false}
          />
        </Box>

        {quickBuildError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setQuickBuildError(null)}>
            {quickBuildError}
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          onClick={handleQuickBuild}
          disabled={orbatLocked || !allFlagsSelected}
          startIcon={<QuickBuildIcon />}
          sx={{ mt: 2 }}
        >
          {t('tab1.qb_build_btn')}
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// INITIALIZATION HELPERS
// ============================================================================

const initializeTacticalInParent = (unit, unitIndex, totalUnits, echelon, nationalStandard = 'NATO') => {
  const position = calculateUnitPosition(unitIndex, totalUnits);

  // BUG-173b FIX: posture must reflect unit type, not always default to combat posture.
  // CSS/support battalion types (LOGISTICS, SIGNAL, MEDICAL, etc.) inherited
  // DELIBERATE_DEFENSE from the parent brigade, producing "Invalid posture" alerts.
  // translatePostureForUnitType returns SUPPORT_OPERATIONS for CSS types,
  // DELIBERATE_DEFENSE for combat types (already valid).
  const unitType = unit?.unit_type;
  const rawDefaultPosture = unitType
    ? translatePostureForUnitType(unitType, 'DELIBERATE_DEFENSE')
    : 'DELIBERATE_DEFENSE';
  const defaultPostureMult = rawDefaultPosture === 'DELIBERATE_DEFENSE' ? 0.70
    : rawDefaultPosture === 'HASTY_DEFENSE' ? 1.0
    : 0.30; // SUPPORT_OPERATIONS / RESERVE

  return {
    ...position,
    mission: 'SUPPORTING',
    mission_multiplier: 1.0,
    posture: rawDefaultPosture,
    posture_multiplier: defaultPostureMult,
    medical_operational: true,
    medical_delay_hours: 0,
    mtf_deployment_hours: 2
  };
};

const initializeEvacuationAssets = (unitType, echelon) => {
  return calculateEvacuationAssets(unitType || 'MECHANIZED', echelon, BASE_EVACUATION_ALLOCATIONS);
};

const initializeEnhancedMedicalFacility = (unit, mtfRole) => {
  const hqPosition = unit.position_feba_km || 5;
  const offset = getDefaultOffset(mtfRole);
  
  return {
    id: `${unit.id}_${mtfRole.toLowerCase()}`,
    role: mtfRole,
    type: getMTFTypeName(mtfRole),
    parent_unit_id: unit.id,
    default_position_feba_km: hqPosition + offset,
    offset_from_hq_km: offset,
    user_positioned: false,
    operational: unit.tactical_in_parent?.medical_operational !== false,
    deployment_hours: unit.tactical_in_parent?.mtf_deployment_hours || 2,
    default_capacity: getDefaultCapacity(mtfRole)
  };
};

const buildTacticalHierarchy = (unit, forceBuilderResult) => {
  const hierarchy = {};
  
  const findUnitById = (id) => {
    if (!id) return null;
    if (forceBuilderResult.corps?.id === id) return forceBuilderResult.corps;
    if (forceBuilderResult.division?.id === id) return forceBuilderResult.division;
    if (forceBuilderResult.brigade?.id === id) return forceBuilderResult.brigade;
    if (forceBuilderResult.battalion?.id === id) return forceBuilderResult.battalion;
    if (forceBuilderResult.divisions) {
      const div = forceBuilderResult.divisions.find(d => d.id === id);
      if (div) return div;
    }
    if (forceBuilderResult.brigades) {
      const bde = forceBuilderResult.brigades.find(b => b.id === id);
      if (bde) return bde;
    }
    if (forceBuilderResult.battalions) {
      const bn = forceBuilderResult.battalions.find(b => b.id === id);
      if (bn) return bn;
    }
    return null;
  };
  
  const unitType = unit.type?.toUpperCase();
  
  if (unitType === 'BATTALION') {
    if (unit.tactical_in_parent) hierarchy.in_brigade = { ...unit.tactical_in_parent };
    const parentBrigade = findUnitById(unit.parent_brigade_id);
    if (parentBrigade?.tactical_in_parent) {
      hierarchy.in_division = { ...parentBrigade.tactical_in_parent };
      const grandparentDivision = findUnitById(parentBrigade.parent_division_id);
      if (grandparentDivision?.tactical_in_parent) {
        hierarchy.in_corps = { ...grandparentDivision.tactical_in_parent };
      }
    }
  } else if (unitType === 'BRIGADE') {
    if (unit.tactical_in_parent) hierarchy.in_division = { ...unit.tactical_in_parent };
    const parentDivision = findUnitById(unit.parent_division_id);
    if (parentDivision?.tactical_in_parent) {
      hierarchy.in_corps = { ...parentDivision.tactical_in_parent };
    }
  } else if (unitType === 'DIVISION') {
    if (unit.tactical_in_parent) hierarchy.in_corps = { ...unit.tactical_in_parent };
  }
  
  return hierarchy;
};

const enhanceUnit = (unit, index, totalUnits, unitEchelon, forceBuilderResult = null, nationalStandard = 'NATO') => {
  unit.tactical_in_parent = initializeTacticalInParent(unit, index, totalUnits, unitEchelon, nationalStandard);
  
  // Set unit_type based on echelon (CORPS/DIVISION are HQ, others are tactical units)
  if (!unit.unit_type) {
    if (unitEchelon === 'CORPS' || unitEchelon === 'DIVISION') {
      unit.unit_type = 'HQ';  // Headquarters, not a tactical unit type
    } else {
      unit.unit_type = unit.brigade_type || 'MECHANIZED';  // brigades: the type the builder gave (2026-10-24); the default only for a unit that has none
    }
  }
  
  // 2026-10-17: a battalion reads the BATTALION table (the one the calculator reads); getCasualtyModifier() looks in the brigade
  // table first (engineers 0.5 there, 0.8 as a battalion; artillery 0.4 against 0.4, supply and others differ too).
  unit.unit_type_modifier = (unitEchelon === 'BATTALION' && BATTALION_TYPES[unit.unit_type]?.casualty_modifier !== undefined)
    ? BATTALION_TYPES[unit.unit_type].casualty_modifier
    : getCasualtyModifier(unit.unit_type);
  unit.evacuation_assets = initializeEvacuationAssets(unit.unit_type, unitEchelon);
  
  // NEW: Translate posture if unit type doesn't support current posture
  const currentPosture = unit.tactical_in_parent?.posture;
  if (currentPosture && unit.unit_type) {
    const translatedPosture = translatePostureForUnitType(unit.unit_type, currentPosture);
    if (translatedPosture !== currentPosture) {
      unit.tactical_in_parent.posture = translatedPosture;
    }
  }
  
  // SECTION 12: Initialize operational_state for all echelons
  // NEW: Use unit type defaults if available
  if (!unit.operational_state) {
    const unitTypeState = UNIT_TYPES_FROM_CONSTANTS[unit.unit_type]?.operational_state;
    if (unitTypeState) {
      unit.operational_state = unitTypeState;
    } else if (unitEchelon === 'CORPS') {
      unit.operational_state = 'SUPPORT_ACTIVE';  // Corps HQ doesn't engage directly
    } else if (unitEchelon === 'DIVISION') {
      unit.operational_state = 'COMBAT_ENGAGED';  // Divisions typically fighting
    } else {
      unit.operational_state = 'COMBAT_ENGAGED';  // Brigades/Battalions default
    }
  }
  
  // Initialize medical facilities
  const mtfRole = getMTFRoleByEchelon(unitEchelon);
  if (mtfRole) {
    unit.medical_facility = initializeEnhancedMedicalFacility(unit, mtfRole);
  }
  
  return unit;
};

// CLEANUP-002: Hoisted from inside enhanceForceBuilderResult — only uses its parameter
// and module-level buildTacticalHierarchy, so no closure dependency.
const addHierarchyToAllUnits = (result) => {
  if (result.corps) result.corps.tactical_hierarchy = buildTacticalHierarchy(result.corps, result);
  if (result.division) result.division.tactical_hierarchy = buildTacticalHierarchy(result.division, result);
  if (result.brigade) result.brigade.tactical_hierarchy = buildTacticalHierarchy(result.brigade, result);
  if (result.battalion) result.battalion.tactical_hierarchy = buildTacticalHierarchy(result.battalion, result);
  result.divisions?.forEach(div => div.tactical_hierarchy = buildTacticalHierarchy(div, result));
  result.brigades?.forEach(bde => bde.tactical_hierarchy = buildTacticalHierarchy(bde, result));
  result.battalions?.forEach(bn => bn.tactical_hierarchy = buildTacticalHierarchy(bn, result));
};

const enhanceForceBuilderResult = (result, echelon, nationalStandard, divisionStandards = {}, brigadeStandards = {}) => {
  if (!result) return result;
  
  // ============================================================================
  // PASS 1: Enhance top-level singular units
  // ============================================================================
  if (result.corps) {
    enhanceUnit(result.corps, 0, 1, 'CORPS', null, nationalStandard);
  }
  if (result.division) {
    enhanceUnit(result.division, 0, 1, 'DIVISION', null, nationalStandard);
  }
  if (result.brigade) {
    enhanceUnit(result.brigade, 0, 1, 'BRIGADE', null, nationalStandard);
  }
  if (result.battalion) {
    enhanceUnit(result.battalion, 0, 1, 'BATTALION', null, nationalStandard);
  }
  
  // ============================================================================
  // PASS 2: Enhance ALL array items for ALL echelons
  // This is the KEY FIX - previously only ran for CORPS + MULTINATIONAL
  // ============================================================================
  
  // Helper to get battalions per brigade for proper indexing
  const getBattalionsPerBrigade = () => {
    if (!result.brigades?.length || !result.battalions?.length) return 3;
    return Math.ceil(result.battalions.length / result.brigades.length);
  };
  
  // Helper to get brigades per division for proper indexing
  const getBrigadesPerDivision = () => {
    if (!result.divisions?.length || !result.brigades?.length) return 3;
    return Math.ceil(result.brigades.length / result.divisions.length);
  };
  
  const bnsPerBde = getBattalionsPerBrigade();
  const bdesPerDiv = getBrigadesPerDivision();
  
  // Process divisions array (for CORPS echelon)
  if (result.divisions?.length > 0) {
    result.divisions.forEach((div, i) => {
      // Only enhance if not already enhanced (avoid double-processing)
      if (!div.tactical_in_parent) {
        const divStandard = div.nationalStandard || nationalStandard;
        enhanceUnit(div, i, result.divisions.length, 'DIVISION', null, divStandard);
      }
    });
  }
  
  // Process brigades array (for CORPS or DIVISION echelon)
  if (result.brigades?.length > 0) {
    result.brigades.forEach((bde, i) => {
      // Only enhance if not already enhanced
      if (!bde.tactical_in_parent) {
        // Find parent division for standard inheritance
        const parentDiv = result.divisions?.find(d => d.id === bde.parent_division_id);
        const bdeStandard = bde.nationalStandard || parentDiv?.nationalStandard || nationalStandard;
        
        // Calculate proper index within parent
        const indexInParent = parentDiv ? (i % bdesPerDiv) : i;
        const totalInParent = parentDiv ? bdesPerDiv : result.brigades.length;
        
        enhanceUnit(bde, indexInParent, totalInParent, 'BRIGADE', null, bdeStandard);
      }
    });
  }
  
  // Process battalions array (for CORPS, DIVISION, or BRIGADE echelon)
  if (result.battalions?.length > 0) {
    result.battalions.forEach((bn, i) => {
      // Only enhance if not already enhanced
      if (!bn.tactical_in_parent) {
        // Find parent brigade for standard inheritance
        const parentBde = result.brigades?.find(b => b.id === bn.parent_brigade_id);
        const bnStandard = bn.nationalStandard || parentBde?.nationalStandard || nationalStandard;
        
        // Calculate proper index within parent
        const indexInParent = parentBde ? (i % bnsPerBde) : i;
        const totalInParent = parentBde ? bnsPerBde : result.battalions.length;
        
        enhanceUnit(bn, indexInParent, totalInParent, 'BATTALION', null, bnStandard);
      }
    });
  }
  
  // ============================================================================
  // PASS 3: Apply national standards for MULTINATIONAL configurations
  // v4.2.2: ONLY update nationalStandard field - strength already correct from ForceService
  // ============================================================================
  
  if (nationalStandard === 'MULTINATIONAL') {
    
    // CORPS + MULTINATIONAL: Apply divisionStandards
    if (echelon === 'CORPS' && Object.keys(divisionStandards).length > 0) {
      result.divisions?.forEach((div, i) => {
        const divNum = i + 1;
        const selectedStandard = divisionStandards[divNum];
        if (selectedStandard) {
          div.nationalStandard = selectedStandard;
          // v4.2.2 FIX: Don't overwrite strength - ForceService already set correct HQ strength
          // getDefaultStrength returns TOTAL force, but div.strength should be HQ personnel only
        }
      });
      
      // Cascade to brigades based on parent division
      result.brigades?.forEach((bde) => {
        const parentDiv = result.divisions?.find(d => d.id === bde.parent_division_id);
        if (parentDiv?.nationalStandard) {
          bde.nationalStandard = parentDiv.nationalStandard;
          // v4.2.2 FIX: Don't overwrite strength - ForceService already built with correct standard
        }
      });
      
      // Cascade to battalions based on parent brigade
      result.battalions?.forEach((bn) => {
        const parentBde = result.brigades?.find(b => b.id === bn.parent_brigade_id);
        if (parentBde?.nationalStandard) {
          bn.nationalStandard = parentBde.nationalStandard;
          // v4.2.2 FIX: Don't overwrite strength - ForceService already built with correct standard
        }
      });
      
      // BUG FIX (Jan 31, 2026): Don't overwrite corps HQ strength with division totals
      // Corps.strength = Corps HQ personnel (~2000)
      // result.total_strength = Total force strength (sum of brigades)
      
    }
    
    // DIVISION + MULTINATIONAL: Apply brigadeStandards
    if (echelon === 'DIVISION' && Object.keys(brigadeStandards).length > 0) {
      result.brigades?.forEach((bde, i) => {
        const bdeNum = i + 1;
        const selectedStandard = brigadeStandards[bdeNum];
        if (selectedStandard) {
          bde.nationalStandard = selectedStandard;
          // v4.2.2 FIX: Don't overwrite strength - ForceService already built with correct standard
        }
      });
      
      // Cascade to battalions based on parent brigade
      result.battalions?.forEach((bn) => {
        const parentBde = result.brigades?.find(b => b.id === bn.parent_brigade_id);
        if (parentBde?.nationalStandard) {
          bn.nationalStandard = parentBde.nationalStandard;
          // v4.2.2 FIX: Don't overwrite strength - ForceService already built with correct standard
        }
      });
      
      // BUG FIX (Jan 31, 2026): Don't overwrite division HQ strength with brigade totals
      // Division.strength = Division HQ personnel (~1000)
      // result.total_strength = Total force strength (sum of brigades)
      
    }
  }
  
  // ============================================================================
  // PASS 4: Build tactical hierarchies for all units
  // ============================================================================
  addHierarchyToAllUnits(result);

  // BUG-188 FIX (March 24, 2026): total_strength was never set for BATTALION and
  // BRIGADE echelons by ForceService.formatBuildResult. ARCH-012 made getSummary,
  // calcTotalStrength, and calculateTotalStrength all read total_strength exclusively
  // (no per-echelon fallbacks) — so Personnel showed 0 immediately after build for
  // BATTALION and BRIGADE echelons.
  // BUG-192 FIX (March 24, 2026): Removed battalions.reduce() from total_strength formula.
  // At BRIGADE echelon, brigade.strength IS the national-standard total (e.g. 4,800 German
  // Mechanized). Organic battalions are sub-units already counted within that figure — they
  // are NOT additive. Adding battalions.reduce() here caused double-counting on build
  // (4,800 + 3×1,000 = 7,800) while handleUnitUpdate (which correctly omitted this term)
  // showed 4,800 after any edit. Result: Personnel jumped on build then dropped on first save.
  // Fix: formula is now identical to handleUnitUpdate — brigade.strength only, no battalion sum.
  // For CORPS/DIVISION echelons ForceService already sets total_strength before this runs
  // (guard: if !result.total_strength), so this block is a no-op for those echelons.
  if (!result.total_strength) {
    result.total_strength =
      (result.corps?.strength     || 0) +
      (result.division?.strength  || 0) +
      (result.brigade?.strength   || 0) +
      (result.battalion?.strength || 0) +
      (result.divisions?.reduce((s, d)  => s + (d.strength  || 0), 0) || 0) +
      (result.brigades?.reduce((s, b)   => s + (b.strength  || 0), 0) || 0) +
      (result.medical_brigades?.reduce((s, mb) => s + (result.brigades?.some(b => b.id === mb.brigade?.id) ? 0 : (mb.brigade?.strength || 0)), 0) || 0);   // 2026-10-25: a medical brigade in the brigade list is counted once
  }

  return result;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Tab1_ForceComposition = forwardRef(function Tab1_ForceComposition({ scenario, onUpdate, onComplete, mode = 'STANDARD', orbatLocked = false }, ref) {
  const { t } = useTranslation();
  // State
  const [echelon, setEchelon] = useState(scenario?.force?.echelon || null);
  const [nationalStandard, setNationalStandard] = useState(scenario?.force?.nationalStandard || null);
  // ENH-018 Phase F: Lead/Framework Nation for MULTINATIONAL corps support troops.
  // null = use averaged MULTINATIONAL data (planning default, correct per doctrine).
  // Non-null = use that nation's SUPPORT_UNITS_BY_ECHELON.corps entry (FNC concept).
  const [corpsLeadNation, setCorpsLeadNation] = useState(scenario?.force?.corpsLeadNation || null);
  const [divisionStandards, setDivisionStandards] = useState(scenario?.force?.divisionStandards || {});
  const [brigadeStandards, setBrigadeStandards] = useState(scenario?.force?.brigadeStandards || {});
  const [numDivisions, setNumDivisions] = useState(scenario?.force?.numDivisions || 2);
  const [numBrigades, setNumBrigades] = useState(scenario?.force?.numBrigades || 3);
  const [buildMode, setBuildMode] = useState(scenario?.force?.buildMode || BUILD_MODE.QUICK);
  const [forceBuilderResult, setForceBuilderResult] = useState(scenario?.force?.forceBuilderResult || null);
  const [medicalAssets, setMedicalAssets] = useState(scenario?.force?.medicalAssets || null);
  const [errors, setErrors] = useState([]);
  const [isConfigured, setIsConfigured] = useState(scenario?.force?.configured || false);
  const [battalionWarningAccepted, setBattalionWarningAccepted] = useState(false);
  const [showBattalionWarning, setShowBattalionWarning] = useState(false);
  const [warningCheckbox, setWarningCheckbox] = useState(false);
  
  // NEW: Force Quality state
  const [forceQuality, setForceQuality] = useState(
    scenario?.force?.forceQuality || getDefaultForceQuality()
  );

  // IMP-011: user overrides for organic support unit groups.
  // null = use ForceService defaults unchanged.
  // { corps: { units: [...] }, divisions: { [division_id]: { units: [...] } } }
  const [supportOverrides, setSupportOverrides] = useState(
    scenario?.force?.support_overrides || null
  );

  // Stable ref for onUpdate - prevents useEffect infinite loop without eslint-disable
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Toolbar navigation architecture: App.jsx calls flushState() before switching tabs.
  // Tab1 is Pattern A (auto-flush) - useEffect fires on every state change so
  // scenario.force is always current. flushState is a no-op here but the stub
  // must exist so App.jsx can call it uniformly across all tabs.
  useImperativeHandle(ref, () => ({
    flushState: () => {
      // No-op: Tab1 useEffect already keeps scenario.force current on every state change.
      // Full flush happens automatically - nothing buffered to push.
      // BUG-066: markDirty still required — toolbar nav bypasses Save & Continue handler.
      markDirty();
    }
  }));

  // RESET DETECTION: When scenario.force is null, reset all Tab1 state
  useEffect(() => {
    if (scenario?.force === null || scenario?.force === undefined) {
      setEchelon(null);
      setNationalStandard(null);
      setCorpsLeadNation(null);  // ENH-018 Phase F
      setDivisionStandards({});
      setBrigadeStandards({});
      setNumDivisions(2);
      setNumBrigades(3);
      setBuildMode(BUILD_MODE.QUICK);
      setForceBuilderResult(null);
      setMedicalAssets(null);
      setIsConfigured(false);
      setBattalionWarningAccepted(false);
      setForceQuality(getDefaultForceQuality());
      setSupportOverrides(null);
    }
  }, [scenario?.force]);

  // Sync state to scenario
  useEffect(() => {
    // v4.6.1 FIX (Feb 13, 2026): NO FALLBACK - Use ForceService value only
    // ARCHITECTURE: ForceService v3.0.7 is single source of truth for total_strength
    // PREVIOUS BUG: Fallback summed only brigades (24,000), missing Corps+Division HQs (4,200)
    // FIX: Use forceBuilderResult.total_strength directly - no recalculation
    
    const forceData = {
      echelon,
      nationalStandard,
      corpsLeadNation,                // ENH-018 Phase F
      divisionStandards,
      brigadeStandards,
      numDivisions,
      numBrigades,
      buildMode,
      battalionWarningAccepted,
      configured: isConfigured,

      // FIX: Flatten force structure to top level (same as handleComplete)
      brigades: forceBuilderResult?.brigades || [],
      battalions: forceBuilderResult?.battalions || [],
      divisions: forceBuilderResult?.divisions || [],
      corps: forceBuilderResult?.corps || null,
      // DIVISION echelon: formatBuildResult returns .division (singular), not .divisions.
      // Tab2 needs this to initialise divisionConfigs. Without it divisionConfigs = [] always.
      division: forceBuilderResult?.division || null,

      // IMP-011: use patched result so calculator sees user-edited support troops
      forceBuilderResult: patchedForceBuilderResult,
      support_overrides: supportOverrides,

      force_quality: forceQuality,  // BUG-A FIX: Calculator expects snake_case (useEffect auto-save)
      medicalAssets,
      totalStrength: forceBuilderResult?.total_strength || 0
    };

    if (onUpdateRef.current) {
      onUpdateRef.current({ force: forceData });
    }

  // NOTE: patchedForceBuilderResult is intentionally omitted from deps — it is derived from
  // forceBuilderResult + supportOverrides which ARE listed. Including it caused a TDZ crash
  // (const patchedForceBuilderResult is initialised after this useEffect in the function body).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [echelon, nationalStandard, corpsLeadNation, divisionStandards, brigadeStandards, numDivisions, numBrigades, buildMode, forceBuilderResult, supportOverrides, medicalAssets, battalionWarningAccepted, isConfigured, forceQuality]);

  // CLEANUP-003 FIX: was identical echelon-branching function as calcTotalStrength,
  // both with frozen brigade/battalion strength antipattern. After ARCH-012/BUG-187,
  // total_strength is always current for all echelons — read directly, no branching.
  const calculateTotalStrength = () => forceBuilderResult?.total_strength || 0;

  // ── IMP-011: Effective support troops (merges user overrides onto ForceService defaults) ──
  const effectiveSupportTroops = React.useMemo(() => {
    const base = forceBuilderResult?.support_troops;
    if (!base) return null;
    const units = supportOverrides?.corps?.units ?? base.organic_units;
    return {
      ...base,
      organic_units: units,
      total_organic_strength: units.reduce((s, u) => s + (u.strength || 0), 0),
    };
  }, [forceBuilderResult, supportOverrides]);

  const effectiveDivisionSupportGroups = React.useMemo(() => {
    return (forceBuilderResult?.division_support_groups || []).map(dg => {
      const units = supportOverrides?.divisions?.[dg.division_id]?.units ?? dg.organic_units;
      return {
        ...dg,
        organic_units: units,
        total_organic_strength: units.reduce((s, u) => s + (u.strength || 0), 0),
      };
    });
  }, [forceBuilderResult, supportOverrides]);

  // forceBuilderResult patched with effective support troops (used in both save paths)
  const patchedForceBuilderResult = React.useMemo(() => {
    if (!forceBuilderResult) return null;
    if (!supportOverrides) return forceBuilderResult;
    const patched = { ...forceBuilderResult };
    if (effectiveSupportTroops) patched.support_troops = effectiveSupportTroops;
    if (effectiveDivisionSupportGroups.length) {
      patched.division_support_groups = effectiveDivisionSupportGroups;
      patched.division_support_strength = effectiveDivisionSupportGroups
        .reduce((s, g) => s + (g.total_organic_strength || 0), 0);
    }
    return patched;
  }, [forceBuilderResult, supportOverrides, effectiveSupportTroops, effectiveDivisionSupportGroups]);

  // IMP-011 handler helpers
  const _seedOverrides = React.useCallback((prev) => {
    if (prev) return prev;
    return {
      corps: { units: forceBuilderResult?.support_troops?.organic_units ? [...forceBuilderResult.support_troops.organic_units] : [] },
      divisions: Object.fromEntries(
        (forceBuilderResult?.division_support_groups || [])
          .map(g => [g.division_id, { units: [...g.organic_units] }])
      ),
    };
  }, [forceBuilderResult]);

  const _getGroupUnits = (base, groupKey) =>
    groupKey === 'corps'
      ? (base.corps?.units || [])
      : (base.divisions?.[groupKey]?.units || []);

  const _setGroupUnits = (base, groupKey, units) =>
    groupKey === 'corps'
      ? { ...base, corps: { units } }
      : { ...base, divisions: { ...(base.divisions || {}), [groupKey]: { units } } };

  const handleSupportUnitUpdate = React.useCallback((groupKey, unitCode, fields) => {
    setSupportOverrides(prev => {
      const base    = _seedOverrides(prev);
      const units   = _getGroupUnits(base, groupKey);
      const updated = units.map((u, i) =>
        (u.code || String(i)) === unitCode ? { ...u, ...fields } : u
      );
      return _setGroupUnits(base, groupKey, updated);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_seedOverrides]);

  const handleSupportUnitDelete = React.useCallback((groupKey, unitCode) => {
    setSupportOverrides(prev => {
      const base    = _seedOverrides(prev);
      const units   = _getGroupUnits(base, groupKey);
      const updated = units.filter((u, i) => (u.code || String(i)) !== unitCode);
      return _setGroupUnits(base, groupKey, updated);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_seedOverrides]);

  const handleSupportUnitAdd = React.useCallback((groupKey, newUnit) => {
    setSupportOverrides(prev => {
      const base    = _seedOverrides(prev);
      const units   = _getGroupUnits(base, groupKey);
      const unit    = { ...newUnit, code: `custom_${Date.now()}` };
      return _setGroupUnits(base, groupKey, [...units, unit]);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_seedOverrides]);

  const handleSupportGroupReset = React.useCallback((groupKey) => {
    setSupportOverrides(prev => {
      if (!prev) return null;
      let next;
      if (groupKey === 'corps') {
        next = { ...prev, corps: undefined };
      } else {
        const nextDivs = { ...(prev.divisions || {}) };
        delete nextDivs[groupKey];
        next = { ...prev, divisions: nextDivs };
      }
      // Collapse to null if nothing remains
      const hasCorps = !!next.corps;
      const hasDivs  = Object.keys(next.divisions || {}).length > 0;
      return (hasCorps || hasDivs) ? next : null;
    });
  }, []);

  // IMP-001: Unlock ORBAT — clears CoA A from localStorage and fires unlock event
  const handleUnlockOrbat = () => {
    if (!window.confirm('Unlock ORBAT? This will clear your saved CoA A and invalidate the CoA comparison. Continue?')) return;
    try { localStorage.removeItem('psp_cre_coa_1'); } catch (_) {}
    window.dispatchEvent(new CustomEvent('psp:orbatUnlocked'));
  };

  const handleEchelonChange = (e) => {
    const newEchelon = e.target.value;
    if (newEchelon === FORCE_ECHELONS.BATTALION) {
      setShowBattalionWarning(true);
    } else {
      setEchelon(newEchelon);
      setForceBuilderResult(null);
      setDivisionStandards({});
      setBrigadeStandards({});
      setSupportOverrides(null); // IMP-011: new force structure → reset support overrides
      if ((newEchelon === 'BATTALION' || newEchelon === 'BRIGADE') && nationalStandard === 'MULTINATIONAL') {
        setNationalStandard(null);
      }
    }
  };

  const handleNationalStandardChange = (e) => {
    const newStandard = e.target.value;
    setNationalStandard(newStandard);
    setCorpsLeadNation(null);          // ENH-018 Phase F: clear lead nation when standard changes
    setForceBuilderResult(null);
    setSupportOverrides(null);         // IMP-011: new national standard → reset support overrides
    if (newStandard !== 'MULTINATIONAL') {
      setDivisionStandards({});
      setBrigadeStandards({});
    }
  };

  const handleBattalionWarningAccept = () => {
    setEchelon(FORCE_ECHELONS.BATTALION);
    setBattalionWarningAccepted(true);
    setShowBattalionWarning(false);
    setWarningCheckbox(false);
  };

  const handleQuickBuild = (result) => {
    const enhanced = enhanceForceBuilderResult(result, echelon, nationalStandard,
divisionStandards, brigadeStandards);
    // BUG-S109-002 FIX: Stamp the user-selected national standard onto the top-level result.
    // ForceService.buildCorps receives 'NATO' for MULTINATIONAL forces (architecture: NATO
    // baseline for HQ calculations), so corps.nationalStandard = 'NATO' internally.
    // The isMultinationalForce chip check reads forceBuilderResult?.nationalStandard — without
    // this stamp it is always undefined, so CORPS chip always showed "NATO Standard".
    // Persists through shallow copies in handleUnitUpdate and through the auto-save → reload cycle
    // (auto-save stores patchedForceBuilderResult which spreads forceBuilderResult, so the field
    // survives a page refresh).
    enhanced.nationalStandard = nationalStandard;
    setForceBuilderResult(enhanced);
    const allocation = EVAC_AUTO_ALLOCATION[echelon] || EVAC_AUTO_ALLOCATION.BRIGADE;
    setMedicalAssets({ ground: allocation.ground, air: allocation.air });
    setErrors([]);
  };

  const handleUnitUpdate = (updatedResult) => {
    // Ensure tactical_in_parent exists for all units
    const ensureTacticalInParent = (unit, parentUnit) => {
      if (!unit.tactical_in_parent) {
        unit.tactical_in_parent = {
          tactical_role: parentUnit?.tactical_in_parent?.tactical_role || 'SUPPORTING',
          posture: parentUnit?.tactical_in_parent?.posture || 'HASTY_DEFENSE',
          // v2.4 Phase 2.1: Removed hq_position_feba_km - ForceService calculates this
          medical_operational: true
        };
      }
    };
    
    if (updatedResult.corps && !updatedResult.corps.tactical_in_parent) {
      updatedResult.corps.tactical_in_parent = { 
        tactical_role: 'SUPPORTING', 
        posture: 'HASTY_DEFENSE', 
        // v2.4 Phase 2.1: Removed hq_position_feba_km - ForceService calculates this
        medical_operational: true 
      };
    }
    updatedResult.divisions?.forEach(div => ensureTacticalInParent(div, updatedResult.corps));
    updatedResult.brigades?.forEach(bde => {
      const parentDiv = updatedResult.divisions?.find(d => d.id === bde.parent_division_id);
      ensureTacticalInParent(bde, parentDiv);
    });
    updatedResult.battalions?.forEach(bn => {
      const parentBde = updatedResult.brigades?.find(b => b.id === bn.parent_brigade_id);
      ensureTacticalInParent(bn, parentBde);
    });
    
    // Rebuild hierarchies (CLEANUP-002: was local rebuildAllHierarchies — same body as module-level addHierarchyToAllUnits)
    addHierarchyToAllUnits(updatedResult);

    // 2026-10-25: after every edit the sub_units of a brigade with cards mirror its cards (added, deleted, retyped or resized units), so the second
    // list of the brigade never keeps old names or strengths. Brigade types without a card list keep their doctrinal sub_units.
    if (BRIGADE_CARDS_ON) {
      const _cardBrigades = [...(updatedResult.brigades || []), ...(updatedResult.brigade ? [updatedResult.brigade] : [])];
      _cardBrigades.forEach((bde) => {
        if (!BRIGADE_CARD_TEMPLATES[bde.unit_type || bde.brigade_type]) return;
        const _cards = (updatedResult.battalions || []).filter(bn => bn.parent_brigade_id === bde.id);
        if (_cards.length > 0) bde.sub_units = ForceBuilder.subUnitsFromBattalions(bde.id, _cards);
      });
    }

    // ARCH-012 / BUG-187 FIX: Recalculate total_strength after any unit edit.
    // ForceService sets total_strength once at build time — never updated when
    // the user edits a unit type/strength in TacticalEditor.
    //
    // BUG-191 FIX (March 24, 2026): Removed the battalion-sum -> brigade.strength
    // overwrite that was introduced in BUG-187. That block wrote:
    //   updatedResult.brigade.strength = sum(battalions) [e.g. 1900]
    // discarding the user's national-standard brigade strength (e.g. 4800 for
    // German ARMOR) every time SAVE CHANGES was pressed on ANY unit. Header then
    // showed 1,900 Personnel regardless of brigade type.
    //
    // At BRIGADE echelon result.brigade (singular) is the top-level unit — its
    // strength is authoritative (set by national standard on type change / on build).
    // Battalions are sub-components already represented within that total; they are
    // NOT additive. Only at DIVISION+ echelon are brigades sub-units whose individual
    // strengths need summing — those live in result.brigades[] (plural) and are
    // already handled by the brigades.reduce() term below.
    //
    // total_strength formula is echelon-agnostic: sums every strength field present.

    // BUG-S109-001 FIX: Mirror ForceService.buildCorps totalStrength formula exactly.
    // ForceService adds division_support_strength + support_troops.total_organic_strength
    // to the base unit sum. Omitting these caused a ~26,000-person drop after the first
    // unit edit in CORPS echelon (support formations silently zeroed on every save).
    // 2026-10-25 (Dr. Tatar: the total must be calculated from the personnel the force holds): a unit that lives both in its singular field
    // (result.division) and in its list (result.divisions) was counted twice, so the first edit of a division force added the division
    // headquarters a second time (+1,000). Every unit is counted once, by id.
    const _countedIds = new Set();
    const _once = (unit) => {
      if (!unit) return 0;
      if (unit.id !== undefined && unit.id !== null) {
        if (_countedIds.has(unit.id)) return 0;
        _countedIds.add(unit.id);
      }
      return unit.strength || 0;
    };
    updatedResult.total_strength =
      _once(updatedResult.corps) +
      _once(updatedResult.division) +
      _once(updatedResult.brigade) +
      _once(updatedResult.battalion) +  // BUG-189 FIX: BATTALION echelon unit lives here
      (updatedResult.divisions?.reduce((sum, d)  => sum + _once(d), 0) || 0) +
      (updatedResult.brigades?.reduce((sum, b)   => sum + _once(b), 0) || 0) +
      (updatedResult.medical_brigades?.reduce((sum, mb) => sum + (updatedResult.brigades?.some(b => b.id === mb.brigade?.id) ? 0 : (mb.brigade?.strength || 0)), 0) || 0) +   // 2026-10-25: counted once
      (updatedResult.division_support_strength || 0) +
      (updatedResult.support_troops?.total_organic_strength || 0);

    setForceBuilderResult(updatedResult);
  };

  const handleForceQualityChange = (newQuality) => {
    setForceQuality(newQuality);
    
    // Update force quality on top-level unit if result exists
    if (forceBuilderResult) {
      const updated = { ...forceBuilderResult };
      if (updated.corps) updated.corps.force_quality = { ...newQuality };
      if (updated.division) updated.division.force_quality = { ...newQuality };
      if (updated.brigade) updated.brigade.force_quality = { ...newQuality };
      if (updated.battalion) updated.battalion.force_quality = { ...newQuality };
      setForceBuilderResult(updated);
    }
  };

  const handleComplete = () => {
    const newErrors = [];
    if (!echelon) newErrors.push('Force echelon not selected');
    if (!nationalStandard) newErrors.push('National standard not selected');
    if (buildMode === BUILD_MODE.QUICK && !forceBuilderResult) {
      newErrors.push('Force structure not built');
    }
    
    // v3.0.1 VALIDATION: Check all brigades/battalions have unit_type
    if (forceBuilderResult) {
      // Check brigades (for BRIGADE, DIVISION, CORPS echelons)
      const brigades = forceBuilderResult.brigades || [];
      brigades.forEach((brigade, index) => {
        if (!brigade.unit_type) {
          newErrors.push(`Brigade "${brigade.name || `#${index + 1}`}" is missing Unit Type (required for v3.0)`);
        }
      });
      
      // Check divisions' brigades (for DIVISION, CORPS echelons)
      if (forceBuilderResult.divisions) {
        forceBuilderResult.divisions.forEach((division) => {
          const divBrigades = division.brigades || [];
          divBrigades.forEach((brigade, index) => {
            if (!brigade.unit_type) {
              newErrors.push(`Brigade "${brigade.name || `#${index + 1}`}" in ${division.name} is missing Unit Type (required for v3.0)`);
            }
          });
        });
      }
      
      // Check battalion (for BATTALION echelon)
      if (echelon === FORCE_ECHELONS.BATTALION && forceBuilderResult.battalion) {
        if (!forceBuilderResult.battalion.unit_type) {
          newErrors.push(`Battalion "${forceBuilderResult.battalion.name}" is missing Unit Type (required for v3.0)`);
        }
      }
    }
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // EXPLICITLY SAVE before continuing
    const forceData = {
      echelon,
      nationalStandard,
      corpsLeadNation,                // ENH-018 Phase F
      divisionStandards,
      brigadeStandards,
      numDivisions,
      numBrigades,
      buildMode,
      battalionWarningAccepted,
      configured: true,
      
      // FIX: Flatten force structure to top level for calculator compatibility
      // These are REFERENCES (not copies) - zero performance impact
      // Calculator can now read: force.brigades (instead of force.forceBuilderResult.brigades)
      brigades: forceBuilderResult?.brigades || [],
      battalions: forceBuilderResult?.battalions || [],
      divisions: forceBuilderResult?.divisions || [],
      corps: forceBuilderResult?.corps || null,
      // DIVISION echelon: formatBuildResult returns .division (singular), not .divisions.
      // Tab2 needs this to initialise divisionConfigs. Without it divisionConfigs = [] always.
      division: forceBuilderResult?.division || null,
      
      // IMP-011: use patched result so calculator sees user-edited support troops
      forceBuilderResult: patchedForceBuilderResult,
      support_overrides: supportOverrides,

      force_quality: forceQuality,  // BUG-A FIX: Calculator expects snake_case
      medicalAssets,
      totalStrength: calculateTotalStrength()
    };
    
    if (onUpdateRef.current) {
      onUpdateRef.current({ force: forceData });
    }
    
    setIsConfigured(true);
    // calculationService: force composition changed — Tab5 result is now stale
    markDirty();
    if (onComplete) onComplete();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#2196f3', mb: 3 }}>
        {t('tab1.title')}
      </Typography>

      <Typography variant="body1" color='#2196f3' paragraph>
        {t('tab1.subtitle')}
      </Typography>

      {/* IMP-001: ORBAT lock banner — shown after CoA A is saved */}
      {orbatLocked && (
        <Alert
          severity="warning"
          action={
            <Button size="small" color="inherit" variant="outlined" onClick={handleUnlockOrbat}>
              {t('tab1.unlock_btn')}
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {t('tab1.orbat_locked_msg')}
        </Alert>
      )}

      {/* Echelon Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl component="fieldset" disabled={orbatLocked}>
            <FormLabel component="legend">{t('tab1.step1_echelon')}</FormLabel>
            <RadioGroup row value={echelon || ''} onChange={handleEchelonChange}>
              <FormControlLabel value={FORCE_ECHELONS.BATTALION} control={<Radio />}
                label={t('tab1.echelon_battalion')} />
              <FormControlLabel value={FORCE_ECHELONS.BRIGADE} control={<Radio />}
                label={t('tab1.echelon_brigade')} />
              <FormControlLabel value={FORCE_ECHELONS.DIVISION} control={<Radio />}
                label={t('tab1.echelon_division')} />
              <FormControlLabel value={FORCE_ECHELONS.CORPS} control={<Radio />}
                label={t('tab1.echelon_corps')} />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* National Standard Selection */}
      {echelon && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth disabled={orbatLocked}>
              <InputLabel>{t('tab1.step2_national_standard')}</InputLabel>
              <Select
                value={nationalStandard || ''}
                label={t('tab1.step2_national_standard')}
                onChange={handleNationalStandardChange}
              >
                {sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
                  .filter(([key]) => {
                    if ((echelon === 'BATTALION' || echelon === 'BRIGADE') && key === 'MULTINATIONAL') return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value.flag} {value.label}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Multinational Configuration for CORPS */}
      {echelon === FORCE_ECHELONS.CORPS && nationalStandard === 'MULTINATIONAL' && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'info.main' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <GlobalIcon sx={{ mr: 1 }} /> {t('tab1.configure_div_standards')}
            </Typography>
            <TextField
              type="number"
              label={t('tab1.num_divisions')}
              value={numDivisions}
              onChange={(e) => setNumDivisions(parseInt(e.target.value))}
              disabled={orbatLocked}
              inputProps={{ min: 2, max: 4 }}
              sx={{ mb: 2, width: 200 }}
            />

            {/* ENH-018 Phase F: Lead/Framework Nation selector.
                When set, corps-level support troops use that nation's SUPPORT_UNITS_BY_ECHELON.corps
                instead of the averaged MULTINATIONAL entry — per NATO Framework Nation Concept.
                Optional: leave blank to use averaged MULTINATIONAL planning data (correct default). */}
            <Box sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 280 }}>
                <InputLabel>{t('tab1.lead_framework_nation')}</InputLabel>
                <Select
                  value={corpsLeadNation || ''}
                  label={t('tab1.lead_framework_nation')}
                  onChange={(e) => {
                    setCorpsLeadNation(e.target.value || null);
                    setForceBuilderResult(null);  // force rebuild when lead nation changes
                  }}
                >
                  <MenuItem value="">
                    <em>{t('tab1.qb_lead_nation_none')}</em>
                  </MenuItem>
                  {sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
                    .filter(([key]) => key !== 'MULTINATIONAL')
                    .map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value.flag} {value.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {corpsLeadNation && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                  {t('tab1.fnc_doctrine_note', { nation: NATIONAL_STANDARDS[corpsLeadNation]?.label })}
                </Typography>
              )}
            </Box>

            <Grid container spacing={2}>
              {Array.from({ length: numDivisions }, (_, i) => i + 1).map(divNum => (
                <Grid size={{ xs: 12, md: 4 }} key={divNum}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tab1.division_n', { n: divNum })}</InputLabel>
                    <Select
                      value={divisionStandards[divNum] || ''}
                      label={t('tab1.division_n', { n: divNum })}
                      onChange={(e) => setDivisionStandards({ ...divisionStandards, [divNum]: e.target.value })}
                    >
                      {/* v3.2: Dynamic nation list from NATIONAL_STANDARDS (modular architecture) */}
                      {/* ENH-014: MULTINATIONAL allowed — enables MND-SE/MND-N/MND-C constructs (MC 0551) */}
                      {sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
                        .map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value.flag} {value.label}{key === 'MULTINATIONAL' ? ' (MND)' : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* NEW: Multinational Configuration for DIVISION */}
      {echelon === FORCE_ECHELONS.DIVISION && nationalStandard === 'MULTINATIONAL' && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'info.main' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <GlobalIcon sx={{ mr: 1 }} /> {t('tab1.configure_bde_standards')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t('tab1.multinational_division')}</AlertTitle>
              {t('tab1.multinational_div_desc')}
            </Alert>
            <TextField
              type="number"
              label={t('tab1.num_brigades')}
              value={numBrigades}
              onChange={(e) => setNumBrigades(parseInt(e.target.value))}
              inputProps={{ min: 2, max: 5 }}
              sx={{ mb: 2, width: 200 }}
            />
            <Grid container spacing={2}>
              {Array.from({ length: numBrigades }, (_, i) => i + 1).map(bdeNum => (
                <Grid size={{ xs: 12, md: 4 }} key={bdeNum}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tab1.brigade_n', { n: bdeNum })}</InputLabel>
                    <Select
                      value={brigadeStandards[bdeNum] || ''}
                      label={t('tab1.brigade_n', { n: bdeNum })}
                      onChange={(e) => setBrigadeStandards({ ...brigadeStandards, [bdeNum]: e.target.value })}
                    >
                      {/* v3.2: Dynamic nation list from NATIONAL_STANDARDS (modular architecture) */}
                      {sortNationalStandards(Object.entries(NATIONAL_STANDARDS))
                        .filter(([key]) => key !== 'MULTINATIONAL') // Exclude MULTINATIONAL from nested selection
                        .map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value.flag} {value.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Build Mode Selection */}
      {echelon && nationalStandard && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('tab1.step3_build_method')}</FormLabel>
              <RadioGroup row value={buildMode} onChange={(e) => { setBuildMode(e.target.value); setForceBuilderResult(null); }}>
                <FormControlLabel value={BUILD_MODE.QUICK} control={<Radio />}
                  label={<Stack direction="row" spacing={1} alignItems="center">
                    <QuickBuildIcon /><span>{t('tab1.quick_build')}</span><Chip label={t('tab1.recommended_chip')} color="success" size="small" />
                  </Stack>} />
                <FormControlLabel value={BUILD_MODE.MANUAL} control={<Radio />}
                  label={<Stack direction="row" spacing={1} alignItems="center">
                    <ManualBuildIcon /><span>{t('tab1.manual_build')}</span>
                  </Stack>} />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Quick Build Panel */}
      {echelon && nationalStandard && buildMode === BUILD_MODE.QUICK && (
        <>
          <QuickBuildPanel
            echelon={echelon}
            nationalStandard={nationalStandard}
            corpsLeadNation={corpsLeadNation}
            divisionStandards={divisionStandards}
            brigadeStandards={brigadeStandards}
            numDivisions={numDivisions}
            numBrigades={numBrigades}
            onBuild={handleQuickBuild}
            onForceQualityChange={handleForceQualityChange}
            forceQuality={forceQuality}
            scenario={scenario}
            orbatLocked={orbatLocked}
          />

          {forceBuilderResult && (
            <>
              <ForceStructurePreview
                forceBuilderResult={forceBuilderResult}
                echelon={echelon}
                echelonSupportTroops={effectiveSupportTroops}
                divisionSupportGroups={effectiveDivisionSupportGroups}
                onUnitUpdate={handleSupportUnitUpdate}
                onUnitDelete={handleSupportUnitDelete}
                onUnitAdd={handleSupportUnitAdd}
                onGroupReset={handleSupportGroupReset}
                hasOverrides={!!supportOverrides}
              />
              <TacticalEditor
                forceBuilderResult={forceBuilderResult}
                echelon={echelon}
                mode={mode}
                onUpdate={handleUnitUpdate}
                orbatLocked={orbatLocked}
              />
            </>
          )}
        </>
      )}

      {/* Manual Build Placeholder */}
      {echelon && nationalStandard && buildMode === BUILD_MODE.MANUAL && (
        <Alert severity="warning">
          <AlertTitle>{t('tab1.manual_build_wip_title')}</AlertTitle>
          {t('tab1.manual_build_wip_body')}
        </Alert>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>{t('tab1.complete_required_title')}</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, idx) => <li key={idx}>{error}</li>)}
          </ul>
        </Alert>
      )}

      {/* Navigation */}
      {echelon && nationalStandard && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" disabled>{t('tab1.back_btn')}</Button>
          <Button
            variant="contained"
            endIcon={<CheckCircleIcon />}
            onClick={handleComplete}
            disabled={errors.length > 0 || (buildMode === BUILD_MODE.QUICK && !forceBuilderResult)}
          >
            {t('tab1.save_continue_ops')}
          </Button>
        </Box>
      )}

      {/* Battalion Warning Dialog */}
      <Dialog open={showBattalionWarning} onClose={() => setShowBattalionWarning(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningIcon color="warning" /><span>{t('tab1.battalion_warn_title')}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab1.battalion_warn_limit_title')}</AlertTitle>
            {t('tab1.battalion_warn_limit_body')}
          </Alert>
          <FormControlLabel
            control={<Checkbox checked={warningCheckbox} onChange={(e) => setWarningCheckbox(e.target.checked)} />}
            label={t('tab1.battalion_warn_checkbox')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowBattalionWarning(false); setWarningCheckbox(false); }}>{t('tab1.cancel')}</Button>
          <Button onClick={handleBattalionWarningAccept} disabled={!warningCheckbox} variant="contained">
            {t('tab1.accept_continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Initial Instructions */}
      {(!echelon || !nationalStandard) && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <AlertTitle>{t('tab1.getting_started')}</AlertTitle>
          <ol style={{ marginBottom: 0 }}>
            <li>{t('tab1.instr_step1')}</li>
            <li>{t('tab1.instr_step2')}</li>
            <li>{t('tab1.instr_step3')}</li>
            <li>{t('tab1.instr_step4')}</li>
            <li>{t('tab1.instr_step5')}</li>
          </ol>
        </Alert>
      )}
    </Box>
  );
});

export default Tab1_ForceComposition;
